// This file is auto-generated by @hey-api/openapi-ts

export const ProductionStudioRecipeSchema = {
  type: 'string',
  enum: ['highlight-montage', 'story-narration', 'series-recommendation', 'overlay-only'],
} as const;

export const ProductionStudioMarketSchema = {
  type: 'string',
  enum: [
    'zh-CN',
    'zh-TW',
    'en-US',
    'en-GB',
    'fil-PH',
    'id-ID',
    'th-TH',
    'vi-VN',
    'ms-MY',
    'ar-AE',
    'pt-BR',
    'es-MX',
  ],
} as const;

export const ProductionStudioSourceAssetRequestSchema = {
  type: 'object',
  additionalProperties: false,
  properties: {
    entryId: {
      type: 'string',
      nullable: true,
    },
    fileId: {
      type: 'string',
      nullable: true,
    },
  },
  description: '必须且只能提供 entryId 或 fileId。',
} as const;

export const ProductionStudioSubtitleStyleSchema = {
  type: 'object',
  additionalProperties: false,
  properties: {
    fontFamily: {
      type: 'string',
      default: 'Noto Sans',
    },
    fontSize: {
      type: 'integer',
      minimum: 12,
      maximum: 160,
      default: 48,
    },
    color: {
      type: 'string',
      pattern: '^#[0-9a-fA-F]{6}$',
      default: '#FFFFFF',
    },
    outlineColor: {
      type: 'string',
      pattern: '^#[0-9a-fA-F]{6}$',
      default: '#000000',
    },
    outlineWidth: {
      type: 'integer',
      minimum: 0,
      maximum: 12,
      default: 3,
    },
    position: {
      type: 'string',
      enum: ['top', 'center', 'bottom'],
      default: 'bottom',
    },
    fontStyle: {
      type: 'string',
      enum: ['modern', 'rounded', 'serif', 'light'],
      default: 'modern',
    },
    sizePreset: {
      type: 'string',
      enum: ['small', 'medium', 'large', 'xlarge'],
      default: 'medium',
    },
    effect: {
      type: 'string',
      enum: [
        'outline-shadow',
        'soft-shadow',
        'heavy-outline',
        'gradient-shadow',
        'three-dimensional',
        'soft-outline',
        'high-contrast-outline',
        'subtle-glow',
        'none',
      ],
      default: 'soft-shadow',
    },
  },
} as const;

export const ProductionStudioBatchConfigSchema = {
  type: 'object',
  additionalProperties: false,
  required: [
    'recipe',
    'market',
    'language',
    'outputLayout',
    'width',
    'height',
    'fps',
    'targetDurationSeconds',
    'quantity',
  ],
  properties: {
    recipe: {
      $ref: '#/components/schemas/ProductionStudioRecipe',
    },
    market: {
      $ref: '#/components/schemas/ProductionStudioMarket',
    },
    targetRegion: {
      type: 'string',
      minLength: 1,
      maxLength: 100,
      default: '中国大陆',
      description:
        'Production Studio 表单中的原始目标地区；新加坡或其它自定义地区通过兼容市场包编译时仍保留此值用于追溯。',
    },
    language: {
      type: 'string',
    },
    projectTitleChinese: {
      type: 'string',
      maxLength: 500,
      default: '',
    },
    projectTitleEnglish: {
      type: 'string',
      maxLength: 500,
      default: '',
    },
    publishingPlatform: {
      type: 'string',
      enum: ['none', 'tiktok', 'youtube-shorts', 'instagram-reels', 'facebook', 'x', 'other'],
      default: 'none',
    },
    outputLayout: {
      type: 'string',
      enum: ['vertical', 'horizontal', 'vertical-letterbox'],
    },
    width: {
      type: 'integer',
      minimum: 320,
      maximum: 7680,
    },
    height: {
      type: 'integer',
      minimum: 320,
      maximum: 7680,
    },
    fps: {
      type: 'integer',
      minimum: 1,
      maximum: 120,
    },
    targetDurationSeconds: {
      type: 'integer',
      minimum: 5,
      maximum: 600,
    },
    ctaDurationSeconds: {
      type: 'number',
      minimum: 0,
      maximum: 30,
      default: 0,
    },
    quantity: {
      type: 'integer',
      minimum: 1,
      maximum: 100,
    },
    narrationRequired: {
      type: 'boolean',
      default: false,
    },
    narrationLanguage: {
      type: 'string',
      default: '',
    },
    narrationVoiceStyle: {
      type: 'string',
      enum: [
        'auto',
        'young-female-gentle',
        'mature-female-healing',
        'young-male-friendly',
        'mature-male-deep',
        'lively-cute',
        'restrained-suspense',
        'energetic-powerful',
      ],
      default: 'auto',
    },
    voice: {
      type: 'string',
      default: '',
    },
    voiceRate: {
      type: 'number',
      minimum: 0.5,
      maximum: 2,
      default: 1,
    },
    subtitleMode: {
      type: 'string',
      enum: ['none', 'preserve', 'localized', 'bilingual'],
      default: 'preserve',
    },
    subtitleSecondaryLanguage: {
      type: 'string',
      enum: ['chinese', 'english'],
      default: 'chinese',
      description: '双语字幕的第二语言；仅 subtitleMode 为 bilingual 时生效。',
    },
    subtitleStyle: {
      $ref: '#/components/schemas/ProductionStudioSubtitleStyle',
    },
    coverOriginalSubtitles: {
      type: 'boolean',
      default: false,
    },
    originalAudioMode: {
      type: 'string',
      enum: ['muted', 'preserve', 'reduced'],
      default: 'muted',
    },
    originalAudioVolume: {
      type: 'number',
      minimum: 0,
      maximum: 2,
      default: 1,
    },
    bgmMode: {
      type: 'string',
      enum: ['none', 'library', 'specified', 'online'],
      default: 'none',
    },
    bgmEntryId: {
      type: 'string',
      nullable: true,
    },
    bgmStyle: {
      type: 'string',
      default: '',
    },
    bgmVolume: {
      type: 'number',
      minimum: 0,
      maximum: 2,
      default: 0.2,
    },
    bgmSource: {
      type: 'string',
      default: '',
    },
    bgmLicense: {
      type: 'string',
      default: '',
    },
    bgmAttribution: {
      type: 'string',
      default: '',
    },
    bgmAuthorizationConfirmed: {
      type: 'boolean',
      default: false,
    },
    beatSync: {
      type: 'boolean',
      default: false,
    },
    strongHook: {
      type: 'boolean',
      default: true,
    },
    coverMode: {
      type: 'string',
      enum: ['auto', 'upload', 'none'],
      default: 'auto',
    },
    coverEntryId: {
      type: 'string',
      nullable: true,
    },
    autoCover: {
      type: 'boolean',
      default: true,
    },
    layoutMode: {
      type: 'string',
      enum: ['single', 'pip', 'split', 'grid', 'auto'],
      default: 'auto',
    },
    montageTemplates: {
      type: 'array',
      maxItems: 5,
      items: {
        type: 'string',
        enum: [
          'vertical-split',
          'horizontal-split',
          'quad',
          'picture-in-picture',
          'diagonal-split',
        ],
      },
    },
    logoEnabled: {
      type: 'boolean',
      default: true,
    },
    customLogoPosition: {
      type: 'string',
      enum: ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'center'],
      default: 'top-right',
    },
    ctaEnabled: {
      type: 'boolean',
      default: true,
    },
    ctaText: {
      type: 'string',
      maxLength: 500,
      default: '',
    },
    ctaLogoPosition: {
      type: 'string',
      enum: ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'center'],
      default: 'top-right',
    },
    customLogoEntryId: {
      type: 'string',
      nullable: true,
    },
    customCtaEntryId: {
      type: 'string',
      nullable: true,
    },
    customCtaLogoEntryId: {
      type: 'string',
      nullable: true,
    },
    customEndCardEntryId: {
      type: 'string',
      nullable: true,
    },
    request: {
      type: 'string',
      maxLength: 20000,
      default: '',
    },
    outputNameTemplate: {
      type: 'string',
      maxLength: 500,
      default: '{batch}-{index}',
    },
    versionNote: {
      type: 'string',
      maxLength: 2000,
      default: '',
    },
  },
} as const;

export const EnsureProductionStudioWorkspaceRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['canvasId', 'nodeId'],
  properties: {
    canvasId: {
      type: 'string',
    },
    nodeId: {
      type: 'string',
    },
  },
} as const;

export const CreateProductionStudioBatchRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['name', 'clientPackId', 'clientPackVersion', 'assets', 'config'],
  properties: {
    name: {
      type: 'string',
      minLength: 1,
      maxLength: 200,
    },
    clientPackId: {
      type: 'string',
    },
    clientPackVersion: {
      type: 'string',
    },
    textProviderItemId: {
      type: 'string',
      nullable: true,
    },
    visionProviderItemId: {
      type: 'string',
      nullable: true,
    },
    assets: {
      type: 'array',
      minItems: 1,
      maxItems: 50,
      items: {
        $ref: '#/components/schemas/ProductionStudioSourceAssetRequest',
      },
    },
    config: {
      $ref: '#/components/schemas/ProductionStudioBatchConfig',
    },
  },
} as const;

export const UpdateProductionStudioBatchRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['expectedRevision'],
  properties: {
    expectedRevision: {
      type: 'integer',
      minimum: 1,
    },
    name: {
      type: 'string',
      minLength: 1,
      maxLength: 200,
    },
    clientPackId: {
      type: 'string',
    },
    clientPackVersion: {
      type: 'string',
    },
    textProviderItemId: {
      type: 'string',
      nullable: true,
    },
    visionProviderItemId: {
      type: 'string',
      nullable: true,
    },
    assets: {
      type: 'array',
      minItems: 1,
      maxItems: 50,
      items: {
        $ref: '#/components/schemas/ProductionStudioSourceAssetRequest',
      },
    },
    config: {
      $ref: '#/components/schemas/ProductionStudioBatchConfig',
    },
  },
} as const;

export const CopyProductionStudioBatchRequestSchema = {
  type: 'object',
  additionalProperties: false,
  properties: {
    name: {
      type: 'string',
      minLength: 1,
      maxLength: 200,
    },
  },
} as const;

export const SaveProductionStudioScriptRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['variantIndex'],
  properties: {
    variantIndex: {
      type: 'integer',
      minimum: 0,
      maximum: 99,
    },
    localText: {
      type: 'string',
      maxLength: 20000,
      default: '',
    },
    chineseText: {
      type: 'string',
      maxLength: 20000,
      default: '',
    },
    englishText: {
      type: 'string',
      maxLength: 20000,
      default: '',
    },
    visualPlan: {
      type: 'string',
      maxLength: 20000,
      default: '',
    },
    source: {
      type: 'string',
      enum: ['model', 'manual', 'offline'],
      default: 'manual',
    },
    evidence: {
      type: 'array',
      maxItems: 100,
      items: {
        type: 'string',
      },
      default: [],
    },
  },
} as const;

export const RegenerateProductionStudioScriptsRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['request'],
  properties: {
    request: {
      type: 'string',
      minLength: 1,
      maxLength: 5000,
    },
    variantIndex: {
      type: 'integer',
      minimum: 0,
      maximum: 99,
    },
  },
} as const;

export const ImportProductionStudioClientPackRequestSchema = {
  type: 'object',
  required: ['file', 'name', 'version', 'authorizationConfirmed'],
  properties: {
    file: {
      type: 'string',
      format: 'binary',
    },
    name: {
      type: 'string',
      minLength: 1,
      maxLength: 48,
    },
    version: {
      type: 'string',
      default: '1.0.0',
    },
    contentType: {
      type: 'string',
      default: '其他',
    },
    market: {
      type: 'string',
      default: '',
    },
    language: {
      type: 'string',
      default: '',
    },
    defaultCta: {
      type: 'string',
      default: '按批次决定',
    },
    outputSpec: {
      type: 'string',
      default: 'MP4 / H.264 / 30fps',
    },
    authorizationConfirmed: {
      type: 'boolean',
    },
  },
} as const;

export const UpdateProductionStudioClientPackStatusRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['status'],
  properties: {
    status: {
      type: 'string',
      enum: ['enabled', 'disabled'],
    },
  },
} as const;

export const UpdateProductionStudioClientPackAssetRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['purpose'],
  properties: {
    purpose: {
      type: 'string',
      enum: [
        'brand-logo',
        'end-card',
        'cta',
        'cta-logo',
        'sticker',
        'ending-sound',
        'font',
        'brand-asset',
      ],
    },
    required: {
      type: 'boolean',
      default: false,
    },
  },
} as const;

export const ProductionStudioClientPackSchema = {
  type: 'object',
  required: ['packVersionId', 'id', 'name', 'version', 'status', 'builtin', 'manifestHash'],
  properties: {
    packVersionId: {
      type: 'string',
    },
    id: {
      type: 'string',
    },
    name: {
      type: 'string',
    },
    description: {
      type: 'string',
    },
    version: {
      type: 'string',
    },
    status: {
      type: 'string',
      enum: ['validated', 'enabled', 'disabled'],
    },
    builtin: {
      type: 'boolean',
    },
    manifestHash: {
      type: 'string',
    },
    client: {
      type: 'object',
      additionalProperties: true,
    },
    assets: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: true,
      },
    },
  },
} as const;

export const ProductionStudioBatchSchema = {
  type: 'object',
  required: [
    'batchId',
    'workspaceId',
    'name',
    'status',
    'stage',
    'revision',
    'config',
    'clientPackId',
    'clientPackVersion',
    'assets',
    'stageRuns',
    'scripts',
    'outputs',
  ],
  properties: {
    batchId: {
      type: 'string',
    },
    workspaceId: {
      type: 'string',
    },
    name: {
      type: 'string',
    },
    status: {
      type: 'string',
    },
    stage: {
      type: 'string',
    },
    revision: {
      type: 'integer',
    },
    config: {
      $ref: '#/components/schemas/ProductionStudioBatchConfig',
    },
    clientPackId: {
      type: 'string',
    },
    clientPackVersion: {
      type: 'string',
    },
    textProviderItemId: {
      type: 'string',
      nullable: true,
    },
    visionProviderItemId: {
      type: 'string',
      nullable: true,
    },
    errorCode: {
      type: 'string',
      nullable: true,
    },
    errorMessage: {
      type: 'string',
      nullable: true,
    },
    assets: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: true,
      },
    },
    stageRuns: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: true,
      },
    },
    scripts: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: true,
      },
    },
    outputs: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: true,
      },
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
    },
  },
} as const;

export const ProductionStudioWorkspaceSchema = {
  type: 'object',
  required: ['workspaceId', 'canvasId', 'nodeId', 'batches'],
  properties: {
    workspaceId: {
      type: 'string',
    },
    canvasId: {
      type: 'string',
    },
    nodeId: {
      type: 'string',
    },
    batches: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/ProductionStudioBatch',
      },
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
    },
  },
} as const;

export const ProductionStudioWorkspaceResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/ProductionStudioWorkspace',
        },
      },
    },
  ],
} as const;

export const ProductionStudioBatchResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/ProductionStudioBatch',
        },
      },
    },
  ],
} as const;

export const ProductionStudioClientPackResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/ProductionStudioClientPack',
        },
      },
    },
  ],
} as const;

export const ProductionStudioClientPackListResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/ProductionStudioClientPack',
          },
        },
      },
    },
  ],
} as const;

export const ProductionStudioStrategyArtifactResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          required: ['outputId', 'artifact'],
          properties: {
            outputId: {
              type: 'string',
            },
            artifact: {
              type: 'object',
              additionalProperties: true,
            },
          },
        },
      },
    },
  ],
} as const;

export const SocialPlatformKeySchema = {
  type: 'string',
  description: '社交平台稳定标识',
  enum: [
    'xiaohongshu',
    'wechat_channels',
    'douyin',
    'kuaishou',
    'tiktok',
    'instagram',
    'facebook',
    'bilibili',
    'baijiahao',
    'youtube',
    'twitter',
  ],
} as const;

export const SocialPlatformSchema = {
  type: 'object',
  description: '社交发布系统支持的平台信息',
  required: ['key', 'type', 'name'],
  properties: {
    key: {
      $ref: '#/components/schemas/SocialPlatformKey',
    },
    type: {
      type: 'integer',
      minimum: 1,
      maximum: 11,
      description: '平台类型编号',
    },
    name: {
      type: 'string',
      description: '平台中文名称',
    },
    logoUrl: {
      type: 'string',
      nullable: true,
      description: '平台 Logo',
    },
    supportsAutoBinding: {
      type: 'boolean',
      description: '是否支持自动绑定',
    },
    supportsTotp: {
      type: 'boolean',
      description: '是否支持自动填写 TOTP',
    },
  },
} as const;

export const SocialAccountOverviewSchema = {
  type: 'object',
  description: '社交账号数据概览；正式实现后返回当前用户的账号总数及国内外平台统计。',
  required: ['totalCount', 'domesticCount', 'overseasCount', 'platformStats'],
  properties: {
    totalCount: {
      type: 'integer',
      minimum: 0,
      description: '账号总数',
    },
    domesticCount: {
      type: 'integer',
      minimum: 0,
      description: '国内平台账号数',
    },
    overseasCount: {
      type: 'integer',
      minimum: 0,
      description: '国外平台账号数',
    },
    platformStats: {
      type: 'array',
      description: '按平台统计的账号数量',
      items: {
        type: 'object',
        required: ['platform', 'platformName', 'platformType', 'count'],
        properties: {
          platform: {
            $ref: '#/components/schemas/SocialPlatformKey',
          },
          platformName: {
            type: 'string',
            description: '平台名称',
          },
          platformType: {
            type: 'integer',
            minimum: 1,
            description: '平台类型编号',
          },
          count: {
            type: 'integer',
            minimum: 0,
            description: '账号数量',
          },
        },
      },
    },
  },
} as const;

export const SocialBrowserIdRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['browser_id'],
  properties: {
    browser_id: {
      type: 'string',
      minLength: 1,
      description: '浏览器会话唯一标识',
    },
  },
} as const;

export const SocialLoginStartRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['id', 'type'],
  properties: {
    id: {
      type: 'string',
      description: '账号唯一标识或账号名称',
    },
    type: {
      type: 'integer',
      description: '平台类型编号',
    },
    run_id: {
      type: 'string',
      description: '登录任务幂等标识',
    },
  },
} as const;

export const SocialLoginCompleteRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['browser_id', 'id', 'type'],
  properties: {
    browser_id: {
      type: 'string',
      description: '浏览器会话唯一标识',
    },
    id: {
      type: 'string',
      description: '账号唯一标识或账号名称',
    },
    type: {
      type: 'integer',
      description: '平台类型编号',
    },
  },
} as const;

export const SocialLoginBatchRequestSchema = {
  type: 'object',
  description: '批量登录账号参数，具体账号字段由登录服务处理。',
  additionalProperties: true,
  properties: {
    accounts: {
      type: 'array',
      description: '待登录账号数组',
      items: {
        type: 'object',
        additionalProperties: true,
      },
    },
    session_id: {
      type: 'string',
      description: '批量会话唯一标识',
    },
  },
} as const;

export const SocialLoginBatchActionRequestSchema = {
  type: 'object',
  additionalProperties: true,
  properties: {
    session_id: {
      type: 'string',
      description: '批量会话唯一标识',
    },
    browser_id: {
      type: 'string',
      description: '浏览器会话唯一标识',
    },
    account_id: {
      type: 'string',
      description: '账号唯一标识',
    },
  },
} as const;

export const SocialLoginSessionSchema = {
  type: 'object',
  additionalProperties: true,
  properties: {
    browser_id: {
      type: 'string',
      description: '浏览器会话唯一标识',
    },
    vnc_url: {
      type: 'string',
      format: 'uri',
      nullable: true,
      description: '登录浏览器可视化地址',
    },
    url: {
      type: 'string',
      format: 'uri',
      nullable: true,
      description: '当前页面地址',
    },
    status: {
      type: 'string',
      description: '登录会话状态',
    },
    cookie_file: {
      type: 'string',
      nullable: true,
      description: '保存后的 Cookie 文件路径',
    },
  },
} as const;

export const SocialLoginBatchSchema = {
  type: 'object',
  additionalProperties: true,
  properties: {
    session_id: {
      type: 'string',
      description: '批量会话唯一标识',
    },
    status: {
      type: 'string',
      description: '批量登录状态',
    },
    accounts: {
      type: 'array',
      description: '各账号登录进度',
      items: {
        type: 'object',
        additionalProperties: true,
      },
    },
  },
} as const;

export const SocialLoginSessionResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/SocialLoginSession',
        },
      },
    },
  ],
} as const;

export const SocialLoginResultResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          additionalProperties: true,
        },
      },
    },
  ],
} as const;

export const SocialLoginBatchResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/SocialLoginBatch',
        },
      },
    },
  ],
} as const;

export const RecognizeSocialAccountInputSchema = {
  type: 'object',
  description: '待识别的账号原始信息',
  additionalProperties: false,
  required: ['platform', 'accountInfo'],
  properties: {
    platform: {
      $ref: '#/components/schemas/SocialPlatformKey',
    },
    userName: {
      type: 'string',
      maxLength: 255,
      description: '账号备注',
    },
    accountInfo: {
      type: 'string',
      minLength: 1,
      maxLength: 4096,
      description: '账号、密码、二次验证密钥等原始账号信息',
    },
  },
} as const;

export const RecognizedSocialAccountSchema = {
  type: 'object',
  description: '标准化后的社交账号信息',
  required: ['platform'],
  properties: {
    platform: {
      $ref: '#/components/schemas/SocialPlatformKey',
    },
    userName: {
      type: 'string',
      nullable: true,
      description: '账号备注',
    },
    loginIdentifier: {
      type: 'string',
      nullable: true,
      description: '账号或登录标识',
    },
    password: {
      type: 'string',
      nullable: true,
      writeOnly: true,
      description: '登录密码',
    },
    totpSecret: {
      type: 'string',
      nullable: true,
      writeOnly: true,
      description: '二次验证密钥',
    },
  },
} as const;

export const RecognizeSocialAccountsRequestSchema = {
  type: 'object',
  description: '批量识别社交账号请求；正式实现前接口返回 501。',
  additionalProperties: false,
  required: ['accounts'],
  properties: {
    accounts: {
      type: 'array',
      minItems: 1,
      maxItems: 100,
      description: '待识别账号数组',
      items: {
        $ref: '#/components/schemas/RecognizeSocialAccountInput',
      },
    },
  },
} as const;

export const ManualBindSocialAccountsRequestSchema = {
  type: 'object',
  description: '一次完成社交账号创建和分类绑定的请求；正式实现前接口返回 501。',
  additionalProperties: false,
  required: ['categoryId', 'accounts'],
  properties: {
    categoryId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
      description: '目标账号分类唯一标识',
    },
    accounts: {
      type: 'array',
      minItems: 1,
      maxItems: 100,
      description: '待手动绑定的账号数组',
      items: {
        $ref: '#/components/schemas/CreateSocialAccountRequest',
      },
    },
  },
} as const;

export const RecognizeSocialAccountsResponseSchema = {
  description: '批量识别社交账号响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          required: ['accounts'],
          properties: {
            accounts: {
              type: 'array',
              items: {
                $ref: '#/components/schemas/RecognizedSocialAccount',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const SocialAccountOverviewResponseSchema = {
  description: '社交账号数据概览响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/SocialAccountOverview',
        },
      },
    },
  ],
} as const;

export const SocialCredentialPresenceSchema = {
  type: 'object',
  description: '社媒账号登录凭据明文信息，供账号登录弹窗展示和浏览器登录使用',
  required: ['hasLoginIdentifier', 'hasPassword', 'hasTotp'],
  properties: {
    loginIdentifier: {
      type: 'string',
      nullable: true,
      description: '登录账号、手机号或邮箱',
    },
    password: {
      type: 'string',
      nullable: true,
      description: '平台登录密码',
    },
    totpSecret: {
      type: 'string',
      nullable: true,
      description: 'TOTP 二步验证密钥',
    },
    hasLoginIdentifier: {
      type: 'boolean',
      description: '是否已保存登录标识',
    },
    hasPassword: {
      type: 'boolean',
      description: '是否已保存密码',
    },
    hasTotp: {
      type: 'boolean',
      description: '是否已保存 TOTP 密钥',
    },
  },
} as const;

export const SocialAccountSchema = {
  type: 'object',
  description: '当前用户拥有的社交平台账号公开信息',
  required: [
    'accountId',
    'platform',
    'platformType',
    'platformName',
    'userName',
    'status',
    'accountStatus',
    'loginStatus',
    'credential',
  ],
  properties: {
    accountId: {
      type: 'string',
      description: '社交账号 ID',
    },
    platform: {
      $ref: '#/components/schemas/SocialPlatformKey',
    },
    platformType: {
      type: 'integer',
      description: '平台类型编号',
    },
    platformName: {
      type: 'string',
      description: '平台中文名称',
    },
    userName: {
      type: 'string',
      description: '账号展示名称',
    },
    accountUsername: {
      type: 'string',
      nullable: true,
      description: '平台账号用户名',
    },
    remark: {
      type: 'string',
      nullable: true,
      description: '账号备注',
    },
    avatarUrl: {
      type: 'string',
      nullable: true,
      description: '平台账号头像',
    },
    platformLogoUrl: {
      type: 'string',
      nullable: true,
      description: '平台 Logo',
    },
    status: {
      type: 'integer',
      enum: [0, 1],
      description: '账号启用状态，0 为停用，1 为启用',
    },
    accountStatus: {
      type: 'string',
      enum: ['valid', 'invalid'],
      description: '账号管理公开状态',
    },
    loginStatus: {
      type: 'string',
      enum: ['valid', 'invalid'],
      description: '兼容字段，仅公开有效或无效',
    },
    lastValidatedAt: {
      type: 'string',
      format: 'date-time',
      nullable: true,
      description: '最近一次登录状态验证时间',
    },
    credential: {
      $ref: '#/components/schemas/SocialCredentialPresence',
    },
    workflows: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/SocialAccountWorkflow',
      },
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '创建时间',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: '更新时间',
    },
  },
} as const;

export const SocialAccountWorkflowSchema = {
  type: 'object',
  required: [
    'workflowId',
    'executionId',
    'name',
    'title',
    'status',
    'createdAt',
    'updatedAt',
    'url',
  ],
  properties: {
    workflowId: {
      type: 'string',
    },
    executionId: {
      type: 'string',
    },
    name: {
      type: 'string',
      description: '工作流展示名称，与 title 保持一致',
    },
    title: {
      type: 'string',
    },
    status: {
      type: 'string',
    },
    url: {
      type: 'string',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
    },
  },
} as const;

export const CreateSocialAccountRequestSchema = {
  type: 'object',
  description: '创建单个社交账号的请求参数',
  additionalProperties: false,
  required: ['platform', 'userName'],
  properties: {
    platform: {
      $ref: '#/components/schemas/SocialPlatformKey',
    },
    userName: {
      type: 'string',
      minLength: 1,
      maxLength: 255,
      description: '账号展示名称',
    },
    loginIdentifier: {
      type: 'string',
      minLength: 1,
      maxLength: 512,
      description: '平台登录标识，例如手机号、邮箱或用户名',
    },
    password: {
      type: 'string',
      minLength: 1,
      maxLength: 2048,
      description: '平台登录密码，按明文保存以适配账号登录页面',
    },
    totpSecret: {
      type: 'string',
      minLength: 1,
      maxLength: 512,
      description: 'TOTP 二步验证密钥',
    },
  },
} as const;

export const BatchCreateSocialAccountsRequestSchema = {
  type: 'object',
  description: '批量创建社交账号的请求参数',
  additionalProperties: false,
  required: ['accounts'],
  properties: {
    pendingBinding: {
      type: 'boolean',
      default: false,
      description: 'true 表示账号暂不进入账号管理列表，只有自动绑定成功后转为正式账号',
    },
    accounts: {
      type: 'array',
      minItems: 1,
      maxItems: 100,
      description: '待创建的账号列表，单批最多 100 个',
      items: {
        $ref: '#/components/schemas/CreateSocialAccountRequest',
      },
    },
  },
} as const;

export const CreateSocialAccountAutoBindingBatchRequestSchema = {
  type: 'object',
  description: '原子创建待绑定账号和浏览器登录批次的请求参数',
  additionalProperties: false,
  required: ['categoryId', 'accounts'],
  properties: {
    categoryId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
      description: '当前用户拥有的目标分类 ID',
    },
    mode: {
      type: 'string',
      enum: ['auto', 'manual'],
      default: 'auto',
      description: 'auto 执行自动登录脚本；manual 只打开内置浏览器，全部动作由用户完成',
    },
    accounts: {
      type: 'array',
      minItems: 1,
      maxItems: 100,
      items: {
        $ref: '#/components/schemas/CreateSocialAccountRequest',
      },
    },
  },
} as const;

export const UpdateSocialCredentialRequestSchema = {
  type: 'object',
  description: '覆盖更新账号登录凭据的请求参数；显式传空值表示清除对应凭据',
  additionalProperties: false,
  minProperties: 1,
  properties: {
    loginIdentifier: {
      type: 'string',
      nullable: true,
      description: '新登录标识',
    },
    password: {
      type: 'string',
      nullable: true,
      description: '新登录密码',
    },
    totpSecret: {
      type: 'string',
      nullable: true,
      description: '新 TOTP 二步验证密钥',
    },
  },
} as const;

export const UpdateSocialAccountProfileRequestSchema = {
  type: 'object',
  description: '更新登录验证采集到的平台账号资料',
  additionalProperties: false,
  minProperties: 1,
  properties: {
    accountUsername: {
      type: 'string',
      nullable: true,
      minLength: 1,
      maxLength: 255,
      description: '平台账号用户名',
    },
    avatarUrl: {
      type: 'string',
      format: 'uri',
      nullable: true,
      maxLength: 2048,
      description: '平台账号头像地址',
    },
    platformLogoUrl: {
      type: 'string',
      format: 'uri',
      nullable: true,
      maxLength: 2048,
      description: '平台 Logo 地址',
    },
  },
} as const;

export const UpdateSocialAccountRemarkRequestSchema = {
  type: 'object',
  description: '更新社交账号展示备注的请求参数',
  additionalProperties: false,
  required: ['userName'],
  properties: {
    userName: {
      type: 'string',
      minLength: 1,
      maxLength: 255,
      description: '新的账号展示备注',
    },
  },
} as const;

export const SaveSocialBrowserStateRequestSchema = {
  type: 'object',
  description: '保存浏览器登录状态的请求参数',
  additionalProperties: false,
  required: ['storageState'],
  properties: {
    storageState: {
      type: 'object',
      description: 'Playwright Storage State 对象，由服务端加密保存',
      additionalProperties: true,
      writeOnly: true,
    },
  },
} as const;

export const ImportSocialBrowserStateRequestSchema = {
  type: 'object',
  description: '导入 Cookie 数组或 Playwright Storage State 的请求参数',
  additionalProperties: false,
  required: ['storageState'],
  properties: {
    storageState: {
      description: 'Cookie JSON 数组或 Playwright Storage State，最大 2 MiB',
      writeOnly: true,
      oneOf: [
        {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: true,
          },
        },
        {
          type: 'object',
          additionalProperties: true,
        },
      ],
    },
  },
} as const;

export const UpdateSocialAccountStatusRequestSchema = {
  type: 'object',
  description: '更新社交账号启用状态的请求参数',
  additionalProperties: false,
  required: ['status'],
  properties: {
    status: {
      type: 'integer',
      enum: [0, 1],
      description: '账号启用状态，0 为停用，1 为启用',
    },
  },
} as const;

export const SocialAccountCategorySchema = {
  type: 'object',
  description: '当前用户定义的社交账号分类',
  required: ['categoryId', 'name', 'isDefault', 'createdAt', 'updatedAt'],
  properties: {
    categoryId: {
      type: 'string',
      description: '分类 ID',
    },
    uid: {
      type: 'string',
      description: '所属用户 ID',
    },
    name: {
      type: 'string',
      description: '分类名称',
    },
    description: {
      type: 'string',
      nullable: true,
      description: '分类说明',
    },
    icon: {
      type: 'string',
      nullable: true,
      description: '分类图标标识',
    },
    isDefault: {
      type: 'boolean',
      description: '是否为不可删除的默认分类',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '创建时间',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: '更新时间',
    },
    deletedAt: {
      type: 'string',
      format: 'date-time',
      nullable: true,
      description: '软删除时间，未删除时为空',
    },
  },
} as const;

export const SocialAccountCategoryRelationSchema = {
  type: 'object',
  description: '社交账号与分类的关联关系',
  required: ['accountId', 'categoryId', 'createdAt'],
  properties: {
    accountId: {
      type: 'string',
      description: '社交账号 ID',
    },
    categoryId: {
      type: 'string',
      description: '账号分类 ID',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '关联创建时间',
    },
    deletedAt: {
      type: 'string',
      format: 'date-time',
      nullable: true,
      description: '关联解除时间，仍有效时为空',
    },
  },
} as const;

export const CreateSocialAccountCategoryRequestSchema = {
  type: 'object',
  description: '创建社交账号分类的请求参数',
  additionalProperties: false,
  required: ['name'],
  properties: {
    name: {
      type: 'string',
      minLength: 1,
      maxLength: 100,
      description: '分类名称，同一用户下不可重复',
    },
    description: {
      type: 'string',
      maxLength: 500,
      description: '分类说明',
    },
    icon: {
      type: 'string',
      maxLength: 100,
      description: '分类图标标识',
    },
  },
} as const;

export const UpdateSocialAccountCategoryRequestSchema = {
  type: 'object',
  description: '更新社交账号分类的请求参数',
  additionalProperties: false,
  minProperties: 1,
  properties: {
    name: {
      type: 'string',
      minLength: 1,
      maxLength: 100,
      description: '新分类名称，同一用户下不可重复',
    },
    description: {
      type: 'string',
      maxLength: 500,
      description: '新分类说明',
    },
    icon: {
      type: 'string',
      maxLength: 100,
      description: '新分类图标标识',
    },
  },
} as const;

export const BatchSocialAccountCategoryRequestSchema = {
  type: 'object',
  description: '批量关联或解除账号分类的请求参数',
  additionalProperties: false,
  required: ['accountIds', 'categoryId'],
  properties: {
    accountIds: {
      type: 'array',
      minItems: 1,
      uniqueItems: true,
      description: '当前用户拥有的社交账号 ID 列表',
      items: {
        type: 'string',
        minLength: 1,
        maxLength: 128,
      },
    },
    categoryId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
      description: '当前用户拥有的账号分类 ID',
    },
  },
} as const;

export const MoveSocialAccountsCategoryRequestSchema = {
  type: 'object',
  description: '批量移动社交账号分类的请求参数',
  additionalProperties: false,
  required: ['accountIds', 'sourceCategoryId', 'targetCategoryId'],
  properties: {
    accountIds: {
      type: 'array',
      minItems: 1,
      maxItems: 100,
      uniqueItems: true,
      description: '当前用户拥有的社交账号 ID 列表',
      items: {
        type: 'string',
        minLength: 1,
        maxLength: 128,
      },
    },
    sourceCategoryId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
      description: '原账号分类 ID',
    },
    targetCategoryId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
      description: '目标账号分类 ID，不能与原分类相同',
    },
  },
} as const;

export const VideoEditStrategySchema = {
  type: 'string',
  enum: [
    'highlight-montage',
    'story-narration',
    'series-recommendation',
    'overlay-only',
    'assemble',
    'speech_cleanup',
    'highlight',
  ],
} as const;

export const VideoEditVersionSourceSchema = {
  type: 'string',
  enum: ['ai_rough_cut', 'opencut_manual'],
} as const;

export const VideoOutputProfileSchema = {
  type: 'object',
  additionalProperties: false,
  properties: {
    profileId: {
      type: 'string',
    },
    aspectRatio: {
      type: 'string',
      example: '9:16',
    },
    width: {
      type: 'integer',
      minimum: 16,
      maximum: 7680,
      default: 1080,
    },
    height: {
      type: 'integer',
      minimum: 16,
      maximum: 7680,
      default: 1920,
    },
    fps: {
      type: 'integer',
      minimum: 1,
      maximum: 120,
      default: 30,
    },
    durationSeconds: {
      type: 'integer',
      minimum: 1,
      maximum: 600,
      default: 60,
    },
    subtitle: {
      type: 'boolean',
      default: true,
    },
    subtitleMode: {
      type: 'string',
      enum: ['none', 'preserve', 'localized', 'bilingual'],
    },
    narrationRequired: {
      type: 'boolean',
      default: false,
    },
    title: {
      type: 'string',
      maxLength: 500,
    },
    targetPlatform: {
      type: 'string',
      minLength: 1,
      maxLength: 64,
    },
    strategyMarket: {
      type: 'string',
      enum: ['zh-CN', 'en-US', 'fil-PH', 'id-ID', 'th-TH', 'vi-VN'],
    },
    musicAssetId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
    },
    providerItemId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
    },
    visionProviderItemId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
    },
    textProviderItemId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
    },
    narrativeReview: {
      $ref: '#/components/schemas/VideoNarrativeReview',
    },
  },
} as const;

export const VideoNarrativeReviewSchema = {
  type: 'object',
  required: [
    'status',
    'source',
    'localizedText',
    'chineseReviewText',
    'visualIntent',
    'factRefs',
    'offlinePrompt',
    'generatedAt',
  ],
  properties: {
    status: {
      type: 'string',
      enum: ['draft', 'manual', 'confirmed'],
    },
    source: {
      type: 'string',
      enum: ['model', 'manual'],
    },
    localizedText: {
      type: 'string',
      maxLength: 10000,
    },
    chineseReviewText: {
      type: 'string',
      maxLength: 10000,
    },
    englishReviewText: {
      type: 'string',
      maxLength: 10000,
    },
    visualIntent: {
      type: 'array',
      maxItems: 50,
      items: {
        type: 'string',
        maxLength: 500,
      },
    },
    factRefs: {
      type: 'array',
      maxItems: 100,
      items: {
        type: 'string',
        maxLength: 500,
      },
    },
    offlinePrompt: {
      type: 'string',
    },
    textProviderItemId: {
      type: 'string',
    },
    visionProviderItemId: {
      type: 'string',
    },
    generatedAt: {
      type: 'string',
      format: 'date-time',
    },
    confirmedAt: {
      type: 'string',
      format: 'date-time',
    },
    confirmedNarrative: {
      type: 'object',
      additionalProperties: true,
    },
  },
} as const;

export const VideoEditVersionAssetSchema = {
  type: 'object',
  required: ['assetId', 'role', 'orderIndex'],
  properties: {
    assetId: {
      type: 'string',
    },
    role: {
      type: 'string',
      enum: ['input', 'music', 'subtitle', 'preview', 'output', 'variant'],
    },
    orderIndex: {
      type: 'integer',
      minimum: 0,
    },
    analysisId: {
      type: 'string',
      nullable: true,
    },
  },
} as const;

export const VideoEditVariantSchema = {
  type: 'object',
  required: ['assetId', 'profileId', 'accountIds', 'duration', 'width', 'height'],
  properties: {
    assetId: {
      type: 'string',
    },
    profileId: {
      type: 'string',
    },
    accountIds: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
    duration: {
      type: 'number',
      minimum: 0,
    },
    width: {
      type: 'integer',
      minimum: 1,
    },
    height: {
      type: 'integer',
      minimum: 1,
    },
  },
} as const;

export const VideoEditVersionSchema = {
  type: 'object',
  required: [
    'versionId',
    'projectId',
    'version',
    'source',
    'strategy',
    'status',
    'assets',
    'createdAt',
    'updatedAt',
  ],
  properties: {
    versionId: {
      type: 'string',
    },
    projectId: {
      type: 'string',
    },
    version: {
      type: 'integer',
      minimum: 1,
    },
    parentVersionId: {
      type: 'string',
      nullable: true,
    },
    source: {
      $ref: '#/components/schemas/VideoEditVersionSource',
    },
    strategy: {
      $ref: '#/components/schemas/VideoEditStrategy',
    },
    request: {
      type: 'string',
    },
    feedback: {
      type: 'string',
      nullable: true,
    },
    outputProfile: {
      $ref: '#/components/schemas/VideoOutputProfile',
    },
    previewAssetId: {
      type: 'string',
      nullable: true,
    },
    outputAssetId: {
      type: 'string',
      nullable: true,
    },
    status: {
      type: 'string',
      enum: [
        'pending',
        'analyzing',
        'awaiting_review',
        'deciding',
        'rendering',
        'completed',
        'failed',
        'cancelled',
      ],
    },
    errorCode: {
      type: 'string',
      nullable: true,
    },
    errorMessage: {
      type: 'string',
      nullable: true,
    },
    assets: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/VideoEditVersionAsset',
      },
    },
    variants: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/VideoEditVariant',
      },
    },
    previewUrl: {
      type: 'string',
      format: 'uri',
      nullable: true,
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
    },
  },
} as const;

export const VideoEditProjectSchema = {
  type: 'object',
  required: [
    'projectId',
    'canvasId',
    'executionId',
    'nodeId',
    'status',
    'revision',
    'versions',
    'createdAt',
    'updatedAt',
  ],
  properties: {
    projectId: {
      type: 'string',
    },
    canvasId: {
      type: 'string',
    },
    executionId: {
      type: 'string',
    },
    nodeId: {
      type: 'string',
    },
    status: {
      type: 'string',
      enum: ['pending', 'processing', 'awaiting_review', 'completed', 'failed', 'cancelled'],
    },
    revision: {
      type: 'integer',
      minimum: 1,
    },
    currentVersionId: {
      type: 'string',
      nullable: true,
    },
    currentOutputAssetId: {
      type: 'string',
      nullable: true,
    },
    versions: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/VideoEditVersion',
      },
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
    },
  },
} as const;

export const CreateVideoEditProjectRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['executionId', 'nodeId', 'assetIds'],
  properties: {
    executionId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
    },
    nodeId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
    },
    assetIds: {
      type: 'array',
      minItems: 1,
      maxItems: 20,
      uniqueItems: true,
      items: {
        type: 'string',
      },
    },
    request: {
      type: 'string',
      maxLength: 20000,
      default: '',
    },
    strategy: {
      $ref: '#/components/schemas/VideoEditStrategy',
    },
    providerItemId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
    },
    visionProviderItemId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
    },
    textProviderItemId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
    },
    accountIds: {
      type: 'array',
      maxItems: 100,
      uniqueItems: true,
      items: {
        type: 'string',
      },
    },
    outputProfile: {
      $ref: '#/components/schemas/VideoOutputProfile',
    },
  },
} as const;

export const ConfirmVideoNarrativeRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['idempotencyKey', 'localizedText', 'chineseReviewText'],
  properties: {
    idempotencyKey: {
      type: 'string',
      minLength: 8,
      maxLength: 128,
    },
    localizedText: {
      type: 'string',
      minLength: 1,
      maxLength: 10000,
    },
    chineseReviewText: {
      type: 'string',
      minLength: 1,
      maxLength: 10000,
    },
    englishReviewText: {
      type: 'string',
      maxLength: 10000,
    },
    visualIntent: {
      type: 'array',
      maxItems: 50,
      items: {
        type: 'string',
        minLength: 1,
        maxLength: 500,
      },
    },
  },
} as const;

export const RegenerateVideoNarrativeRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['idempotencyKey'],
  properties: {
    idempotencyKey: {
      type: 'string',
      minLength: 8,
      maxLength: 128,
    },
    request: {
      type: 'string',
      maxLength: 20000,
    },
  },
} as const;

export const AttachVideoEditAssetsRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['assetIds'],
  properties: {
    assetIds: {
      type: 'array',
      minItems: 1,
      maxItems: 20,
      uniqueItems: true,
      items: {
        type: 'string',
      },
    },
  },
} as const;

export const AttachVideoEditAssetsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          required: ['projectId', 'assetIds'],
          properties: {
            projectId: {
              type: 'string',
            },
            assetIds: {
              type: 'array',
              items: {
                type: 'string',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const SaveVideoEditSnapshotRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['expectedProjectRevision', 'schemaVersion', 'snapshot'],
  properties: {
    expectedProjectRevision: {
      type: 'integer',
      minimum: 1,
    },
    schemaVersion: {
      type: 'integer',
      minimum: 1,
      maximum: 100,
      default: 31,
    },
    snapshot: {
      type: 'object',
      additionalProperties: true,
    },
  },
} as const;

export const CompleteVideoManualEditRequestSchema = {
  type: 'object',
  additionalProperties: false,
  required: ['idempotencyKey', 'expectedProjectRevision', 'outputAssetId'],
  properties: {
    idempotencyKey: {
      type: 'string',
      minLength: 8,
      maxLength: 128,
    },
    expectedProjectRevision: {
      type: 'integer',
      minimum: 1,
    },
    outputAssetId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
    },
    schemaVersion: {
      type: 'integer',
      minimum: 1,
      maximum: 100,
      default: 31,
    },
    snapshot: {
      type: 'object',
      additionalProperties: true,
    },
  },
} as const;

export const VideoEditProjectResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/VideoEditProject',
        },
      },
    },
  ],
} as const;

export const VideoEditSnapshotSchema = {
  type: 'object',
  required: ['projectId', 'revision', 'assets'],
  properties: {
    projectId: {
      type: 'string',
    },
    revision: {
      type: 'integer',
      minimum: 1,
    },
    schemaVersion: {
      type: 'integer',
      nullable: true,
    },
    snapshot: {
      type: 'object',
      nullable: true,
      additionalProperties: true,
    },
    assets: {
      type: 'array',
      description: 'OpenCut 按 assetId 缓存的工程素材与短期访问地址',
      items: {
        type: 'object',
        required: ['assetId', 'name', 'mimeType', 'mediaKind', 'size', 'url', 'expiresAt'],
        properties: {
          assetId: {
            type: 'string',
          },
          name: {
            type: 'string',
          },
          mimeType: {
            type: 'string',
          },
          mediaKind: {
            type: 'string',
            enum: ['image', 'video', 'audio'],
          },
          size: {
            type: 'integer',
            minimum: 0,
          },
          width: {
            type: 'integer',
            nullable: true,
          },
          height: {
            type: 'integer',
            nullable: true,
          },
          durationMs: {
            type: 'integer',
            nullable: true,
          },
          url: {
            type: 'string',
            format: 'uri',
          },
          expiresAt: {
            type: 'string',
            format: 'date-time',
          },
        },
      },
    },
  },
} as const;

export const VideoEditSnapshotResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/VideoEditSnapshot',
        },
      },
    },
  ],
} as const;

export const VideoEditVersionResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/VideoEditVersion',
        },
      },
    },
  ],
} as const;

export const SocialBrowserSessionSchema = {
  type: 'object',
  description: '短期登录或人工接管浏览器会话',
  required: [
    'sessionId',
    'accountId',
    'purpose',
    'status',
    'stage',
    'viewerMode',
    'automationPaused',
    'extensionCount',
    'expiresAt',
  ],
  properties: {
    sessionId: {
      type: 'string',
      description: '浏览器会话 ID',
    },
    accountId: {
      type: 'string',
      description: '关联的社交账号 ID',
    },
    taskId: {
      type: 'string',
      nullable: true,
      description: '人工接管关联的发布任务 ID',
    },
    batchId: {
      type: 'string',
      nullable: true,
      description: '所属批量会话 ID',
    },
    purpose: {
      type: 'string',
      enum: ['login', 'publish', 'human_takeover'],
      description: '会话用途，login 为登录，publish 为直发，human_takeover 为人工接管',
    },
    status: {
      $ref: '#/components/schemas/SocialBrowserSessionStatus',
    },
    stage: {
      type: 'string',
      description: '当前执行阶段',
    },
    viewerMode: {
      type: 'string',
      enum: ['novnc_proxy'],
      description: '当前会话使用的 noVNC 画面模式',
    },
    automationPaused: {
      type: 'boolean',
      description: '是否已暂停自动 TOTP、页面探测和中间设备状态保存',
    },
    extensionCount: {
      type: 'integer',
      minimum: 0,
      maximum: 1,
      description: '已续期次数',
    },
    errorCode: {
      type: 'string',
      nullable: true,
      description: '结构化错误码',
    },
    errorMessage: {
      type: 'string',
      nullable: true,
      description: '脱敏错误说明',
    },
    expiresAt: {
      type: 'string',
      format: 'date-time',
      description: '会话过期时间',
    },
    completedAt: {
      type: 'string',
      format: 'date-time',
      nullable: true,
      description: '会话结束时间',
    },
    accessToken: {
      type: 'string',
      writeOnly: true,
      description: '仅创建响应返回一次的短期令牌',
    },
    userName: {
      type: 'string',
      description: '账号备注',
    },
    platform: {
      $ref: '#/components/schemas/SocialPlatformKey',
    },
    platformName: {
      type: 'string',
      description: '平台中文名称',
    },
    credential: {
      $ref: '#/components/schemas/SocialCredentialPresence',
    },
    bindingStatus: {
      type: 'string',
      enum: ['queued', 'binding', 'bound', 'cancelled'],
      description: '自动绑定弹窗四态',
    },
  },
} as const;

export const SocialBrowserSessionStatusSchema = {
  type: 'string',
  description: '互动浏览器会话状态',
  enum: [
    'queued',
    'starting',
    'waiting_user',
    'saving',
    'succeeded',
    'failed',
    'cancelling',
    'cancelled',
    'expired',
  ],
} as const;

export const SocialBrowserViewerTicketSchema = {
  type: 'object',
  description: '互动浏览器画面短期票据',
  required: ['ticket', 'expiresAt'],
  properties: {
    ticket: {
      type: 'string',
      writeOnly: true,
      description: '仅本次响应返回的短期票据',
    },
    expiresAt: {
      type: 'string',
      format: 'date-time',
      description: '首次使用截止时间',
    },
  },
} as const;

export const OpenSocialBrowserViewerConnectionRequestSchema = {
  type: 'object',
  description: '用一次性票据建立画面连接的请求参数',
  additionalProperties: false,
  required: ['ticket'],
  properties: {
    ticket: {
      type: 'string',
      minLength: 20,
      maxLength: 512,
      writeOnly: true,
      description: '尚未使用的一次性画面票据',
    },
  },
} as const;

export const SetSocialBrowserSessionAutomationRequestSchema = {
  type: 'object',
  description: '暂停或恢复浏览器会话自动操作的请求参数',
  additionalProperties: false,
  required: ['paused'],
  properties: {
    paused: {
      type: 'boolean',
      description: 'true 暂停自动操作，false 恢复自动操作',
    },
  },
} as const;

export const SocialBrowserViewerConnectionSchema = {
  type: 'object',
  description: '当前浏览器会话的短期画面连接凭据',
  required: ['viewerToken', 'expiresAt', 'viewerMode'],
  properties: {
    viewerToken: {
      type: 'string',
      writeOnly: true,
      description: '当前画面模式使用的短期连接令牌，仅返回一次且不得持久化',
    },
    expiresAt: {
      type: 'string',
      format: 'date-time',
      description: '连接令牌过期时间',
    },
    viewerMode: {
      type: 'string',
      enum: ['novnc_proxy'],
      description: '本次连接使用的 noVNC 画面模式',
    },
    websocketPath: {
      type: 'string',
      pattern: '^/ws/media-publish/browser-sessions/[A-Za-z0-9-]+/vnc$',
      description: 'noVNC 使用的同源相对 WebSocket 路径',
    },
    rfbPassword: {
      type: 'string',
      minLength: 1,
      maxLength: 8,
      writeOnly: true,
      description: 'noVNC 会话级 RFB 凭据，仅返回一次并只保存在当前浏览器内存',
    },
  },
} as const;

export const CreateSocialBrowserSessionBatchRequestSchema = {
  type: 'object',
  description: '创建批量登录会话的请求参数',
  additionalProperties: false,
  required: ['accountIds'],
  properties: {
    accountIds: {
      type: 'array',
      minItems: 1,
      maxItems: 100,
      uniqueItems: true,
      description: '当前用户拥有的账号 ID 列表',
      items: {
        type: 'string',
        minLength: 1,
        maxLength: 128,
      },
    },
  },
} as const;

export const SocialBrowserSessionBatchSchema = {
  type: 'object',
  description: '批量互动浏览器会话及持久化进度',
  required: [
    'batchId',
    'status',
    'totalCount',
    'queuedCount',
    'runningCount',
    'succeededCount',
    'failedCount',
    'cancelledCount',
    'expiredCount',
    'maxConcurrency',
    'sessions',
  ],
  properties: {
    batchId: {
      type: 'string',
      description: '批次 ID',
    },
    status: {
      type: 'string',
      enum: ['queued', 'running', 'succeeded', 'completed_with_errors', 'cancelled'],
      description: '批次状态',
    },
    totalCount: {
      type: 'integer',
      minimum: 1,
      maximum: 100,
      description: '总会话数',
    },
    queuedCount: {
      type: 'integer',
      minimum: 0,
      description: '排队数',
    },
    runningCount: {
      type: 'integer',
      minimum: 0,
      maximum: 3,
      description: '启动或运行数',
    },
    succeededCount: {
      type: 'integer',
      minimum: 0,
      description: '成功数',
    },
    failedCount: {
      type: 'integer',
      minimum: 0,
      description: '失败数',
    },
    cancelledCount: {
      type: 'integer',
      minimum: 0,
      description: '取消数',
    },
    expiredCount: {
      type: 'integer',
      minimum: 0,
      description: '过期数',
    },
    maxConcurrency: {
      type: 'integer',
      minimum: 1,
      maximum: 3,
      description: '当前批次并发上限',
    },
    sessions: {
      type: 'array',
      description: '批次内会话列表',
      items: {
        $ref: '#/components/schemas/SocialBrowserSession',
      },
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '创建时间',
    },
    completedAt: {
      type: 'string',
      format: 'date-time',
      nullable: true,
      description: '完成时间',
    },
  },
} as const;

export const SocialPublishExecutorStatusSchema = {
  type: 'object',
  description: '脱敏的 MPP 执行器状态',
  required: ['available', 'interactiveEnabled', 'mockMode', 'platforms', 'viewerMode'],
  properties: {
    available: {
      type: 'boolean',
      description: 'MPP 是否可访问',
    },
    interactiveEnabled: {
      type: 'boolean',
      description: '互动浏览器功能开关',
    },
    mockMode: {
      type: 'boolean',
      description: 'MPP 是否处于 Mock 验证模式',
    },
    platforms: {
      type: 'array',
      description: '执行器已接线的平台标识',
      items: {
        $ref: '#/components/schemas/SocialPlatformKey',
      },
    },
    viewerMode: {
      type: 'string',
      enum: ['novnc_proxy'],
      description: '执行器使用的安全 noVNC 画面模式',
    },
    errorMessage: {
      type: 'string',
      nullable: true,
      description: '执行器不可用时的脱敏错误说明',
    },
  },
} as const;

export const SocialPlatformListResponseSchema = {
  description: '支持的社交平台列表响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: '支持的社交平台列表',
          items: {
            $ref: '#/components/schemas/SocialPlatform',
          },
        },
      },
    },
  ],
} as const;

export const SocialAccountListResponseSchema = {
  description: '社交账号列表响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: '社交账号列表',
          items: {
            $ref: '#/components/schemas/SocialAccount',
          },
        },
      },
    },
  ],
} as const;

export const SocialAccountResponseSchema = {
  description: '单个社交账号响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/SocialAccount',
        },
      },
    },
  ],
} as const;

export const SocialAccountWorkflowListResponseSchema = {
  description: '账号关联工作流列表响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/SocialAccountWorkflow',
          },
        },
      },
    },
  ],
} as const;

export const BatchCreateSocialAccountsResponseSchema = {
  description: '批量创建社交账号响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            totalCount: {
              type: 'integer',
              minimum: 1,
              maximum: 100,
              description: '成功创建的账号总数',
            },
            accounts: {
              type: 'array',
              description: '已创建的社交账号列表',
              items: {
                $ref: '#/components/schemas/SocialAccount',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const SocialCredentialPresenceResponseSchema = {
  description: '账号凭据存在性响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/SocialCredentialPresence',
        },
      },
    },
  ],
} as const;

export const SocialAccountCategoryResponseSchema = {
  description: '单个社交账号分类响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/SocialAccountCategory',
        },
      },
    },
  ],
} as const;

export const SocialAccountCategoryListResponseSchema = {
  description: '社交账号分类列表响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: '社交账号分类列表',
          items: {
            $ref: '#/components/schemas/SocialAccountCategory',
          },
        },
      },
    },
  ],
} as const;

export const SocialAccountCategoryRelationResponseSchema = {
  description: '社交账号分类关联响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/SocialAccountCategoryRelation',
        },
      },
    },
  ],
} as const;

export const BatchSocialAccountCategoryResponseSchema = {
  description: '批量关联或解除账号分类响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: '批量关联或解除的处理统计',
          required: ['successCount', 'totalCount'],
          properties: {
            successCount: {
              type: 'integer',
              minimum: 0,
              description: '成功处理的账号数量',
            },
            skippedCount: {
              type: 'integer',
              minimum: 0,
              description: '已有关联而跳过的账号数量，仅批量关联时返回',
            },
            totalCount: {
              type: 'integer',
              minimum: 0,
              description: '本次有效账号总数',
            },
          },
        },
      },
    },
  ],
} as const;

export const MoveSocialAccountsCategoryResponseSchema = {
  description: '批量移动社交账号分类响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          required: [
            'accountCount',
            'boundCount',
            'unboundCount',
            'sourceCategoryId',
            'targetCategoryId',
          ],
          properties: {
            accountCount: {
              type: 'integer',
              minimum: 1,
              description: '移动的账号数量',
            },
            boundCount: {
              type: 'integer',
              minimum: 0,
              description: '已绑定到目标分类的账号数量',
            },
            unboundCount: {
              type: 'integer',
              minimum: 0,
              description: '从原分类解除关联的账号数量',
            },
            sourceCategoryId: {
              type: 'string',
              description: '原分类 ID',
            },
            targetCategoryId: {
              type: 'string',
              description: '目标分类 ID',
            },
          },
        },
      },
    },
  ],
} as const;

export const CreateSocialBrowserSessionRequestSchema = {
  type: 'object',
  description: '创建浏览器会话的请求参数',
  additionalProperties: false,
  properties: {
    purpose: {
      type: 'string',
      enum: ['login', 'human_takeover'],
      default: 'login',
      description: '会话用途，默认用于账号登录',
    },
  },
} as const;

export const SocialBrowserSessionResponseSchema = {
  description: '浏览器会话响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/SocialBrowserSession',
        },
      },
    },
  ],
} as const;

export const SocialBrowserViewerTicketResponseSchema = {
  description: '浏览器画面短期票据响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/SocialBrowserViewerTicket',
        },
      },
    },
  ],
} as const;

export const SocialBrowserViewerConnectionResponseSchema = {
  description: '浏览器画面连接响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/SocialBrowserViewerConnection',
        },
      },
    },
  ],
} as const;

export const SocialBrowserSessionBatchResponseSchema = {
  description: '批量浏览器会话响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/SocialBrowserSessionBatch',
        },
      },
    },
  ],
} as const;

export const SocialPublishExecutorStatusResponseSchema = {
  description: '媒体发布执行器状态响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/SocialPublishExecutorStatus',
        },
      },
    },
  ],
} as const;

export const ExtractVariablesRequestSchema = {
  type: 'object',
  required: ['prompt', 'canvasId', 'mode'],
  properties: {
    prompt: {
      type: 'string',
      description: 'Natural language prompt describing the desired functionality',
      minLength: 1,
      maxLength: 500,
      example: 'Create a user registration form with username, email, and password fields',
    },
    canvasId: {
      type: 'string',
      description: 'Canvas ID to retrieve existing variable context and content',
      pattern: '^[a-zA-Z0-9_-]+$',
      example: 'canvas-123',
    },
    mode: {
      type: 'string',
      enum: ['direct', 'candidate'],
      description: `Processing mode for variable extraction:
- direct: Directly update Canvas variables and return results
- candidate: Return candidate solutions for user selection
`,
      example: 'candidate',
    },
    sessionId: {
      type: 'string',
      description: `Optional session ID for direct mode to check for existing candidate records.
When provided in direct mode, the system will check if there are
unapplied candidate records for this session.
`,
      example: 'session-789',
    },
  },
} as const;

export const VariableExtractionResultSchema = {
  type: 'object',
  required: ['originalPrompt', 'processedPrompt', 'variables', 'reusedVariables'],
  properties: {
    originalPrompt: {
      type: 'string',
      description: 'Original natural language prompt from user input',
      example: 'Create a user registration form with username, email, and password fields',
    },
    processedPrompt: {
      type: 'string',
      description: `Processed prompt with variable references embedded.
Variables are represented using handlebars syntax (e.g., {{variableName}}).
`,
      example:
        'Create a user registration form with {{username}}, {{email}}, and {{password}} fields',
    },
    variables: {
      type: 'array',
      description: 'List of extracted workflow variables',
      items: {
        $ref: '#/components/schemas/WorkflowVariable',
      },
    },
    reusedVariables: {
      type: 'array',
      description: 'List of variables that were reused from existing context',
      items: {
        $ref: '#/components/schemas/VariableReuse',
      },
    },
    sessionId: {
      type: 'string',
      description: `Session ID for candidate mode responses.
This ID can be used in subsequent direct mode requests
to apply the selected candidate solution.
`,
      example: 'session-abc123',
    },
    extractionConfidence: {
      type: 'number',
      description: 'Confidence score for the extraction (0.0 to 1.0)',
      example: 0.78,
    },
  },
} as const;

export const VariableReuseSchema = {
  type: 'object',
  description: 'Information about variables reused from existing context',
  required: ['detectedText', 'reusedVariableName', 'confidence', 'reason'],
  properties: {
    detectedText: {
      type: 'string',
      description: 'Text in the prompt that triggered variable reuse detection',
      example: 'form',
    },
    reusedVariableName: {
      type: 'string',
      description: 'Name of the existing variable that was reused',
      example: 'formType',
    },
    confidence: {
      type: 'number',
      format: 'float',
      minimum: 0,
      maximum: 1,
      description: 'Confidence score for the reuse detection (0.0 to 1.0)',
      example: 0.78,
    },
    reason: {
      type: 'string',
      description: 'Explanation of why this variable was reused',
      example: 'Similar form structure detected in existing variables',
    },
  },
} as const;

export const McpServerTypeSchema = {
  type: 'string',
  description: 'MCP 服务器类型',
  enum: ['sse', 'streamable', 'stdio'],
} as const;

export const McpServerDTOSchema = {
  type: 'object',
  required: ['name', 'type', 'enabled', 'isGlobal'],
  properties: {
    name: {
      type: 'string',
      description: 'MCP 服务器名称',
      example: 'My MCP Server',
    },
    type: {
      $ref: '#/components/schemas/McpServerType',
    },
    url: {
      type: 'string',
      description: 'MCP 服务器 URL（用于 sse 和 streamable 类型）',
      example: 'https://example.com/mcp',
    },
    command: {
      type: 'string',
      description: '要执行的命令（用于 stdio 类型）',
      example: 'python',
    },
    args: {
      type: 'array',
      description: '命令参数（用于 stdio 类型）',
      items: {
        type: 'string',
      },
      example: ['-m', 'server.py'],
    },
    env: {
      type: 'object',
      description: '环境变量（用于 stdio 类型）',
      additionalProperties: {
        type: 'string',
      },
      example: {
        API_KEY: 'your_api_key',
      },
    },
    headers: {
      type: 'object',
      description: 'HTTP 请求头（用于 sse 和 streamable 类型）',
      additionalProperties: {
        type: 'string',
      },
      example: {
        Authorization: 'Bearer token',
      },
    },
    reconnect: {
      type: 'object',
      description: '重连配置',
      properties: {
        enabled: {
          type: 'boolean',
          description: '是否启用重连',
        },
        maxAttempts: {
          type: 'integer',
          description: '最大重连次数',
        },
        delayMs: {
          type: 'integer',
          description: '重连间隔（毫秒）',
        },
      },
    },
    config: {
      type: 'object',
      description: '其他配置',
      additionalProperties: true,
    },
    enabled: {
      type: 'boolean',
      description: 'MCP 服务器是否启用',
    },
    isGlobal: {
      type: 'boolean',
      description: 'MCP 服务器是否为全局配置',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'MCP 服务器创建时间',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'MCP 服务器更新时间',
    },
  },
} as const;

export const ListMcpServersResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/McpServerDTO',
          },
        },
      },
    },
  ],
} as const;

export const UpsertMcpServerRequestSchema = {
  type: 'object',
  required: ['name', 'type'],
  properties: {
    name: {
      type: 'string',
      description: 'MCP server name',
    },
    type: {
      $ref: '#/components/schemas/McpServerType',
    },
    url: {
      type: 'string',
      description: 'MCP server URL (required for sse and streamable types)',
    },
    command: {
      type: 'string',
      description: 'Command to execute (required for stdio type)',
    },
    args: {
      type: 'array',
      description: 'Command arguments (required for stdio type)',
      items: {
        type: 'string',
      },
    },
    env: {
      type: 'object',
      description: 'Environment variables (for stdio type)',
      additionalProperties: {
        type: 'string',
      },
    },
    headers: {
      type: 'object',
      description: 'HTTP headers (for sse and streamable types)',
      additionalProperties: {
        type: 'string',
      },
    },
    reconnect: {
      type: 'object',
      description: 'Reconnection configuration',
      properties: {
        enabled: {
          type: 'boolean',
          description: 'Whether reconnection is enabled',
        },
        maxAttempts: {
          type: 'integer',
          description: 'Maximum number of reconnection attempts',
        },
        delayMs: {
          type: 'integer',
          description: 'Delay between reconnection attempts in milliseconds',
        },
      },
    },
    config: {
      type: 'object',
      description: 'Additional configuration',
      additionalProperties: true,
    },
    enabled: {
      type: 'boolean',
      description: 'Whether the MCP server is enabled',
    },
  },
} as const;

export const UpsertMcpServerResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/McpServerDTO',
        },
      },
    },
  ],
} as const;

export const DeleteMcpServerRequestSchema = {
  type: 'object',
  required: ['name'],
  properties: {
    name: {
      type: 'string',
      description: 'MCP name to delete',
    },
  },
} as const;

export const McpServerToolSchema = {
  type: 'object',
  required: ['name', 'description'],
  properties: {
    name: {
      type: 'string',
      description: 'Tool name',
      example: 'tool_name',
    },
    description: {
      type: 'string',
      description: 'Tool description',
      example: 'This tool does something useful',
    },
  },
} as const;

export const ValidateMcpServerResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/McpServerTool',
          },
        },
      },
    },
  ],
} as const;

export const DeleteMcpServerResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'null',
        },
      },
    },
  ],
} as const;

export const PageSchema = {
  type: 'object',
  required: ['pageId', 'title', 'createdAt', 'updatedAt'],
  properties: {
    pageId: {
      type: 'string',
      description: 'Page ID',
      example: 'pg-g30e1b80b5g1itbemc0g5jj3',
    },
    title: {
      type: 'string',
      description: 'Page title',
      example: 'My Page',
    },
    description: {
      type: 'string',
      description: 'Page description',
    },
    status: {
      type: 'string',
      description: 'Page status',
      enum: ['draft', 'published'],
    },
    canvasId: {
      type: 'string',
      description: 'Canvas ID associated with this page',
      example: 'cv-g30e1b80b5g1itbemc0g5jj3',
    },
    coverUrl: {
      type: 'string',
      description: 'Page cover URL',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Page creation time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Page last update time',
    },
  },
} as const;

export const PageNodeRelationSchema = {
  type: 'object',
  required: ['relationId', 'nodeId', 'nodeType', 'entityId', 'orderIndex'],
  properties: {
    relationId: {
      type: 'string',
      description: 'Relation ID',
    },
    pageId: {
      type: 'string',
      description: 'Page ID',
    },
    nodeId: {
      type: 'string',
      description: 'Node ID',
    },
    nodeType: {
      type: 'string',
      description: 'Node type',
    },
    entityId: {
      type: 'string',
      description: 'Entity ID',
    },
    orderIndex: {
      type: 'integer',
      description: 'Order index',
    },
    nodeData: {
      description: 'Node data',
      $ref: '#/components/schemas/CanvasNodeData',
    },
  },
} as const;

export const PageDetailSchema = {
  type: 'object',
  allOf: [
    {
      $ref: '#/components/schemas/Page',
    },
    {
      type: 'object',
      properties: {
        nodeRelations: {
          type: 'array',
          description: 'List of node relations',
          items: {
            $ref: '#/components/schemas/PageNodeRelation',
          },
        },
        pageConfig: {
          type: 'object',
          description: 'Page configuration',
        },
      },
    },
  ],
} as const;

export const UpdatePageRequestSchema = {
  type: 'object',
  properties: {
    title: {
      type: 'string',
      description: 'Page title',
    },
    description: {
      type: 'string',
      description: 'Page description',
    },
    nodeRelations: {
      type: 'array',
      description: 'List of node relations with updated order',
      items: {
        type: 'object',
        properties: {
          nodeId: {
            type: 'string',
            description: 'Node ID',
          },
          orderIndex: {
            type: 'integer',
            description: 'New order index',
          },
        },
      },
    },
  },
} as const;

export const UpdatePageResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          allOf: [
            {
              $ref: '#/components/schemas/Page',
            },
            {
              type: 'object',
              properties: {
                nodeRelations: {
                  type: 'array',
                  description: 'List of node relations',
                  items: {
                    $ref: '#/components/schemas/PageNodeRelation',
                  },
                },
              },
            },
          ],
        },
      },
    },
  ],
} as const;

export const DeletePageResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            pageId: {
              type: 'string',
              description: 'ID of the deleted page',
            },
            canvasId: {
              type: 'string',
              description: 'Canvas ID associated with the deleted page',
            },
          },
        },
      },
    },
  ],
} as const;

export const SharePageResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            pageId: {
              type: 'string',
              description: 'Page ID',
            },
            canvasId: {
              type: 'string',
              description: 'Canvas ID associated with the page',
            },
            shareId: {
              type: 'string',
              description: 'Share ID',
            },
            shareUrl: {
              type: 'string',
              description: 'Share URL',
            },
          },
        },
      },
    },
  ],
} as const;

export const DeletePageNodeResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            pageId: {
              type: 'string',
              description: 'Page ID',
            },
            canvasId: {
              type: 'string',
              description: 'Canvas ID associated with the page',
            },
            nodeId: {
              type: 'string',
              description: 'ID of the deleted node',
            },
          },
        },
      },
    },
  ],
} as const;

export const ListPagesResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            total: {
              type: 'integer',
              description: 'Total number of pages',
            },
            pages: {
              type: 'array',
              description: 'List of pages',
              items: {
                $ref: '#/components/schemas/Page',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const PageDetailResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/PageDetail',
        },
      },
    },
  ],
} as const;

export const AddPageNodesRequestSchema = {
  type: 'object',
  required: ['nodeIds'],
  properties: {
    nodeIds: {
      type: 'array',
      description: 'List of node IDs to add to the page',
      items: {
        type: 'string',
      },
    },
  },
} as const;

export const AddPageNodesResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            page: {
              $ref: '#/components/schemas/Page',
            },
            nodeRelations: {
              type: 'array',
              description: 'List of node relations',
              items: {
                $ref: '#/components/schemas/PageNodeRelation',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const CanvasPageResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            page: {
              $ref: '#/components/schemas/Page',
            },
            nodeRelations: {
              type: 'array',
              description: 'List of node relations',
              items: {
                $ref: '#/components/schemas/PageNodeRelation',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const UserSchema = {
  type: 'object',
  description: 'Refly user, used as JWT payload',
  required: ['uid'],
  properties: {
    uid: {
      type: 'string',
      description: 'UID',
    },
    email: {
      type: 'string',
      description: 'Email',
    },
  },
} as const;

export const AuthTypeSchema = {
  type: 'string',
  description: 'Auth type',
  enum: ['email', 'oauth'],
} as const;

export const AccountSchema = {
  type: 'object',
  description: 'Auth account',
  required: ['type', 'provider', 'providerAccountId'],
  properties: {
    type: {
      $ref: '#/components/schemas/AuthType',
      description: 'Auth type',
    },
    provider: {
      type: 'string',
      description: 'Provider',
    },
    scope: {
      type: 'array',
      description: 'Auth scope',
      items: {
        type: 'string',
      },
    },
    providerAccountId: {
      type: 'string',
      description: 'Provider account ID',
    },
  },
} as const;

export const ListAccountsResponseSchema = {
  type: 'object',
  description: 'List auth accounts response',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'List of auth accounts',
          items: {
            $ref: '#/components/schemas/Account',
          },
        },
      },
    },
  ],
} as const;

export const CheckToolOAuthStatusResponseSchema = {
  type: 'object',
  description: 'Check tool OAuth status response',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'OAuth status information',
          properties: {
            authorized: {
              type: 'boolean',
              description: 'Whether user has sufficient OAuth authorization',
            },
            provider: {
              type: 'string',
              description: 'OAuth provider',
            },
            scope: {
              type: 'array',
              description: 'Required OAuth scopes',
              items: {
                type: 'string',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const ShareUserSchema = {
  type: 'object',
  description: '共享实体中的 Refly 用户',
  required: ['uid'],
  properties: {
    uid: {
      type: 'string',
      description: '用户 UID',
    },
    name: {
      type: 'string',
      description: '用户名',
    },
    nickname: {
      type: 'string',
      description: '用户昵称',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '用户创建时间',
    },
    avatar: {
      type: 'string',
      description: '用户头像',
    },
  },
} as const;

export const ListOrderSchema = {
  type: 'string',
  description: '列表排序方式',
  enum: ['creationAsc', 'creationDesc', 'updationAsc', 'updationDesc'],
} as const;

export const CanvasStatusSchema = {
  type: 'string',
  description: '画布状态',
  enum: ['ready', 'duplicating', 'duplicate_failed'],
} as const;

export const CanvasSchema = {
  type: 'object',
  required: ['canvasId', 'title', 'createdAt', 'updatedAt'],
  properties: {
    canvasId: {
      type: 'string',
      description: '画布 ID',
      example: 'c-g30e1b80b5g1itbemc0g5jj3',
    },
    title: {
      type: 'string',
      description: '画布标题',
      example: 'My canvas',
    },
    readOnly: {
      type: 'boolean',
      description: '该画布是否只读',
    },
    status: {
      type: 'string',
      description: '画布状态',
      $ref: '#/components/schemas/CanvasStatus',
    },
    owner: {
      description: '画布所有者',
      $ref: '#/components/schemas/ShareUser',
    },
    shareRecord: {
      description: '画布分享记录',
      $ref: '#/components/schemas/ShareRecord',
    },
    workflowApp: {
      description: '画布工作流应用',
      $ref: '#/components/schemas/WorkflowApp',
    },
    usedToolsets: {
      type: 'array',
      description: '画布使用的工具集',
      items: {
        type: 'object',
        $ref: '#/components/schemas/GenericToolset',
      },
    },
    minimapUrl: {
      type: 'string',
      description: '缩略图 URL',
    },
    minimapStorageKey: {
      type: 'string',
      description: '缩略图存储键',
    },
    schedule: {
      description: '工作流定时配置',
      $ref: '#/components/schemas/WorkflowSchedule',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '画布创建时间',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: '画布更新时间',
    },
  },
} as const;

export const WorkflowScheduleSchema = {
  type: 'object',
  properties: {
    scheduleId: {
      type: 'string',
      description: '定时任务 ID',
    },
    uid: {
      type: 'string',
      description: '用户 ID',
    },
    canvasId: {
      type: 'string',
      description: '画布 ID',
    },
    name: {
      type: 'string',
      description: '定时任务名称',
    },
    isEnabled: {
      type: 'boolean',
      description: '定时任务是否启用',
    },
    cronExpression: {
      type: 'string',
      description: 'Cron 表达式',
    },
    scheduleConfig: {
      type: 'string',
      description: '定时配置 JSON（类型、时间、星期和月份日期）',
    },
    timezone: {
      type: 'string',
      description: '时区',
    },
    nextRunAt: {
      type: 'string',
      format: 'date-time',
      description: '下次运行时间',
      nullable: true,
    },
    lastRunAt: {
      type: 'string',
      format: 'date-time',
      description: '上次运行时间',
      nullable: true,
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '创建时间',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: '更新时间',
    },
    deletedAt: {
      type: 'string',
      format: 'date-time',
      description: '删除时间',
      nullable: true,
    },
  },
} as const;

export const CreateScheduleRequestSchema = {
  type: 'object',
  required: ['canvasId', 'name', 'cronExpression', 'scheduleConfig'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID to schedule',
    },
    name: {
      type: 'string',
      description: 'Schedule name',
    },
    cronExpression: {
      type: 'string',
      description: 'Cron expression for scheduling',
    },
    scheduleConfig: {
      type: 'string',
      description: 'Schedule configuration JSON',
    },
    timezone: {
      type: 'string',
      description: 'Timezone for schedule execution',
    },
    isEnabled: {
      type: 'boolean',
      description: 'Whether the schedule is enabled',
      default: true,
    },
  },
} as const;

export const CreateScheduleResponseSchema = {
  type: 'object',
  properties: {
    success: {
      type: 'boolean',
      description: 'Whether the operation was successful',
    },
    data: {
      $ref: '#/components/schemas/WorkflowSchedule',
    },
    message: {
      type: 'string',
      description: 'Response message',
    },
  },
} as const;

export const UpdateScheduleRequestSchema = {
  type: 'object',
  required: ['scheduleId'],
  properties: {
    scheduleId: {
      type: 'string',
      description: 'Schedule ID to update',
    },
    name: {
      type: 'string',
      description: 'Schedule name',
    },
    cronExpression: {
      type: 'string',
      description: 'Cron expression for scheduling',
    },
    scheduleConfig: {
      type: 'string',
      description: 'Schedule configuration JSON',
    },
    timezone: {
      type: 'string',
      description: 'Timezone for schedule execution',
    },
    isEnabled: {
      type: 'boolean',
      description: 'Whether the schedule is enabled',
    },
  },
} as const;

export const UpdateScheduleResponseSchema = {
  type: 'object',
  properties: {
    success: {
      type: 'boolean',
      description: 'Whether the operation was successful',
    },
    data: {
      $ref: '#/components/schemas/WorkflowSchedule',
    },
    message: {
      type: 'string',
      description: 'Response message',
    },
  },
} as const;

export const DeleteScheduleRequestSchema = {
  type: 'object',
  required: ['scheduleId'],
  properties: {
    scheduleId: {
      type: 'string',
      description: 'Schedule ID to delete',
    },
  },
} as const;

export const DeleteScheduleResponseSchema = {
  type: 'object',
  properties: {
    success: {
      type: 'boolean',
      description: 'Whether the operation was successful',
    },
    data: {
      $ref: '#/components/schemas/WorkflowSchedule',
    },
    message: {
      type: 'string',
      description: 'Response message',
    },
  },
} as const;

export const ListSchedulesRequestSchema = {
  type: 'object',
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID to filter schedules',
    },
    page: {
      type: 'integer',
      description: 'Page number for pagination',
      default: 1,
    },
    pageSize: {
      type: 'integer',
      description: 'Number of items per page',
      default: 10,
    },
  },
} as const;

export const ListSchedulesResponseSchema = {
  type: 'object',
  properties: {
    success: {
      type: 'boolean',
      description: 'Whether the operation was successful',
    },
    data: {
      type: 'object',
      properties: {
        total: {
          type: 'integer',
          description: 'Total number of schedules',
        },
        page: {
          type: 'integer',
          description: 'Current page number',
        },
        pageSize: {
          type: 'integer',
          description: 'Number of items per page',
        },
        items: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/WorkflowSchedule',
          },
          description: 'List of schedules',
        },
      },
    },
  },
} as const;

export const GetScheduleDetailRequestSchema = {
  type: 'object',
  required: ['scheduleId'],
  properties: {
    scheduleId: {
      type: 'string',
      description: 'Schedule ID to get details for',
    },
  },
} as const;

export const GetScheduleDetailResponseSchema = {
  type: 'object',
  properties: {
    success: {
      type: 'boolean',
      description: 'Whether the operation was successful',
    },
    data: {
      $ref: '#/components/schemas/WorkflowSchedule',
    },
    message: {
      type: 'string',
      description: 'Response message',
    },
  },
} as const;

export const WorkflowScheduleRecordSchema = {
  type: 'object',
  properties: {
    scheduleRecordId: {
      type: 'string',
      description: 'Schedule record ID',
    },
    scheduleId: {
      type: 'string',
      description: 'Schedule ID',
    },
    uid: {
      type: 'string',
      description: 'User ID',
    },
    sourceCanvasId: {
      type: 'string',
      description: 'Source canvas ID (template)',
    },
    canvasId: {
      type: 'string',
      description: 'Execution canvas ID',
    },
    workflowExecutionId: {
      type: 'string',
      description: 'Workflow execution ID',
    },
    workflowTitle: {
      type: 'string',
      description: 'Workflow title',
    },
    status: {
      type: 'string',
      enum: ['scheduled', 'pending', 'processing', 'running', 'success', 'failed', 'skipped'],
      description: 'Execution status',
    },
    scheduledAt: {
      type: 'string',
      format: 'date-time',
      description: 'Scheduled execution time',
    },
    triggeredAt: {
      type: 'string',
      format: 'date-time',
      description: 'Actual trigger time',
    },
    completedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Execution completion time',
      nullable: true,
    },
    priority: {
      type: 'integer',
      description: 'Execution priority',
    },
    creditUsed: {
      type: 'integer',
      description: 'Credit used for execution',
    },
    failureReason: {
      type: 'string',
      description: 'Failure reason if failed',
      nullable: true,
    },
    errorDetails: {
      type: 'string',
      description: 'Detailed error information',
      nullable: true,
    },
    usedTools: {
      type: 'string',
      description: 'JSON string of tools used',
      nullable: true,
    },
    snapshotStorageKey: {
      type: 'string',
      description: 'Canvas snapshot storage key',
      nullable: true,
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Record creation time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Record update time',
    },
  },
} as const;

export const GetScheduleRecordsRequestSchema = {
  type: 'object',
  required: ['scheduleId'],
  properties: {
    scheduleId: {
      type: 'string',
      description: 'Schedule ID',
    },
    page: {
      type: 'integer',
      description: 'Page number for pagination',
      default: 1,
    },
    pageSize: {
      type: 'integer',
      description: 'Number of items per page',
      default: 10,
    },
  },
} as const;

export const GetScheduleRecordsResponseSchema = {
  type: 'object',
  properties: {
    success: {
      type: 'boolean',
      description: 'Whether the operation was successful',
    },
    data: {
      type: 'object',
      properties: {
        total: {
          type: 'integer',
          description: 'Total number of records',
        },
        page: {
          type: 'integer',
          description: 'Current page number',
        },
        pageSize: {
          type: 'integer',
          description: 'Number of items per page',
        },
        items: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/WorkflowScheduleRecord',
          },
          description: 'List of schedule records',
        },
      },
    },
  },
} as const;

export const ListAllScheduleRecordsRequestSchema = {
  type: 'object',
  properties: {
    page: {
      type: 'integer',
      description: 'Page number for pagination',
      default: 1,
    },
    pageSize: {
      type: 'integer',
      description: 'Number of items per page',
      default: 10,
    },
    executionStatus: {
      type: 'string',
      enum: ['scheduled', 'pending', 'processing', 'running', 'success', 'failed'],
      description: 'Filter by execution status',
    },
    triggerType: {
      type: 'string',
      enum: ['schedule', 'api'],
      description: 'Filter by trigger source',
    },
    keyword: {
      type: 'string',
      description: 'Search keyword for workflow title',
    },
    tools: {
      type: 'array',
      items: {
        type: 'string',
      },
      description: 'Filter by tools used',
    },
    canvasId: {
      type: 'string',
      description: 'Filter by canvas ID',
    },
  },
} as const;

export const ListAllScheduleRecordsResponseSchema = {
  type: 'object',
  properties: {
    success: {
      type: 'boolean',
      description: 'Whether the operation was successful',
    },
    data: {
      type: 'object',
      properties: {
        total: {
          type: 'integer',
          description: 'Total number of records',
        },
        page: {
          type: 'integer',
          description: 'Current page number',
        },
        pageSize: {
          type: 'integer',
          description: 'Number of items per page',
        },
        items: {
          type: 'array',
          items: {
            allOf: [
              {
                $ref: '#/components/schemas/WorkflowScheduleRecord',
              },
              {
                type: 'object',
                properties: {
                  scheduleName: {
                    type: 'string',
                    description: 'Schedule name',
                  },
                },
              },
            ],
          },
          description: 'List of schedule records with schedule names',
        },
      },
    },
  },
} as const;

export const GetAvailableToolsResponseSchema = {
  type: 'object',
  properties: {
    success: {
      type: 'boolean',
      description: 'Whether the operation was successful',
    },
    data: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: {
            type: 'string',
            description: 'Tool ID',
          },
          name: {
            type: 'string',
            description: 'Tool name',
          },
        },
      },
      description: 'List of available tools',
    },
  },
} as const;

export const GetScheduleRecordDetailRequestSchema = {
  type: 'object',
  required: ['scheduleRecordId'],
  properties: {
    scheduleRecordId: {
      type: 'string',
      description: 'Schedule record ID',
    },
  },
} as const;

export const GetScheduleRecordDetailResponseSchema = {
  type: 'object',
  properties: {
    success: {
      type: 'boolean',
      description: 'Whether the operation was successful',
    },
    data: {
      allOf: [
        {
          $ref: '#/components/schemas/WorkflowScheduleRecord',
        },
        {
          type: 'object',
          properties: {
            scheduleName: {
              type: 'string',
              description: 'Schedule name',
            },
          },
        },
      ],
    },
  },
} as const;

export const GetRecordSnapshotRequestSchema = {
  type: 'object',
  required: ['scheduleRecordId'],
  properties: {
    scheduleRecordId: {
      type: 'string',
      description: 'Schedule record ID',
    },
  },
} as const;

export const GetRecordSnapshotResponseSchema = {
  type: 'object',
  properties: {
    success: {
      type: 'boolean',
      description: 'Whether the operation was successful',
    },
    data: {
      type: 'object',
      description: 'Canvas snapshot data (JSON object)',
    },
  },
} as const;

export const TriggerScheduleManuallyRequestSchema = {
  type: 'object',
  required: ['scheduleId'],
  properties: {
    scheduleId: {
      type: 'string',
      description: 'Schedule ID to trigger',
    },
  },
} as const;

export const TriggerScheduleManuallyResponseSchema = {
  type: 'object',
  properties: {
    success: {
      type: 'boolean',
      description: 'Whether the operation was successful',
    },
    data: {
      type: 'object',
      properties: {
        scheduleId: {
          type: 'string',
          description: 'Schedule ID',
        },
        scheduleRecordId: {
          type: 'string',
          description: 'Created schedule record ID',
        },
        triggeredAt: {
          type: 'string',
          format: 'date-time',
          description: 'Trigger time',
        },
        priority: {
          type: 'integer',
          description: 'Execution priority',
        },
      },
    },
  },
} as const;

export const RetryScheduleRecordRequestSchema = {
  type: 'object',
  required: ['scheduleRecordId'],
  properties: {
    scheduleRecordId: {
      type: 'string',
      description: 'Schedule record ID to retry',
    },
  },
} as const;

export const RetryScheduleRecordResponseSchema = {
  type: 'object',
  properties: {
    success: {
      type: 'boolean',
      description: 'Whether the operation was successful',
    },
    data: {
      type: 'object',
      properties: {
        scheduleRecordId: {
          type: 'string',
          description: 'Schedule record ID',
        },
        scheduleId: {
          type: 'string',
          description: 'Schedule ID',
        },
        status: {
          type: 'string',
          description: 'New status (pending)',
        },
        priority: {
          type: 'integer',
          description: 'Execution priority',
        },
      },
    },
  },
} as const;

export const CanvasTemplateCategorySchema = {
  type: 'object',
  required: ['categoryId', 'name', 'labelDict', 'descriptionDict'],
  properties: {
    categoryId: {
      type: 'string',
      description: '分类 ID',
    },
    name: {
      type: 'string',
      description: '分类名称',
    },
    labelDict: {
      type: 'object',
      description: '分类多语言名称字典',
      additionalProperties: {
        type: 'string',
      },
    },
    descriptionDict: {
      type: 'object',
      description: '分类多语言描述字典',
      additionalProperties: {
        type: 'string',
      },
    },
  },
} as const;

export const CanvasTemplateSchema = {
  type: 'object',
  required: ['templateId', 'shareId', 'title', 'description', 'language', 'createdAt', 'updatedAt'],
  properties: {
    templateId: {
      type: 'string',
      description: '画布模板 ID',
      example: 'ct-g30e1b80b5g1itbemc0g5jj3',
    },
    shareId: {
      type: 'string',
      description: '分享 ID',
      example: 'can-g30e1b80b5g1itbemc0g5jj3',
    },
    shareUser: {
      description: '分享用户',
      $ref: '#/components/schemas/ShareUser',
    },
    version: {
      type: 'integer',
      description: '画布模板版本',
    },
    category: {
      description: '画布模板分类',
      $ref: '#/components/schemas/CanvasTemplateCategory',
    },
    title: {
      type: 'string',
      description: '画布模板标题',
    },
    description: {
      type: 'string',
      description: '画布模板描述',
    },
    language: {
      type: 'string',
      description: '画布模板语言代码',
    },
    featured: {
      type: 'boolean',
      description: '该画布模板是否为精选',
    },
    appId: {
      type: 'string',
      description: '工作流应用 ID',
    },
    coverUrl: {
      type: 'string',
      description: '画布模板封面 URL',
    },
    appShareId: {
      type: 'string',
      description: '关联工作流应用分享 ID',
    },
    creditUsage: {
      type: 'integer',
      description: '运行该工作流应用所需积分',
      nullable: true,
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '画布模板创建时间',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: '画布模板更新时间',
    },
  },
} as const;

export const ResourceMetaSchema = {
  type: 'object',
  description: 'Resource metadata',
  properties: {
    url: {
      type: 'string',
      description: 'Weblink URL',
      example: 'https://www.google.com',
    },
    title: {
      type: 'string',
      description: 'Weblink title',
      example: 'Google',
    },
    contentType: {
      type: 'string',
      description: 'File content type (MIME type)',
      example: 'application/pdf',
    },
  },
} as const;

export const ResourceTypeSchema = {
  type: 'string',
  description: 'Resource type',
  enum: ['weblink', 'text', 'file', 'document', 'image', 'video', 'audio', 'text/plain'],
} as const;

export const IndexErrorSchema = {
  type: 'object',
  description: 'Error message for resource indexing',
  properties: {
    type: {
      type: 'string',
      description: 'Error type',
      enum: ['pageLimitExceeded', 'unknownError'],
    },
    metadata: {
      type: 'object',
      description: 'Error metadata',
    },
  },
} as const;

export const ResourceSchema = {
  type: 'object',
  required: ['resourceId', 'resourceType', 'title'],
  properties: {
    resourceId: {
      type: 'string',
      description: "Resource ID (empty if it's external)",
      example: 'r-g30e1b80b5g1itbemc0g5jj3',
    },
    resourceType: {
      description: 'Resource type',
      $ref: '#/components/schemas/ResourceType',
    },
    title: {
      type: 'string',
      description: 'Resource title',
    },
    data: {
      type: 'object',
      description: 'Resource metadata',
      $ref: '#/components/schemas/ResourceMeta',
    },
    indexStatus: {
      description: 'Resource index status',
      $ref: '#/components/schemas/IndexStatus',
    },
    indexError: {
      description: 'Error message for resource indexing',
      $ref: '#/components/schemas/IndexError',
    },
    storageKey: {
      type: 'string',
      description: 'Resource storage key',
    },
    storageSize: {
      type: 'string',
      description: 'Resource storage size (in bytes)',
    },
    vectorSize: {
      type: 'string',
      description: 'Resource vector storage size (in bytes)',
    },
    rawFileKey: {
      type: 'string',
      description: 'Raw file storage key (used to download the file)',
    },
    canvasId: {
      type: 'string',
      description: 'Canvas ID to bind with',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Resource creation time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Resource update time',
    },
    contentPreview: {
      type: 'string',
      description: 'Preview content for this resource',
    },
    shareId: {
      type: 'string',
      description: 'Share ID',
    },
    content: {
      type: 'string',
      description: 'Document content for this resource (only returned in getResourceDetail API)',
    },
    downloadURL: {
      type: 'string',
      description: 'Download URL for this resource (for file type only)',
    },
    publicURL: {
      type: 'string',
      description: 'Publicly accessible URL for this resource (file type only)',
    },
  },
} as const;

export const DocumentSchema = {
  type: 'object',
  required: ['docId', 'title'],
  properties: {
    docId: {
      type: 'string',
      description: 'Document ID',
      example: 'd-g30e1b80b5g1itbemc0g5jj3',
    },
    title: {
      type: 'string',
      description: 'Document title',
      example: 'My document',
    },
    contentPreview: {
      type: 'string',
      description: 'Document content preview',
    },
    content: {
      type: 'string',
      description: 'Full document content (only returned in detail api)',
    },
    readOnly: {
      type: 'boolean',
      description: 'Whether this document is read-only',
    },
    canvasId: {
      type: 'string',
      description: 'Canvas ID to bind with',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Document creation time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Document update time',
    },
  },
} as const;

export const EntityTypeSchema = {
  type: 'string',
  description: '实体类型',
  enum: [
    'document',
    'resource',
    'canvas',
    'share',
    'user',
    'project',
    'skillResponse',
    'codeArtifact',
    'page',
    'mediaResult',
    'workflowApp',
    'driveFile',
  ],
} as const;

export const EntitySchema = {
  type: 'object',
  description: 'Entity',
  properties: {
    entityId: {
      type: 'string',
      description: 'Entity ID',
    },
    entityType: {
      description: 'Entity type',
      $ref: '#/components/schemas/EntityType',
    },
  },
} as const;

export const ProjectSourceSchema = {
  type: 'object',
  description: 'Project source',
  properties: {
    entityId: {
      type: 'string',
      description: 'Entity ID',
    },
    entityType: {
      description: 'Entity type',
      $ref: '#/components/schemas/EntityType',
    },
    title: {
      type: 'string',
      description: 'Project title',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Project creation time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Project update time',
    },
  },
} as const;

export const ProjectSchema = {
  type: 'object',
  description: 'Project',
  required: ['projectId', 'name'],
  properties: {
    projectId: {
      type: 'string',
      description: 'Project ID',
    },
    name: {
      type: 'string',
      description: 'Project name',
    },
    description: {
      type: 'string',
      description: 'Project description',
    },
    coverUrl: {
      type: 'string',
      description: 'Project cover URL',
    },
    customInstructions: {
      type: 'string',
      description: 'Custom instructions for the project',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Project creation time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Project update time',
    },
  },
} as const;

export const LabelClassSchema = {
  type: 'object',
  description: 'Label class',
  required: ['labelClassId', 'name', 'displayName', 'createdAt', 'updatedAt'],
  properties: {
    labelClassId: {
      type: 'string',
      description: 'Label class ID',
      example: 'lc-g30e1b80b5g1itbemc0g5jj3',
    },
    name: {
      type: 'string',
      description: 'Label class name',
      example: 'Related Dataset',
    },
    displayName: {
      type: 'string',
      description: 'Label class display name',
      example: 'Label display name',
    },
    icon: {
      description: 'Label icon',
      $ref: '#/components/schemas/Icon',
    },
    prompt: {
      type: 'string',
      description: 'Label creation instruction prompt',
      example: 'Extract labels for the tech-related keywords',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Label class creation time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Label class update time',
    },
  },
} as const;

export const LabelInstanceSchema = {
  type: 'object',
  description: 'Label instances related to entities',
  required: ['labelId', 'labelClassId', 'value'],
  properties: {
    labelId: {
      type: 'string',
      description: 'Label instance ID',
      example: 'lb-g30e1b80b5g1itbemc0g5jj3',
    },
    labelClassId: {
      type: 'string',
      description: 'Label class ID',
      example: 'lc-g30e1b80b5g1itbemc0g5jj3',
    },
    labelClass: {
      description: 'Label class',
      $ref: '#/components/schemas/LabelClass',
    },
    value: {
      type: 'string',
      description: 'Label value',
      example: 'HotPotQA',
    },
  },
} as const;

export const InputModeSchema = {
  type: 'string',
  description: '数据输入模式',
  enum: ['text', 'textarea', 'number', 'select', 'multiSelect', 'radio', 'switch'],
} as const;

export const ConfigScopeSchema = {
  type: 'array',
  description: 'Config scope',
  items: {
    type: 'string',
    enum: ['runtime', 'template'],
  },
} as const;

export const SelectOptionSchema = {
  type: 'object',
  description: '选择项',
  required: ['value'],
  properties: {
    value: {
      type: 'string',
      description: '选项值',
    },
    labelDict: {
      type: 'object',
      description: '选项名称（键为语言，值为名称）',
      additionalProperties: {
        type: 'string',
      },
    },
    disabled: {
      type: 'boolean',
      description: '该选项是否禁用',
      default: false,
    },
  },
} as const;

export const DynamicConfigItemSchema = {
  type: 'object',
  description: '动态配置项',
  required: ['key', 'inputMode', 'labelDict', 'descriptionDict'],
  properties: {
    key: {
      type: 'string',
      description: '配置键',
    },
    inputMode: {
      description: '配置输入模式',
      $ref: '#/components/schemas/InputMode',
    },
    required: {
      type: 'boolean',
      description: '指定该配置是否必填',
      default: false,
    },
    labelDict: {
      type: 'object',
      description: '配置名称（键为语言，值为名称）',
      additionalProperties: {
        type: 'string',
      },
    },
    descriptionDict: {
      type: 'object',
      description: '配置描述（键为语言，值为描述）',
      additionalProperties: {
        type: 'string',
      },
    },
    defaultValue: {
      description: '默认值',
      oneOf: [
        {
          type: 'number',
        },
        {
          type: 'string',
        },
        {
          type: 'boolean',
        },
        {
          type: 'array',
          items: {
            type: 'string',
          },
        },
      ],
    },
    options: {
      type: 'array',
      description: '配置选项',
      items: {
        $ref: '#/components/schemas/SelectOption',
      },
    },
    inputProps: {
      type: 'object',
      description: '其他输入属性',
      properties: {
        min: {
          type: 'number',
          description: '数字输入最小值',
        },
        max: {
          type: 'number',
          description: '数字输入最大值',
        },
        step: {
          type: 'number',
          description: '数字输入步长',
        },
        precision: {
          type: 'number',
          description: '小数精度',
        },
        passwordType: {
          type: 'boolean',
          description: '是否以密码输入框显示',
        },
      },
    },
  },
} as const;

export const DynamicConfigValueSchema = {
  type: 'object',
  description: 'Dynamic config value',
  required: ['label', 'value', 'displayValue'],
  properties: {
    label: {
      type: 'string',
      description: 'Config key label',
    },
    value: {
      oneOf: [
        {
          type: 'string',
        },
        {
          type: 'number',
        },
        {
          type: 'boolean',
        },
        {
          type: 'array',
          items: {
            type: 'string',
          },
        },
        {
          type: 'object',
          additionalProperties: true,
        },
      ],
      description: 'Config value',
    },
    displayValue: {
      type: 'string',
      description: 'Config display value',
    },
    configScope: {
      description: 'The contexts in which the requirement applies',
      $ref: '#/components/schemas/ConfigScope',
    },
  },
} as const;

export const SkillTemplateConfigDefinitionSchema = {
  type: 'object',
  description: 'Skill template config schema',
  required: ['items'],
  properties: {
    items: {
      type: 'array',
      description: 'Config items',
      items: {
        $ref: '#/components/schemas/DynamicConfigItem',
      },
    },
  },
} as const;

export const IconTypeSchema = {
  type: 'string',
  description: 'Icon type',
  enum: ['emoji', 'image'],
} as const;

export const IconSchema = {
  type: 'object',
  description: 'Icon',
  required: ['type', 'value'],
  properties: {
    type: {
      description: 'Icon type',
      $ref: '#/components/schemas/IconType',
    },
    value: {
      type: 'string',
      description: 'Icon value',
    },
  },
} as const;

export const SkillSchema = {
  type: 'object',
  description: 'Skill',
  required: ['name'],
  properties: {
    name: {
      type: 'string',
      description: 'Skill name',
    },
    description: {
      type: 'string',
      description: 'Skill description',
    },
    icon: {
      description: 'Skill icon',
      $ref: '#/components/schemas/Icon',
    },
    configSchema: {
      description: 'Skill config schema',
      $ref: '#/components/schemas/SkillTemplateConfigDefinition',
    },
    tplConfig: {
      description: 'Skill template config',
      $ref: '#/components/schemas/SkillTemplateConfig',
    },
  },
} as const;

export const SkillTriggerTypeSchema = {
  type: 'string',
  description: 'Skill trigger type',
  enum: ['timer', 'simpleEvent'],
} as const;

export const SimpleEventNameSchema = {
  type: 'string',
  description: 'Simple event name',
  enum: ['onResourceReady'],
} as const;

export const SimpleEventSchema = {
  type: 'object',
  required: ['name', 'displayName'],
  properties: {
    name: {
      description: 'Simple event name',
      $ref: '#/components/schemas/SimpleEventName',
    },
    displayName: {
      type: 'object',
      description: 'Simple event display name (key is locale, value is display name)',
    },
  },
} as const;

export const TimerIntervalSchema = {
  type: 'string',
  description: 'Timer interval',
  enum: ['hour', 'day', 'week', 'month', 'year'],
} as const;

export const TimerTriggerConfigSchema = {
  type: 'object',
  required: ['datetime'],
  properties: {
    datetime: {
      type: 'string',
      format: 'date-time',
      description: 'Time to run',
    },
    repeatInterval: {
      type: 'string',
      description: 'Repeat interval',
      $ref: '#/components/schemas/TimerInterval',
    },
  },
} as const;

export const SkillTriggerSchema = {
  type: 'object',
  description: 'Skill triggers',
  required: [
    'skillId',
    'displayName',
    'triggerId',
    'triggerType',
    'enabled',
    'createdAt',
    'updatedAt',
  ],
  properties: {
    skillId: {
      type: 'string',
      description: 'Skill ID',
      example: 'sk-g30e1b80b5g1itbemc0g5jj3',
    },
    displayName: {
      type: 'string',
      description: 'Trigger display name',
      example: 'My trigger',
    },
    triggerId: {
      type: 'string',
      description: 'Trigger ID',
      example: 'tr-g30e1b80b5g1itbemc0g5jj3',
    },
    triggerType: {
      description: 'Trigger type',
      $ref: '#/components/schemas/SkillTriggerType',
    },
    simpleEventName: {
      description: 'Simple event name (only required when trigger type is `simpleEvent`)',
      $ref: '#/components/schemas/SimpleEventName',
    },
    timerConfig: {
      description: 'Timer config (only required when trigger type is `timer`)',
      $ref: '#/components/schemas/TimerTriggerConfig',
    },
    input: {
      description: 'Skill input',
      $ref: '#/components/schemas/SkillInput',
    },
    context: {
      description: 'Skill context',
      $ref: '#/components/schemas/SkillContext',
    },
    tplConfig: {
      description: 'Skill template config',
      $ref: '#/components/schemas/SkillTemplateConfig',
    },
    enabled: {
      type: 'boolean',
      description: 'Trigger enabled',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Trigger creation time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Trigger update time',
    },
  },
} as const;

export const SkillMetaSchema = {
  type: 'object',
  description: 'Skill metadata',
  required: ['name'],
  properties: {
    name: {
      type: 'string',
      description: 'Skill name',
    },
    icon: {
      description: 'Skill icon',
      $ref: '#/components/schemas/Icon',
    },
  },
} as const;

export const ActionMetaSchema = {
  type: 'object',
  description: 'Action metadata',
  required: ['name'],
  properties: {
    type: {
      type: 'string',
      description: 'Action type',
      $ref: '#/components/schemas/ActionType',
    },
    name: {
      type: 'string',
      description: 'Action name',
    },
    icon: {
      description: 'Action icon',
      $ref: '#/components/schemas/Icon',
    },
  },
} as const;

export const SkillRuntimeConfigSchema = {
  type: 'object',
  description: 'Skill runtime config',
  properties: {
    disableLinkParsing: {
      type: 'boolean',
      description: 'Whether to disable link parsing for input query',
    },
    enabledKnowledgeBase: {
      type: 'boolean',
      description: 'Whether to enable knowledge base for input query',
    },
  },
} as const;

export const SkillTemplateConfigSchema = {
  type: 'object',
  description: 'Skill template config (key is config item key, value is config value)',
  additionalProperties: {
    description: 'Skill template config value',
    $ref: '#/components/schemas/DynamicConfigValue',
  },
} as const;

export const ActionConfigSchema = {
  type: 'object',
  description: 'Action config (key is config item key, value is config value)',
  additionalProperties: {
    description: 'Skill template config value',
    $ref: '#/components/schemas/DynamicConfigValue',
  },
} as const;

export const SkillInstanceSchema = {
  type: 'object',
  description: 'Skill',
  allOf: [
    {
      $ref: '#/components/schemas/SkillMeta',
    },
    {
      type: 'object',
      required: ['createdAt', 'updatedAt', 'invocationConfig'],
      properties: {
        description: {
          type: 'string',
          description: 'Skill instance description',
        },
        promptHint: {
          type: 'string',
          description: 'Skill instance prompt hint',
        },
        tplConfig: {
          description: 'Skill template config',
          $ref: '#/components/schemas/SkillTemplateConfig',
        },
        tplConfigSchema: {
          description: 'Skill template config schema',
          $ref: '#/components/schemas/SkillTemplateConfigDefinition',
        },
        pinnedAt: {
          type: 'string',
          format: 'date-time',
          description: 'Skill pinned time',
        },
        createdAt: {
          type: 'string',
          format: 'date-time',
          description: 'Skill creation time',
        },
        updatedAt: {
          type: 'string',
          format: 'date-time',
          description: 'Skill update time',
        },
      },
    },
  ],
} as const;

export const SourceMetaSchema = {
  type: 'object',
  description: 'Source metadata',
  properties: {
    source: {
      type: 'string',
      description: 'Source URL',
      deprecated: true,
    },
    title: {
      type: 'string',
      description: 'Source title',
      deprecated: true,
    },
    publishedTime: {
      type: 'string',
      format: 'date-time',
      description: 'Source publish timesss',
    },
    entityId: {
      type: 'string',
      description: 'Related entity ID',
    },
    entityType: {
      type: 'string',
      description: 'Related entity type',
    },
    originalLocale: {
      type: 'string',
      description: 'Original locale',
    },
    translatedDisplayLocale: {
      type: 'string',
      description: 'Translated display locale',
    },
    isTranslated: {
      type: 'boolean',
      description: 'Whether the source is translated',
    },
    originalQuery: {
      type: 'string',
      description: 'Original query',
    },
    translatedQuery: {
      type: 'string',
      description: 'Translated query',
    },
    sourceType: {
      type: 'string',
      description: 'Source type',
      enum: ['webSearch', 'library'],
    },
  },
} as const;

export const SourceSelectionSchema = {
  type: 'object',
  description: 'Source selection',
  required: ['content', 'type'],
  properties: {
    xPath: {
      type: 'string',
      description: 'Selected xPath',
      deprecated: true,
    },
    content: {
      type: 'string',
      description: 'Selected content',
    },
    type: {
      type: 'string',
      description: 'Selection type',
      enum: ['text', 'table', 'link', 'image', 'video', 'audio'],
    },
  },
} as const;

export const SourceSchema = {
  type: 'object',
  description: 'Source of the message',
  required: ['pageContent'],
  properties: {
    url: {
      type: 'string',
      description: 'Source URL',
    },
    title: {
      type: 'string',
      description: 'Source title',
    },
    pageContent: {
      type: 'string',
      description: 'Source content',
    },
    score: {
      type: 'number',
      description: 'Relativity score',
    },
    metadata: {
      type: 'object',
      description: 'Source metadata',
      $ref: '#/components/schemas/SourceMeta',
    },
    selections: {
      type: 'array',
      description: 'Source selections',
      items: {
        $ref: '#/components/schemas/SourceSelection',
      },
    },
  },
} as const;

export const SearchStepSchema = {
  type: 'object',
  description: 'Search step',
  properties: {
    step: {
      type: 'string',
      description: 'Search step name',
    },
    duration: {
      type: 'number',
      description: 'Step duration in milliseconds',
    },
    result: {
      type: 'object',
      description: 'Step-specific result data',
    },
  },
} as const;

export const ModelTierSchema = {
  type: 'string',
  description: 'Model tier',
  enum: ['t1', 't2', 'free'],
} as const;

export const TokenUsageItemSchema = {
  type: 'object',
  description: 'Token usage item',
  required: ['modelName', 'modelProvider', 'inputTokens', 'outputTokens'],
  properties: {
    modelName: {
      type: 'string',
      description: 'Model name',
    },
    modelProvider: {
      type: 'string',
      description: 'Model provider',
    },
    modelLabel: {
      type: 'string',
      description: 'Model label',
    },
    inputTokens: {
      type: 'number',
      description: 'Input tokens',
    },
    outputTokens: {
      type: 'number',
      description: 'Output tokens',
    },
    cacheReadTokens: {
      type: 'number',
      description: 'Cache read tokens',
    },
    cacheWriteTokens: {
      type: 'number',
      description: 'Cache write tokens',
    },
    providerItemId: {
      type: 'string',
      description: 'Provider item ID',
    },
    tier: {
      type: 'string',
      description: 'Model tier',
      deprecated: true,
    },
    originalModelId: {
      type: 'string',
      description: "Original model ID before routing (e.g., 'auto')",
    },
    modelRoutedData: {
      type: 'object',
      description: 'Complete model routing metadata (JSON)',
      properties: {
        isRouted: {
          type: 'boolean',
          description: 'Whether this request was routed',
        },
        originalItemId: {
          type: 'string',
          description: 'Original provider item ID before routing',
        },
        originalModelId: {
          type: 'string',
          description: "Original model ID (e.g., 'auto')",
        },
        originalProvider: {
          type: 'string',
          description: 'Original model provider name',
        },
        originalModelName: {
          type: 'string',
          description: 'Original model name/label for display',
        },
        routedAt: {
          type: 'string',
          format: 'date-time',
          description: 'Routing timestamp',
        },
        routingStrategy: {
          type: 'string',
          description: "Routing strategy used (e.g., 'auto', 'load_balance', 'region')",
        },
      },
    },
  },
} as const;

export const ActionStatusSchema = {
  type: 'string',
  description: 'Action status',
  enum: ['init', 'waiting', 'executing', 'waiting_user', 'waiting_external', 'finish', 'failed'],
} as const;

export const ActionErrorTypeSchema = {
  type: 'string',
  description: 'Action error type',
  enum: ['systemError', 'userAbort'],
} as const;

export const ArtifactTypeSchema = {
  type: 'string',
  description: 'Artifact type',
  enum: ['document', 'codeArtifact', 'image', 'video', 'audio'],
} as const;

export const ArtifactStatusSchema = {
  type: 'string',
  description: 'Artifact status',
  enum: ['waiting', 'generating', 'finish', 'failed'],
} as const;

export const ArtifactSchema = {
  type: 'object',
  description: 'Artifact',
  required: ['type', 'entityId', 'title'],
  properties: {
    type: {
      type: 'string',
      description: 'Artifact type',
      $ref: '#/components/schemas/ArtifactType',
    },
    entityId: {
      type: 'string',
      description: 'Entity ID',
    },
    title: {
      type: 'string',
      description: 'Artifact title',
    },
    status: {
      description: 'Artifact status',
      $ref: '#/components/schemas/ArtifactStatus',
    },
    content: {
      type: 'string',
      description: 'Artifact content',
    },
    metadata: {
      type: 'object',
      description: 'Artifact metadata',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Artifact creation time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Artifact update time',
    },
  },
} as const;

export const ActionStepMetaSchema = {
  type: 'object',
  description: 'Action step metadata',
  required: ['name'],
  properties: {
    name: {
      type: 'string',
      description: 'Step name',
    },
  },
} as const;

export const ActionLogSchema = {
  type: 'object',
  description: 'Action log',
  required: ['key'],
  properties: {
    key: {
      type: 'string',
      description: 'Log key',
    },
    titleArgs: {
      type: 'object',
      description: 'Log title arguments',
    },
    descriptionArgs: {
      type: 'object',
      description: 'Log description arguments',
    },
  },
} as const;

export const ActionStepSchema = {
  type: 'object',
  description: 'Action step',
  required: ['name', 'status'],
  properties: {
    name: {
      type: 'string',
      description: 'Step name',
    },
    content: {
      type: 'string',
      description: 'Step content',
    },
    reasoningContent: {
      type: 'string',
      description: 'Step reasoning content',
    },
    artifacts: {
      type: 'array',
      description: 'Step artifacts',
      items: {
        $ref: '#/components/schemas/Artifact',
      },
    },
    structuredData: {
      type: 'object',
      description: 'Step structured data output',
      example: {
        sources: ['Source'],
        relatedQuestions: ['string'],
      },
    },
    logs: {
      type: 'array',
      description: 'Action step logs',
      items: {
        $ref: '#/components/schemas/ActionLog',
      },
    },
    tokenUsage: {
      type: 'array',
      description: 'Token usage',
      items: {
        $ref: '#/components/schemas/TokenUsageItem',
      },
    },
    toolCalls: {
      type: 'array',
      description: 'Tool calls in this step',
      items: {
        $ref: '#/components/schemas/ToolCallResult',
      },
    },
  },
} as const;

export const CodeArtifactTypeSchema = {
  type: 'string',
  description: 'Code artifact type',
  enum: [
    'application/refly.artifacts.react',
    'image/svg+xml',
    'application/refly.artifacts.mermaid',
    'text/markdown',
    'application/refly.artifacts.code',
    'text/html',
    'application/refly.artifacts.mindmap',
    'text/csv',
    'application/json',
    'text/plain',
  ],
} as const;

export const CodeArtifactSchema = {
  type: 'object',
  description: 'Code artifact',
  required: ['type', 'artifactId', 'title'],
  properties: {
    type: {
      type: 'string',
      description: 'Artifact type',
      $ref: '#/components/schemas/CodeArtifactType',
    },
    artifactId: {
      type: 'string',
      description: 'Artifact ID',
    },
    title: {
      type: 'string',
      description: 'Artifact title',
    },
    content: {
      type: 'string',
      description: 'Code artifact content',
    },
    language: {
      type: 'string',
      description: 'Code artifact language',
    },
    resultId: {
      type: 'string',
      description: 'Action result ID',
    },
    resultVersion: {
      type: 'number',
      description: 'Action result version',
    },
    canvasId: {
      type: 'string',
      description: 'Canvas ID to bind with',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
    },
  },
} as const;

export const ActionMessageTypeSchema = {
  type: 'string',
  description: 'Action message type',
  enum: ['ai', 'tool'],
} as const;

export const ActionMessageSchema = {
  type: 'object',
  description: 'Action message',
  required: ['messageId', 'type'],
  properties: {
    messageId: {
      type: 'string',
      description: 'Action message ID',
    },
    type: {
      $ref: '#/components/schemas/ActionMessageType',
      description: 'Action message type',
    },
    content: {
      type: 'string',
      description: 'Action message content',
    },
    reasoningContent: {
      type: 'string',
      description: 'Action message reasoning content',
    },
    toolCallMeta: {
      $ref: '#/components/schemas/ToolCallMeta',
      description: 'Action message tool call metadata',
    },
    toolCallId: {
      type: 'string',
      description: 'Action message tool call ID',
    },
    toolCallResult: {
      $ref: '#/components/schemas/ToolCallResult',
      description: 'Tool call result',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Action message creation time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Action message update time',
    },
  },
} as const;

export const ActionResultSchema = {
  type: 'object',
  description: 'Action result',
  required: ['resultId'],
  properties: {
    resultId: {
      type: 'string',
      readOnly: true,
      description: 'Action result ID',
      example: 'ar-g30e1b80b5g1itbemc0g5jj3',
    },
    version: {
      type: 'number',
      description: 'Action result version',
    },
    title: {
      type: 'string',
      description: 'Action result title',
      deprecated: true,
    },
    input: {
      description: 'Action input',
      $ref: '#/components/schemas/SkillInput',
    },
    tier: {
      description: 'Model tier',
      $ref: '#/components/schemas/ModelTier',
    },
    status: {
      type: 'string',
      description: 'Step status',
      $ref: '#/components/schemas/ActionStatus',
    },
    errorType: {
      type: 'string',
      description: 'Error type (defaults to systemError when omitted)',
      $ref: '#/components/schemas/ActionErrorType',
    },
    type: {
      description: 'Action type',
      $ref: '#/components/schemas/ActionType',
    },
    modelInfo: {
      description: 'Selected model',
      $ref: '#/components/schemas/ModelInfo',
    },
    actualProviderItemId: {
      type: 'string',
      description:
        'Actual provider item ID used for execution after routing (e.g. routed from Auto)',
    },
    isAutoModelRouted: {
      type: 'boolean',
      description: 'Whether the action execution was routed from the Auto model',
    },
    targetType: {
      description: 'Action target type',
      $ref: '#/components/schemas/EntityType',
    },
    targetId: {
      type: 'string',
      description: 'Action target ID',
    },
    actionMeta: {
      type: 'object',
      description: 'Action metadata',
      $ref: '#/components/schemas/ActionMeta',
    },
    context: {
      type: 'object',
      description: 'Action context',
      $ref: '#/components/schemas/SkillContext',
    },
    tplConfig: {
      type: 'object',
      description: 'Action template config',
      $ref: '#/components/schemas/SkillTemplateConfig',
      deprecated: true,
    },
    runtimeConfig: {
      type: 'object',
      description: 'Action runtime config',
      $ref: '#/components/schemas/SkillRuntimeConfig',
      deprecated: true,
    },
    history: {
      type: 'array',
      description: 'Action result history',
      items: {
        $ref: '#/components/schemas/ActionResult',
      },
    },
    steps: {
      type: 'array',
      description: 'Action steps',
      deprecated: true,
      items: {
        $ref: '#/components/schemas/ActionStep',
      },
    },
    messages: {
      type: 'array',
      description: 'Action messages',
      items: {
        $ref: '#/components/schemas/ActionMessage',
      },
    },
    errors: {
      type: 'array',
      description: 'Errors',
      items: {
        type: 'string',
      },
    },
    toolsets: {
      type: 'array',
      description: 'Action toolsets',
      items: {
        $ref: '#/components/schemas/GenericToolset',
      },
    },
    toolCalls: {
      type: 'array',
      description: 'Tool calls during action execution',
      items: {
        $ref: '#/components/schemas/ToolCallResult',
      },
    },
    files: {
      type: 'array',
      description: 'Files generated by the action',
      items: {
        $ref: '#/components/schemas/DriveFile',
      },
    },
    artifacts: {
      type: 'array',
      description: 'Structured artifacts produced by successful tool calls',
      items: {
        $ref: '#/components/schemas/WorkflowArtifact',
      },
    },
    outputUrl: {
      type: 'string',
      format: 'uri',
      description: 'Media generation output URL (for media type actions)',
      example: 'https://example.com/generated/image_123.png',
    },
    storageKey: {
      type: 'string',
      description: 'Media generation output storage key',
    },
    pilotStepId: {
      type: 'string',
      description: 'Pilot step ID',
    },
    pilotSessionId: {
      type: 'string',
      description: 'Pilot session ID',
    },
    workflowExecutionId: {
      type: 'string',
      description: 'Workflow execution ID for workflow context',
    },
    workflowNodeExecutionId: {
      type: 'string',
      description: 'Workflow node execution ID for workflow context',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Message creation time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Message update time',
    },
  },
} as const;

export const IndexStatusSchema = {
  type: 'string',
  description: 'Resource index status',
  enum: ['init', 'wait_parse', 'wait_index', 'finish', 'parse_failed', 'index_failed'],
} as const;

export const SubscriptionIntervalSchema = {
  type: 'string',
  description: 'Payment recurring interval',
  enum: ['monthly', 'yearly'],
} as const;

export const SubscriptionPlanTypeSchema = {
  type: 'string',
  description: 'Subscription plan type',
  enum: ['free', 'starter', 'maker', 'enterprise', 'plus', 'pro'],
} as const;

export const SubscriptionStatusSchema = {
  type: 'string',
  description: 'Subscription status',
  enum: [
    'active',
    'canceled',
    'incomplete',
    'incomplete_expired',
    'past_due',
    'paused',
    'trialing',
    'unpaid',
  ],
} as const;

export const SubscriptionSchema = {
  type: 'object',
  required: ['subscriptionId', 'planType', 'status'],
  properties: {
    subscriptionId: {
      type: 'string',
      description: 'Subscription ID',
    },
    lookupKey: {
      type: 'string',
      description: 'Subscription lookup key',
    },
    planType: {
      type: 'string',
      description: 'Subscription plan type',
      $ref: '#/components/schemas/SubscriptionPlanType',
    },
    interval: {
      description: 'Payment recurring interval',
      $ref: '#/components/schemas/SubscriptionInterval',
    },
    status: {
      type: 'string',
      description: 'Subscription status',
      $ref: '#/components/schemas/SubscriptionStatus',
    },
    isTrial: {
      type: 'boolean',
      description: 'Whether the subscription is a trial',
    },
    cancelAt: {
      type: 'string',
      format: 'date-time',
      description: 'Subscription cancel time',
    },
  },
} as const;

export const TokenUsageMeterSchema = {
  type: 'object',
  required: [
    'meterId',
    'uid',
    'startAt',
    't1TokenQuota',
    't1TokenUsed',
    't2TokenQuota',
    't2TokenUsed',
  ],
  properties: {
    meterId: {
      type: 'string',
      description: 'Token usage meter ID',
    },
    uid: {
      type: 'string',
      description: 'User ID',
    },
    subscriptionId: {
      type: 'string',
      description: 'Subscription ID',
    },
    startAt: {
      type: 'string',
      format: 'date-time',
      description: 'Token usage meter start time',
    },
    endAt: {
      type: 'string',
      format: 'date-time',
      description: 'Token usage meter end time',
    },
    t1CountQuota: {
      type: 'number',
      description: 'Request count quota (T1)',
      example: 1000000,
    },
    t1CountUsed: {
      type: 'number',
      description: 'Request count used (T1)',
      example: 100000,
    },
    t1TokenQuota: {
      type: 'number',
      description: 'Token quota (T1)',
      example: 1000000,
      deprecated: true,
    },
    t1TokenUsed: {
      type: 'number',
      description: 'Token used (T1)',
      example: 100000,
      deprecated: true,
    },
    t2CountQuota: {
      type: 'number',
      description: 'Request count quota (T2)',
      example: 1000000,
    },
    t2CountUsed: {
      type: 'number',
      description: 'Request count used (T2)',
      example: 100000,
    },
    t2TokenQuota: {
      type: 'number',
      description: 'Token quota (T2)',
      example: 1000000,
      deprecated: true,
    },
    t2TokenUsed: {
      type: 'number',
      description: 'Token used (T2)',
      example: 100000,
      deprecated: true,
    },
  },
} as const;

export const StorageUsageMeterSchema = {
  type: 'object',
  required: [
    'meterId',
    'uid',
    'objectStorageQuota',
    'resourceSize',
    'canvasSize',
    'fileSize',
    'vectorStorageQuota',
    'vectorStorageUsed',
  ],
  properties: {
    meterId: {
      type: 'string',
      description: 'Storage usage meter ID',
    },
    uid: {
      type: 'string',
      description: 'User ID',
    },
    subscriptionId: {
      type: 'string',
      description: 'Subscription ID',
    },
    fileCountUsed: {
      type: 'number',
      description: 'File count used',
      example: 100,
    },
    fileCountQuota: {
      type: 'number',
      description: 'File count quota',
      example: 1000,
    },
    objectStorageQuota: {
      type: 'string',
      description: 'Object storage quota (in bytes), including resource, canvas and static files',
      example: '104857600',
      deprecated: true,
    },
    resourceSize: {
      type: 'string',
      description: 'Resource storage size in use (in bytes)',
      example: '1048576',
      deprecated: true,
    },
    canvasSize: {
      type: 'string',
      description: 'Canvas storage size in use (in bytes)',
      example: '1048576',
      deprecated: true,
    },
    fileSize: {
      type: 'string',
      description: 'Static file storage size in use (in bytes)',
      example: '1048576',
      deprecated: true,
    },
    vectorStorageQuota: {
      type: 'string',
      description: 'Vector storage quota (in bytes)',
      example: '1048576',
      deprecated: true,
    },
    vectorStorageUsed: {
      type: 'string',
      description: 'Vector storage size used (in bytes)',
      example: '1048576',
      deprecated: true,
    },
  },
} as const;

export const FileParsingMeterSchema = {
  type: 'object',
  required: ['pagesParsed', 'pagesLimit'],
  properties: {
    pagesParsed: {
      type: 'number',
      description: 'File pages parsed',
    },
    pagesLimit: {
      type: 'number',
      description: 'File pages limit',
    },
    fileUploadLimit: {
      type: 'number',
      description: 'File upload limit (in MB)',
    },
  },
} as const;

export const OperationModeSchema = {
  type: 'string',
  description: 'Operation mode',
  enum: ['mouse', 'touchpad'],
} as const;

export const ProviderConfigSchema = {
  type: 'object',
  description: 'Provider config',
  properties: {
    providerId: {
      type: 'string',
      description: 'Provider ID',
    },
    providerKey: {
      type: 'string',
      description: 'Provider key',
    },
  },
} as const;

export const ModelSceneSchema = {
  type: 'string',
  description: 'Model usage scene',
  enum: ['chat', 'copilot', 'agent', 'queryAnalysis', 'titleGeneration', 'image', 'video', 'audio'],
} as const;

export const DefaultModelConfigSchema = {
  type: 'object',
  description: 'Default model config',
  properties: {
    chat: {
      description: 'Default chat model to use',
      $ref: '#/components/schemas/ProviderItem',
    },
    copilot: {
      description: 'Default copilot model to use',
      $ref: '#/components/schemas/ProviderItem',
    },
    agent: {
      description: 'Default agent model to use',
      $ref: '#/components/schemas/ProviderItem',
    },
    queryAnalysis: {
      description: 'Query analysis and context processing model',
      $ref: '#/components/schemas/ProviderItem',
    },
    titleGeneration: {
      description: 'Title generation model for canvas and documents',
      $ref: '#/components/schemas/ProviderItem',
    },
    image: {
      description: 'Default image generation model',
      $ref: '#/components/schemas/ProviderItem',
    },
    video: {
      description: 'Default video generation model',
      $ref: '#/components/schemas/ProviderItem',
    },
    audio: {
      description: 'Default audio generation model',
      $ref: '#/components/schemas/ProviderItem',
    },
  },
} as const;

export const ProviderModeSchema = {
  type: 'string',
  deprecated: true,
  description:
    'Deprecated. Built-in models are always available and user models are gated by their own switch, so the global/custom split is no longer honoured by the backend.',
  enum: ['global', 'custom'],
} as const;

export const UserPreferencesSchema = {
  type: 'object',
  description: 'User preferences',
  properties: {
    operationMode: {
      description: 'Operation mode',
      $ref: '#/components/schemas/OperationMode',
    },
    providerMode: {
      deprecated: true,
      description: 'Deprecated, no longer honoured by the backend',
      $ref: '#/components/schemas/ProviderMode',
    },
    disableHoverCard: {
      type: 'boolean',
      description: 'Whether to disable hover tutorial',
      default: false,
    },
    hasBeenInvited: {
      type: 'boolean',
      description: 'Whether the user has been invited',
      default: false,
    },
    requireInvitationCode: {
      type: 'boolean',
      description: 'Whether to require invitation code',
    },
    needOnboarding: {
      type: 'boolean',
      description: 'Whether this user needs onboarding',
    },
    webSearch: {
      description: 'Web search config',
      $ref: '#/components/schemas/ProviderConfig',
    },
    urlParsing: {
      description: 'URL parsing config',
      $ref: '#/components/schemas/ProviderConfig',
    },
    pdfParsing: {
      description: 'PDF parsing config',
      $ref: '#/components/schemas/ProviderConfig',
    },
    defaultModel: {
      description: 'Default model config',
      $ref: '#/components/schemas/DefaultModelConfig',
    },
  },
} as const;

export const OnboardingStatusSchema = {
  type: 'string',
  description: 'Onboarding status',
  enum: ['not_started', 'skipped', 'completed'],
} as const;

export const OnboardingConfigSchema = {
  type: 'object',
  properties: {
    settings: {
      description: 'Settings onboarding status',
      $ref: '#/components/schemas/OnboardingStatus',
    },
    tour: {
      description: 'Tour onboarding status',
      $ref: '#/components/schemas/OnboardingStatus',
    },
  },
} as const;

export const UserSettingsSchema = {
  type: 'object',
  required: ['uid', 'avatar', 'name', 'email'],
  properties: {
    uid: {
      type: 'string',
      description: 'User ID',
      example: 'u-g30e1b80b5g1itbemc0g5jj3',
    },
    avatar: {
      type: 'string',
      description: 'User avatar',
      example: 'https://nfyc.top/static/avatar/1.jpg',
    },
    name: {
      type: 'string',
      description: 'Username (can only contains letters, numbers, and underscores)',
      example: 'john_doe',
    },
    nickname: {
      type: 'string',
      description: 'User nickname',
      example: 'John Doe',
    },
    email: {
      type: 'string',
      description: 'User email',
      example: '6XJpZ@example.com',
    },
    emailVerified: {
      type: 'boolean',
      description: 'Whether email is verified',
      default: false,
    },
    uiLocale: {
      type: 'string',
      description: 'User UI locale',
      example: 'en',
    },
    outputLocale: {
      type: 'string',
      description: 'User output locale',
      example: 'en',
    },
    customerId: {
      type: 'string',
      description: 'Stripe customer ID',
    },
    subscription: {
      description: 'User subscription',
      $ref: '#/components/schemas/Subscription',
    },
    hasBetaAccess: {
      type: 'boolean',
      description: 'Whether the user has beta access',
      default: false,
    },
    preferences: {
      description: 'User preferences',
      $ref: '#/components/schemas/UserPreferences',
    },
    onboarding: {
      description: 'Onboarding config',
      $ref: '#/components/schemas/OnboardingConfig',
    },
  },
} as const;

export const AuthProviderSchema = {
  type: 'string',
  description: 'Auth provider',
  enum: ['email', 'google', 'github', 'invitation'],
} as const;

export const AuthConfigItemSchema = {
  type: 'object',
  required: ['provider'],
  properties: {
    provider: {
      description: 'Auth provider',
      $ref: '#/components/schemas/AuthProvider',
    },
  },
} as const;

export const AuthConfigResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Auth providers',
          items: {
            $ref: '#/components/schemas/AuthConfigItem',
          },
        },
      },
    },
  ],
} as const;

export const EmailSignupRequestSchema = {
  type: 'object',
  description: 'Email signup request',
  required: ['email', 'password'],
  properties: {
    email: {
      type: 'string',
      description: 'Email',
    },
    password: {
      type: 'string',
      description: 'Password',
    },
  },
} as const;

export const EmailSignupDataSchema = {
  type: 'object',
  properties: {
    sessionId: {
      type: 'string',
      description: 'Verification session ID',
    },
    skipVerification: {
      type: 'boolean',
      description: 'Whether email verification is skipped',
    },
  },
} as const;

export const EmailSignupResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/EmailSignupData',
        },
      },
    },
  ],
} as const;

export const VerificationPurposeSchema = {
  type: 'string',
  description: 'Verification purpose',
  enum: ['signup', 'resetPassword'],
} as const;

export const CreateVerificationRequestSchema = {
  type: 'object',
  description: 'Create verification session request',
  required: ['email', 'purpose'],
  properties: {
    email: {
      type: 'string',
      description: 'Email',
    },
    purpose: {
      type: 'string',
      description: 'Verification purpose',
      $ref: '#/components/schemas/VerificationPurpose',
    },
    password: {
      type: 'string',
      description: 'Password',
    },
  },
} as const;

export const CreateVerificationDataSchema = {
  type: 'object',
  properties: {
    sessionId: {
      type: 'string',
      description: 'Verification session ID',
    },
  },
} as const;

export const CreateVerificationResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/CreateVerificationData',
        },
      },
    },
  ],
} as const;

export const ResendVerificationRequestSchema = {
  type: 'object',
  description: 'Resend verification request',
  required: ['sessionId'],
  properties: {
    sessionId: {
      type: 'string',
      description: 'Verification session ID',
    },
  },
} as const;

export const CheckVerificationRequestSchema = {
  type: 'object',
  description: 'Check verification code request',
  required: ['sessionId', 'code'],
  properties: {
    sessionId: {
      type: 'string',
      description: 'Verification session ID',
    },
    code: {
      type: 'string',
      description: 'Verification code',
    },
  },
} as const;

export const EmailLoginRequestSchema = {
  type: 'object',
  description: 'Email login request',
  required: ['email', 'password'],
  properties: {
    email: {
      type: 'string',
      description: 'Email',
    },
    password: {
      type: 'string',
      description: 'Password',
    },
  },
} as const;

export const EmailLoginDataSchema = {
  type: 'object',
  properties: {
    accessToken: {
      type: 'string',
      description: 'Access token',
    },
  },
} as const;

export const EmailLoginResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/EmailLoginData',
        },
      },
    },
  ],
} as const;

export const GetUserSettingsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/UserSettings',
        },
      },
    },
  ],
} as const;

export const CollabTokenDataSchema = {
  type: 'object',
  required: ['token'],
  properties: {
    token: {
      type: 'string',
      description: 'Collab token',
    },
    expiresAt: {
      type: 'number',
      description: 'Token expiration time (in unix milliseconds)',
    },
  },
} as const;

export const GetCollabTokenResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/CollabTokenData',
        },
      },
    },
  ],
} as const;

export const WorkflowSkillInputFieldSchema = {
  description: '工作流技能的输入字段定义',
  type: 'object',
  required: ['variableId', 'name', 'type', 'required'],
  properties: {
    variableId: {
      type: 'string',
      description: '工作流变量 ID',
      example: 'var-topic',
    },
    name: {
      type: 'string',
      description: '输入字段名称',
      example: '选题',
    },
    type: {
      type: 'string',
      description: '工作流变量类型',
      example: 'string',
    },
    required: {
      type: 'boolean',
      description: '是否必填',
      example: true,
    },
    description: {
      type: 'string',
      description: '输入字段说明',
      example: '要生成内容的主题',
    },
  },
} as const;

export const CreateWorkflowSkillRequestSchema = {
  description: '创建或更新工作流技能的请求；plan、planId、canvasId 至少提供一个',
  type: 'object',
  required: ['name'],
  properties: {
    name: {
      type: 'string',
      minLength: 1,
      maxLength: 100,
      description: '当前用户下唯一的技能名称',
      example: '社媒内容发布',
    },
    description: {
      type: 'string',
      maxLength: 1000,
      description: '技能用途说明',
      example: '根据选题生成内容并发布到社交平台',
    },
    planId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
      description: '已保存的工作流计划 ID',
      example: 'wfp-abc123',
    },
    planVersion: {
      type: 'integer',
      minimum: 0,
      description: '工作流计划版本；省略时读取服务端默认版本',
      example: 1,
    },
    canvasId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
      description: '当前用户拥有的源画布 ID',
      example: 'c-abc123',
    },
    plan: {
      $ref: '#/components/schemas/WorkflowPlan',
    },
    inputSchema: {
      type: 'array',
      description: '自定义技能输入字段；省略时从计划变量生成',
      items: {
        $ref: '#/components/schemas/WorkflowSkillInputField',
      },
    },
  },
} as const;

export const WorkflowSkillIdRequestSchema = {
  description: '工作流技能 ID 请求',
  type: 'object',
  required: ['skillId'],
  additionalProperties: false,
  properties: {
    skillId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
      description: '工作流技能 ID',
      example: 'skill-abc123',
    },
  },
} as const;

export const InvokeWorkflowSkillRequestSchema = {
  description: '调用工作流技能请求',
  type: 'object',
  required: ['skillId'],
  additionalProperties: false,
  properties: {
    skillId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
      description: '工作流技能 ID',
      example: 'skill-abc123',
    },
    variables: {
      type: 'object',
      description: '按变量名称或变量 ID 覆盖的标量输入值',
      additionalProperties: {
        oneOf: [
          {
            type: 'string',
          },
          {
            type: 'number',
          },
          {
            type: 'boolean',
          },
          {
            type: 'string',
            nullable: true,
          },
        ],
      },
      example: {
        选题: '新品发布',
        count: 3,
      },
    },
  },
} as const;

export const WorkflowSkillSchema = {
  description: '可复用的工作流计划快照',
  type: 'object',
  required: ['skillId', 'name', 'plan', 'inputSchema', 'enabled', 'createdAt', 'updatedAt'],
  properties: {
    skillId: {
      type: 'string',
      description: '工作流技能 ID',
      example: 'skill-abc123',
    },
    name: {
      type: 'string',
      description: '技能名称',
      example: '社媒内容发布',
    },
    description: {
      type: 'string',
      nullable: true,
      description: '技能用途说明',
    },
    canvasId: {
      type: 'string',
      nullable: true,
      description: '源画布 ID',
    },
    planId: {
      type: 'string',
      nullable: true,
      description: '源工作流计划 ID',
    },
    planVersion: {
      type: 'integer',
      nullable: true,
      description: '源工作流计划版本',
    },
    plan: {
      $ref: '#/components/schemas/WorkflowPlan',
    },
    inputSchema: {
      type: 'array',
      description: '技能输入字段',
      items: {
        $ref: '#/components/schemas/WorkflowSkillInputField',
      },
    },
    enabled: {
      type: 'boolean',
      description: '技能是否可调用',
      example: true,
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '创建时间',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: '更新时间',
    },
  },
} as const;

export const WorkflowSkillResponseSchema = {
  description: '单个工作流技能响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/WorkflowSkill',
        },
      },
    },
  ],
} as const;

export const WorkflowSkillListResponseSchema = {
  description: '工作流技能列表响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/WorkflowSkill',
          },
        },
      },
    },
  ],
} as const;

export const InvokeWorkflowSkillResultSchema = {
  description: '工作流技能调用结果',
  type: 'object',
  required: ['canvasId', 'skillId', 'name', 'nodesCount'],
  properties: {
    canvasId: {
      type: 'string',
      description: '新创建的画布 ID',
      example: 'c-abc123',
    },
    skillId: {
      type: 'string',
      description: '被调用的技能 ID',
      example: 'skill-abc123',
    },
    name: {
      type: 'string',
      description: '被调用的技能名称',
      example: '社媒内容发布',
    },
    nodesCount: {
      type: 'integer',
      minimum: 1,
      description: '新画布节点数量',
      example: 5,
    },
  },
} as const;

export const InvokeWorkflowSkillResponseSchema = {
  description: '工作流技能调用响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/InvokeWorkflowSkillResult',
        },
      },
    },
  ],
} as const;

export const PromptSkillListItemSchema = {
  description: '提示词技能列表项（不含说明文档正文）',
  type: 'object',
  required: ['skillId', 'title', 'description', 'enabled', 'source', 'createdAt', 'updatedAt'],
  properties: {
    skillId: {
      type: 'string',
      description: '提示词技能 ID',
      example: 'pks-x7k2m9q4',
    },
    title: {
      type: 'string',
      description: '技能名称',
      example: '阿拉丁·Logo极速生成器',
    },
    description: {
      type: 'string',
      description: '技能简介，供 AI 在能力清单中判断是否选用',
      example: '输入品牌名与一句气质描述，30秒产出可落地的 Logo 方案。',
    },
    enabled: {
      type: 'boolean',
      description: '是否启用。启用后才会进入 AI 能力清单',
      example: true,
    },
    source: {
      type: 'string',
      description: '创建来源',
      enum: ['ai', 'manual'],
      example: 'manual',
    },
    createdAt: {
      type: 'string',
      description: '创建时间',
    },
    updatedAt: {
      type: 'string',
      description: '更新时间',
    },
  },
} as const;

export const PromptSkillSchema = {
  description: '提示词技能详情',
  allOf: [
    {
      $ref: '#/components/schemas/PromptSkillListItem',
    },
    {
      type: 'object',
      required: ['instructions'],
      properties: {
        instructions: {
          type: 'string',
          description: 'Markdown 格式的说明文档，绑定到节点后注入 AI 提示词',
        },
      },
    },
  ],
} as const;

export const PromptSkillIdRequestSchema = {
  description: '提示词技能 ID 请求',
  type: 'object',
  required: ['skillId'],
  properties: {
    skillId: {
      type: 'string',
      description: '提示词技能 ID',
    },
  },
} as const;

export const CreatePromptSkillRequestSchema = {
  description: '创建提示词技能请求',
  type: 'object',
  required: ['title'],
  properties: {
    title: {
      type: 'string',
      description: '技能名称',
      maxLength: 100,
    },
    description: {
      type: 'string',
      description: '技能简介',
      maxLength: 1000,
    },
    instructions: {
      type: 'string',
      description: 'Markdown 格式的说明文档',
      maxLength: 50000,
    },
  },
} as const;

export const UpdatePromptSkillRequestSchema = {
  description: '更新提示词技能请求，三个可选字段至少提供一个',
  type: 'object',
  required: ['skillId'],
  properties: {
    skillId: {
      type: 'string',
      description: '提示词技能 ID',
    },
    title: {
      type: 'string',
      description: '技能名称',
      maxLength: 100,
    },
    description: {
      type: 'string',
      description: '技能简介',
      maxLength: 1000,
    },
    instructions: {
      type: 'string',
      description: 'Markdown 格式的说明文档',
      maxLength: 50000,
    },
  },
} as const;

export const TogglePromptSkillRequestSchema = {
  description: '启用或停用提示词技能请求',
  type: 'object',
  required: ['skillId', 'enabled'],
  properties: {
    skillId: {
      type: 'string',
      description: '提示词技能 ID',
    },
    enabled: {
      type: 'boolean',
      description: '目标启用状态',
    },
  },
} as const;

export const ListPromptSkillResponseSchema = {
  description: '提示词技能列表响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/PromptSkillListItem',
          },
        },
      },
    },
  ],
} as const;

export const PromptSkillResponseSchema = {
  description: '提示词技能详情响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/PromptSkill',
        },
      },
    },
  ],
} as const;

export const PromptSkillListItemResponseSchema = {
  description: '提示词技能列表项响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/PromptSkillListItem',
        },
      },
    },
  ],
} as const;

export const BaseResponseSchema = {
  description: 'API 通用响应',
  type: 'object',
  required: ['success'],
  properties: {
    success: {
      type: 'boolean',
      description: '操作是否成功',
      example: true,
    },
    errCode: {
      type: 'string',
      description: '失败时的错误码',
      example: 'RESOURCE_NOT_FOUND',
    },
    errMsg: {
      type: 'string',
      description: '失败时的错误信息',
      example: '操作失败',
    },
    traceId: {
      type: 'string',
      description: '请求跟踪 ID，用于日志排查',
      example: '7f02c56a2e1f4b83',
    },
    stack: {
      type: 'string',
      description: '错误堆栈，仅开发环境返回',
    },
  },
} as const;

export const ErrorDetailSchema = {
  type: 'object',
  required: ['code', 'message'],
  properties: {
    code: {
      type: 'string',
      description: 'Error code identifier',
      example: 'TTS_FAILED',
    },
    message: {
      type: 'string',
      description: 'Human-readable error message',
      example: 'Failed to generate speech after 3 attempts',
    },
  },
} as const;

export const BaseResponseV2Schema = {
  type: 'object',
  required: ['status'],
  properties: {
    status: {
      type: 'string',
      description: 'Response status',
      enum: ['success', 'failed'],
      example: 'success',
    },
    data: {
      description: 'Response data (generic object, present when status is success)',
      type: 'object',
      additionalProperties: true,
    },
    errors: {
      type: 'array',
      description: 'Array of error objects (present when status is error)',
      items: {
        $ref: '#/components/schemas/ErrorDetail',
      },
    },
  },
} as const;

export const ListCanvasResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: '画布列表',
          items: {
            $ref: '#/components/schemas/Canvas',
          },
        },
      },
    },
  ],
} as const;

export const GetCanvasDetailResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/Canvas',
        },
      },
    },
  ],
} as const;

export const CanvasHistoryVersionSchema = {
  type: 'object',
  required: ['version', 'hash', 'timestamp'],
  properties: {
    version: {
      type: 'string',
      description: 'Canvas version',
    },
    hash: {
      type: 'string',
      description: 'Canvas hash',
    },
    timestamp: {
      type: 'number',
      description: 'Canvas timestamp (in unix milliseconds)',
    },
  },
} as const;

export const CanvasDataSchema = {
  type: 'object',
  description: 'Canvas data',
  required: ['nodes', 'edges'],
  properties: {
    nodes: {
      type: 'array',
      description: 'Canvas nodes',
      items: {
        $ref: '#/components/schemas/CanvasNode',
      },
    },
    edges: {
      type: 'array',
      description: 'Canvas edges',
      items: {
        $ref: '#/components/schemas/CanvasEdge',
      },
    },
  },
} as const;

export const CanvasStateSchema = {
  type: 'object',
  description: 'Canvas state',
  allOf: [
    {
      $ref: '#/components/schemas/CanvasData',
    },
    {
      type: 'object',
      properties: {
        version: {
          type: 'string',
          description: 'Canvas version',
        },
        hash: {
          type: 'string',
          description: 'Canvas state hash (sha256), calculated from nodes and edges',
        },
        transactions: {
          type: 'array',
          description: 'Canvas transaction list',
          items: {
            $ref: '#/components/schemas/CanvasTransaction',
          },
        },
        history: {
          type: 'array',
          description: 'Canvas history versions',
          items: {
            $ref: '#/components/schemas/CanvasHistoryVersion',
          },
        },
        createdAt: {
          type: 'number',
          description: 'Canvas creation timestamp (in unix milliseconds)',
        },
        updatedAt: {
          type: 'number',
          description: 'Canvas last updated timestamp (in unix milliseconds)',
        },
      },
    },
  ],
} as const;

export const RawCanvasDataSchema = {
  type: 'object',
  description: 'Raw canvas data',
  allOf: [
    {
      $ref: '#/components/schemas/CanvasData',
    },
    {
      type: 'object',
      properties: {
        canvasId: {
          type: 'string',
          description: 'Canvas ID',
        },
        title: {
          type: 'string',
          description: 'Canvas title',
        },
        owner: {
          type: 'object',
          description: 'Canvas owner',
          $ref: '#/components/schemas/ShareUser',
        },
        minimapUrl: {
          type: 'string',
          description: 'Minimap URL',
        },
        variables: {
          type: 'array',
          description: 'Workflow variables',
          items: {
            $ref: '#/components/schemas/WorkflowVariable',
          },
        },
      },
    },
  ],
} as const;

export const GetCanvasDataResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/RawCanvasData',
        },
      },
    },
  ],
} as const;

export const SharedCanvasDataSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/RawCanvasData',
    },
    {
      type: 'object',
      properties: {
        resources: {
          type: 'array',
          description: 'Resources in the canvas',
          deprecated: true,
          items: {
            $ref: '#/components/schemas/Resource',
          },
        },
        files: {
          type: 'array',
          description: 'Drive files in the canvas',
          items: {
            $ref: '#/components/schemas/DriveFile',
          },
        },
      },
    },
  ],
} as const;

export const ExportCanvasResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            downloadUrl: {
              type: 'string',
              description: 'Download URL for the canvas data',
            },
          },
        },
      },
    },
  ],
} as const;

export const ImportCanvasRequestSchema = {
  type: 'object',
  required: ['file'],
  properties: {
    file: {
      type: 'string',
      format: 'binary',
      description: 'File to import',
    },
    canvasId: {
      type: 'string',
      description: 'Canvas ID to specify',
    },
  },
} as const;

export const DuplicateCanvasRequestSchema = {
  type: 'object',
  required: ['canvasId'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID to duplicate',
    },
    title: {
      type: 'string',
      description: 'Custom canvas title',
    },
    projectId: {
      type: 'string',
      description: 'Project ID to bind with',
    },
    duplicateEntities: {
      type: 'boolean',
      description: 'Whether to duplicate entities within the canvas',
    },
  },
} as const;

export const DuplicateDocumentRequestSchema = {
  type: 'object',
  required: ['docId'],
  properties: {
    docId: {
      type: 'string',
      description: 'Document ID to duplicate',
    },
    title: {
      type: 'string',
      description: 'Custom document title for the duplicate',
    },
    canvasId: {
      type: 'string',
      description: 'Target canvas ID',
    },
  },
} as const;

export const DuplicateResourceRequestSchema = {
  type: 'object',
  required: ['resourceId'],
  properties: {
    resourceId: {
      type: 'string',
      description: 'Resource ID to duplicate',
    },
    title: {
      type: 'string',
      description: 'Custom resource title for the duplicate',
    },
    canvasId: {
      type: 'string',
      description: 'Target canvas ID',
    },
  },
} as const;

export const UpsertCanvasRequestSchema = {
  type: 'object',
  properties: {
    title: {
      type: 'string',
      description: '画布标题',
      example: 'My Canvas',
    },
    canvasId: {
      type: 'string',
      description: '画布 ID（仅更新时使用）',
      example: 'c-g30e1b80b5g1itbemc0g5jj3',
    },
    projectId: {
      type: 'string',
      description: '要绑定的项目 ID',
    },
    minimapStorageKey: {
      type: 'string',
      description: '缩略图存储键',
    },
    variables: {
      type: 'array',
      description: '工作流变量',
      items: {
        $ref: '#/components/schemas/WorkflowVariable',
      },
    },
    visibility: {
      type: 'boolean',
      description: '是否在列表中展示该画布',
      default: true,
    },
  },
} as const;

export const UpsertCanvasResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/Canvas',
        },
      },
    },
  ],
} as const;

export const DeleteCanvasRequestSchema = {
  type: 'object',
  required: ['canvasId'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID to delete',
      example: 'c-g30e1b80b5g1itbemc0g5jj3',
    },
    deleteAllFiles: {
      type: 'boolean',
      description: 'Whether to delete all files in the canvas',
      default: false,
    },
  },
} as const;

export const AutoNameCanvasRequestSchema = {
  type: 'object',
  required: ['canvasId'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID',
    },
    directUpdate: {
      type: 'boolean',
      description: 'Whether to directly update the canvas title',
      default: false,
    },
  },
} as const;

export const AutoNameCanvasResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Auto name canvas result',
          properties: {
            title: {
              type: 'string',
              description: 'New canvas title',
            },
          },
        },
      },
    },
  ],
} as const;

export const GetCanvasStateResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/CanvasState',
        },
      },
    },
  ],
} as const;

export const SetCanvasStateRequestSchema = {
  type: 'object',
  required: ['canvasId', 'state'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID',
    },
    state: {
      type: 'object',
      description: 'Canvas state to set',
      $ref: '#/components/schemas/CanvasState',
    },
  },
} as const;

export const GetCanvasTransactionsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Canvas diff list',
          items: {
            $ref: '#/components/schemas/CanvasTransaction',
          },
        },
      },
    },
  ],
} as const;

export const DiffTypeSchema = {
  type: 'string',
  description: 'Diff type',
  enum: ['add', 'update', 'delete'],
} as const;

export const NodeDiffSchema = {
  type: 'object',
  required: ['id', 'type'],
  properties: {
    id: {
      type: 'string',
      description: 'Node ID',
    },
    type: {
      type: 'string',
      description: 'Node diff type',
      $ref: '#/components/schemas/DiffType',
    },
    from: {
      type: 'object',
      description: 'Node diff from (only the fields that are different will be included)',
      additionalProperties: true,
    },
    to: {
      type: 'object',
      description: 'Node diff to (only the fields that are different will be included)',
      additionalProperties: true,
    },
  },
} as const;

export const EdgeDiffSchema = {
  type: 'object',
  required: ['id', 'type'],
  properties: {
    id: {
      type: 'string',
      description: 'Edge ID',
    },
    type: {
      type: 'string',
      description: 'Edge diff type',
      $ref: '#/components/schemas/DiffType',
    },
    from: {
      type: 'object',
      description: 'Edge diff from',
      $ref: '#/components/schemas/CanvasEdge',
    },
    to: {
      type: 'object',
      description: 'Edge diff to',
      $ref: '#/components/schemas/CanvasEdge',
    },
  },
} as const;

export const CanvasTransactionSourceSchema = {
  type: 'object',
  description: 'Canvas transaction source',
  required: ['type'],
  properties: {
    type: {
      type: 'string',
      description: 'Source type',
      enum: ['user', 'system'],
    },
    uid: {
      type: 'string',
      description: 'Source user ID',
    },
  },
} as const;

export const CanvasTransactionSchema = {
  type: 'object',
  required: ['txId', 'nodeDiffs', 'edgeDiffs', 'createdAt'],
  properties: {
    txId: {
      type: 'string',
      description: 'Transaction ID',
    },
    nodeDiffs: {
      type: 'array',
      description: 'Node diffs',
      items: {
        $ref: '#/components/schemas/NodeDiff',
      },
    },
    edgeDiffs: {
      type: 'array',
      description: 'Edge diffs',
      items: {
        $ref: '#/components/schemas/EdgeDiff',
      },
    },
    revoked: {
      type: 'boolean',
      description: 'Whether the transaction is revoked',
    },
    source: {
      type: 'object',
      description: 'Transaction source',
      $ref: '#/components/schemas/CanvasTransactionSource',
    },
    deleted: {
      type: 'boolean',
      description: 'Whether the transaction is deleted',
    },
    createdAt: {
      type: 'number',
      description: 'Transaction creation timestamp (in unix milliseconds)',
    },
    syncedAt: {
      type: 'number',
      description: 'Transaction synchronization timestamp (in unix milliseconds)',
    },
  },
} as const;

export const SyncCanvasStateRequestSchema = {
  type: 'object',
  required: ['canvasId', 'transactions'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID',
    },
    version: {
      type: 'string',
      description: 'Canvas state version',
    },
    transactions: {
      type: 'array',
      description: 'Transaction list',
      items: {
        $ref: '#/components/schemas/CanvasTransaction',
      },
    },
  },
} as const;

export const SyncCanvasStateResultSchema = {
  type: 'object',
  required: ['transactions'],
  properties: {
    transactions: {
      type: 'array',
      description: 'Transaction list',
      items: {
        $ref: '#/components/schemas/CanvasTransaction',
      },
    },
  },
} as const;

export const SyncCanvasStateResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Apply canvas state result',
          $ref: '#/components/schemas/SyncCanvasStateResult',
        },
      },
    },
  ],
} as const;

export const CreateCanvasVersionRequestSchema = {
  type: 'object',
  required: ['canvasId', 'state'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID',
    },
    state: {
      type: 'object',
      description: 'Canvas state',
      $ref: '#/components/schemas/CanvasState',
    },
  },
} as const;

export const VersionConflictSchema = {
  type: 'object',
  required: ['localState', 'remoteState'],
  properties: {
    localState: {
      type: 'object',
      description: 'Local canvas state',
      $ref: '#/components/schemas/CanvasState',
    },
    remoteState: {
      type: 'object',
      description: 'Server canvas state',
      $ref: '#/components/schemas/CanvasState',
    },
  },
} as const;

export const CreateCanvasVersionResultSchema = {
  type: 'object',
  required: ['canvasId'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID',
    },
    conflict: {
      type: 'object',
      description: 'Version conflict (when there is a conflict)',
      $ref: '#/components/schemas/VersionConflict',
    },
    newState: {
      type: 'object',
      description: 'New canvas state (when there is no conflict)',
      $ref: '#/components/schemas/CanvasState',
    },
  },
} as const;

export const CreateCanvasVersionResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Create canvas version result',
          $ref: '#/components/schemas/CreateCanvasVersionResult',
        },
      },
    },
  ],
} as const;

export const ListCanvasTemplateResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: '画布模板列表',
          items: {
            $ref: '#/components/schemas/CanvasTemplate',
          },
        },
      },
    },
  ],
} as const;

export const CreateCanvasTemplateRequestSchema = {
  type: 'object',
  required: ['canvasId', 'title', 'description', 'language'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID',
    },
    title: {
      type: 'string',
      description: 'Canvas template title',
    },
    description: {
      type: 'string',
      description: 'Canvas template description',
    },
    categoryId: {
      type: 'string',
      description: 'Canvas template category ID',
    },
    language: {
      type: 'string',
      description: 'Canvas template language code',
    },
    coverStorageKey: {
      type: 'string',
      description: 'Cover storage key',
    },
    creditUsage: {
      type: 'integer',
      description: 'Credit usage for running this workflow app',
      nullable: true,
    },
  },
} as const;

export const UpdateCanvasTemplateRequestSchema = {
  type: 'object',
  required: ['templateId'],
  properties: {
    templateId: {
      type: 'string',
      description: 'Canvas template ID',
    },
    title: {
      type: 'string',
      description: 'Canvas template title',
    },
    description: {
      type: 'string',
      description: 'Canvas template description',
    },
    categoryId: {
      type: 'string',
      description: 'Canvas template category ID',
    },
    language: {
      type: 'string',
      description: 'Canvas template language code',
    },
  },
} as const;

export const UpsertCanvasTemplateResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: 'Canvas template',
          $ref: '#/components/schemas/CanvasTemplate',
        },
      },
    },
  ],
} as const;

export const ListCanvasTemplateCategoryResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: '画布模板分类列表',
          items: {
            $ref: '#/components/schemas/CanvasTemplateCategory',
          },
        },
      },
    },
  ],
} as const;

export const UpsertResourceRequestSchema = {
  type: 'object',
  required: ['title', 'resourceType'],
  properties: {
    title: {
      type: 'string',
      description: 'Resource title',
      example: 'My Resource',
    },
    resourceType: {
      description: 'Resource type',
      $ref: '#/components/schemas/ResourceType',
    },
    resourceId: {
      type: 'string',
      description: 'Resource ID (only used for update)',
      example: 'r-g30e1b80b5g1itbemc0g5jj3',
    },
    projectId: {
      type: 'string',
      description: 'Project ID to bind with',
    },
    canvasId: {
      type: 'string',
      description: 'Canvas ID to bind with',
    },
    data: {
      description: 'Resource metadata',
      $ref: '#/components/schemas/ResourceMeta',
    },
    storageKey: {
      type: 'string',
      description: 'Storage key',
    },
    content: {
      type: 'string',
      description: 'Resource content (this will be ignored if storageKey was set)',
    },
  },
} as const;

export const UpsertResourceResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/Resource',
        },
      },
    },
  ],
} as const;

export const BatchCreateResourceResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Resource list',
          items: {
            $ref: '#/components/schemas/Resource',
          },
        },
      },
    },
  ],
} as const;

export const ReindexResourceRequestSchema = {
  type: 'object',
  required: ['resourceIds'],
  properties: {
    resourceIds: {
      type: 'array',
      description: 'Resource ID list',
      items: {
        type: 'string',
      },
    },
  },
} as const;

export const ReindexResourceResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Resource list',
          items: {
            $ref: '#/components/schemas/Resource',
          },
        },
      },
    },
  ],
} as const;

export const DeleteResourceRequestSchema = {
  type: 'object',
  required: ['resourceId'],
  properties: {
    resourceId: {
      type: 'string',
      description: 'Resource ID to delete',
      example: 'r-g30e1b80b5g1itbemc0g5jj3',
    },
  },
} as const;

export const ListResourceResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Resource list',
          items: {
            $ref: '#/components/schemas/Resource',
          },
        },
      },
    },
  ],
} as const;

export const GetResourceDetailResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Resource data',
          $ref: '#/components/schemas/Resource',
        },
      },
    },
  ],
} as const;

export const ListDocumentResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Canvas list',
          items: {
            $ref: '#/components/schemas/Document',
          },
        },
      },
    },
  ],
} as const;

export const GetDocumentDetailResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Document data',
          $ref: '#/components/schemas/Document',
        },
      },
    },
  ],
} as const;

export const UpsertDocumentRequestSchema = {
  type: 'object',
  properties: {
    title: {
      type: 'string',
      description: 'Canvas title',
      example: 'My Canvas',
    },
    docId: {
      type: 'string',
      description: 'Document ID (only used for update)',
      example: 'd-g30e1b80b5g1itbemc0g5jj3',
    },
    projectId: {
      type: 'string',
      description: 'Project ID to bind with',
    },
    canvasId: {
      type: 'string',
      description: 'Canvas ID to bind with',
    },
    readOnly: {
      type: 'boolean',
      description: 'Whether this document is read-only',
      default: false,
    },
    initialContent: {
      type: 'string',
      description: 'Document initial content',
    },
    resultId: {
      type: 'string',
      description: 'Action result ID to bind with',
    },
  },
} as const;

export const UpsertDocumentResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/Document',
        },
      },
    },
  ],
} as const;

export const DeleteDocumentRequestSchema = {
  type: 'object',
  required: ['docId'],
  properties: {
    docId: {
      type: 'string',
      description: 'Document ID to delete',
      example: 'd-g30e1b80b5g1itbemc0g5jj3',
    },
  },
} as const;

export const GetActionResultResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/ActionResult',
        },
      },
    },
  ],
} as const;

export const AbortActionRequestSchema = {
  type: 'object',
  required: ['resultId'],
  properties: {
    resultId: {
      type: 'string',
      description: 'Action result ID',
    },
    version: {
      type: 'integer',
      description: 'Action result version',
    },
  },
} as const;

export const ListProjectResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Project list',
          items: {
            $ref: '#/components/schemas/Project',
          },
        },
      },
    },
  ],
} as const;

export const GetProjectDetailResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/Project',
        },
      },
    },
  ],
} as const;

export const UpsertProjectRequestSchema = {
  type: 'object',
  properties: {
    projectId: {
      type: 'string',
      description: 'Project ID (only used for update)',
    },
    name: {
      type: 'string',
      description: 'Project name',
    },
    description: {
      type: 'string',
      description: 'Project description',
    },
    coverStorageKey: {
      type: 'string',
      description: 'Project cover storage key',
    },
    customInstructions: {
      type: 'string',
      description: 'Custom instructions',
    },
  },
} as const;

export const UpsertProjectResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/Project',
        },
      },
    },
  ],
} as const;

export const UpdateProjectItemsRequestSchema = {
  type: 'object',
  properties: {
    projectId: {
      type: 'string',
      description: 'Project ID',
    },
    operation: {
      type: 'string',
      description: 'Operation type',
      enum: ['add', 'remove'],
    },
    items: {
      type: 'array',
      description: 'Item list',
      items: {
        $ref: '#/components/schemas/Entity',
      },
    },
  },
} as const;

export const DeleteProjectRequestSchema = {
  type: 'object',
  required: ['projectId'],
  properties: {
    projectId: {
      type: 'string',
      description: 'Project ID to delete',
      example: 'p-g30e1b80b5g1itbemc0g5jj3',
    },
  },
} as const;

export const DeleteProjectItemsRequestSchema = {
  type: 'object',
  required: ['projectId', 'items'],
  properties: {
    projectId: {
      type: 'string',
      description: 'Project ID',
    },
    items: {
      type: 'array',
      description: 'Item list',
      items: {
        $ref: '#/components/schemas/Entity',
      },
    },
  },
} as const;

export const SkillEventTypeSchema = {
  type: 'string',
  description: 'Skill event type',
  enum: [
    'start',
    'end',
    'stream',
    'log',
    'artifact',
    'structured_data',
    'token_usage',
    'create_node',
    'tool_call_start',
    'tool_call_end',
    'tool_call_error',
    'tool_call_stream',
    'error',
  ],
} as const;

export const SkillEventSchema = {
  type: 'object',
  required: ['event', 'resultId'],
  properties: {
    event: {
      description: 'Event type',
      $ref: '#/components/schemas/SkillEventType',
    },
    skillMeta: {
      description: 'Skill metadata',
      $ref: '#/components/schemas/SkillMeta',
    },
    step: {
      description: 'Action step metadata',
      $ref: '#/components/schemas/ActionStepMeta',
      deprecated: true,
    },
    resultId: {
      type: 'string',
      description: 'Result ID',
    },
    version: {
      type: 'number',
      description: 'Result version',
    },
    messageId: {
      type: 'string',
      description: 'Message ID',
    },
    content: {
      type: 'string',
      description: 'Event content. Only present when `event` is `stream`',
    },
    reasoningContent: {
      type: 'string',
      description: 'Reasoning content. Only present when `event` is `stream`',
    },
    tokenUsage: {
      description: 'Token usage data. Only present when `event` is `token_usage`.',
      $ref: '#/components/schemas/TokenUsageItem',
    },
    log: {
      description: 'Log data. Only present when `event` is `log`.',
      $ref: '#/components/schemas/ActionLog',
    },
    structuredData: {
      type: 'object',
      description: 'Structured data. Only present when `event` is `structured_data`.',
    },
    artifact: {
      description: 'Artifact data. Only present when `event` is `artifact`.',
      $ref: '#/components/schemas/Artifact',
    },
    node: {
      description: 'Canvas node data. Only present when `event` is `create_node`.',
      $ref: '#/components/schemas/CanvasNode',
    },
    error: {
      description: 'Error data. Only present when `event` is `error`.',
      $ref: '#/components/schemas/BaseResponse',
    },
    originError: {
      type: 'string',
      description: 'Original error message. Only present when `event` is `error`.',
    },
    toolCallMeta: {
      description:
        'Tool call metadata. Only present when `event` is `tool_call_start`, `tool_call_end`, or `tool_call_error`.',
      $ref: '#/components/schemas/ToolCallMeta',
    },
    toolCallResult: {
      description: 'Tool call result data.',
      $ref: '#/components/schemas/ToolCallResult',
    },
  },
} as const;

export const ToolCallStatusSchema = {
  type: 'string',
  description: 'Tool call status',
  enum: ['executing', 'completed', 'failed'],
} as const;

export const ToolCallMetaSchema = {
  type: 'object',
  properties: {
    toolName: {
      type: 'string',
      description: 'Tool name',
    },
    toolsetId: {
      type: 'string',
      description: 'Toolset ID',
    },
    toolsetKey: {
      type: 'string',
      description: 'Toolset key',
    },
    toolCallId: {
      type: 'string',
      description: 'Tool call ID',
    },
    status: {
      $ref: '#/components/schemas/ToolCallStatus',
      description: 'Tool call status',
    },
    startTs: {
      type: 'number',
      description: 'Tool call start timestamp (milliseconds)',
    },
    endTs: {
      type: 'number',
      description: 'Tool call end timestamp (milliseconds)',
    },
    error: {
      type: 'string',
      description: 'Tool call error',
    },
  },
} as const;

export const ToolCallResultSchema = {
  type: 'object',
  required: ['callId', 'status', 'createdAt', 'updatedAt'],
  properties: {
    callId: {
      type: 'string',
      description: 'Tool call ID (run_id from LangChain)',
    },
    uid: {
      type: 'string',
      description: 'User ID',
    },
    toolsetId: {
      type: 'string',
      description: 'Toolset ID (toolsetKey)',
    },
    toolName: {
      type: 'string',
      description: 'Tool name',
    },
    stepName: {
      type: 'string',
      description: 'Step name (the action step in which this tool was called)',
    },
    input: {
      oneOf: [
        {
          type: 'object',
        },
        {
          type: 'string',
        },
      ],
      description: 'Tool input arguments',
    },
    output: {
      oneOf: [
        {
          type: 'object',
        },
        {
          type: 'string',
        },
      ],
      description: 'Tool output result',
    },
    error: {
      type: 'string',
      description: 'Error message if tool execution failed',
    },
    status: {
      description: 'Tool call status',
      $ref: '#/components/schemas/ToolCallStatus',
    },
    createdAt: {
      type: 'number',
      description: 'Tool call start timestamp (milliseconds)',
    },
    updatedAt: {
      type: 'number',
      description: 'Tool call last update timestamp (milliseconds)',
    },
    deletedAt: {
      type: 'number',
      description: 'Tool call deletion timestamp (milliseconds)',
    },
  },
} as const;

export const ShareRecordSchema = {
  type: 'object',
  required: ['shareId', 'entityType', 'entityId'],
  properties: {
    shareId: {
      type: 'string',
      description: '分享 ID',
    },
    title: {
      type: 'string',
      description: '分享标题',
    },
    entityType: {
      $ref: '#/components/schemas/EntityType',
      description: '实体类型',
    },
    entityId: {
      type: 'string',
      description: '实体 ID',
    },
    allowDuplication: {
      type: 'boolean',
      description: '是否允许复制共享实体',
    },
    parentShareId: {
      type: 'string',
      description: '父分享 ID',
    },
    templateId: {
      type: 'string',
      description: '画布模板 ID',
    },
    createdAt: {
      type: 'string',
      description: '创建时间戳',
    },
    updatedAt: {
      type: 'string',
      description: '更新时间戳',
    },
  },
} as const;

export const UpsertCodeArtifactRequestSchema = {
  type: 'object',
  properties: {
    artifactId: {
      type: 'string',
      description: 'Code artifact ID (not needed for creation)',
    },
    title: {
      type: 'string',
      description: 'Code artifact title',
    },
    type: {
      type: 'string',
      description: 'Code artifact type',
    },
    content: {
      type: 'string',
      description: 'Code artifact content',
    },
    language: {
      type: 'string',
      description: 'Code artifact language',
    },
    resultId: {
      type: 'string',
      description: 'Action result ID',
    },
    resultVersion: {
      type: 'number',
      description: 'Action result version',
    },
    canvasId: {
      type: 'string',
      description: 'Canvas ID to bind with',
    },
    previewStorageKey: {
      type: 'string',
      description: 'Code artifact preview storage key',
    },
    createIfNotExists: {
      type: 'boolean',
      description: 'Whether to create the code artifact if it does not exist',
      default: false,
    },
  },
} as const;

export const UpsertCodeArtifactResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/CodeArtifact',
        },
      },
    },
  ],
} as const;

export const ListCodeArtifactResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Code artifact list',
          items: {
            $ref: '#/components/schemas/CodeArtifact',
          },
        },
      },
    },
  ],
} as const;

export const GetCodeArtifactDetailResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/CodeArtifact',
        },
      },
    },
  ],
} as const;

export const DuplicateCodeArtifactRequestSchema = {
  type: 'object',
  required: ['artifactId'],
  properties: {
    artifactId: {
      type: 'string',
      description: 'Code artifact ID to duplicate',
    },
    canvasId: {
      type: 'string',
      description: 'Target canvas ID',
    },
  },
} as const;

export const CreateShareRequestSchema = {
  type: 'object',
  required: ['entityType', 'entityId'],
  properties: {
    entityType: {
      $ref: '#/components/schemas/EntityType',
      description: 'Entity type',
    },
    entityId: {
      type: 'string',
      description: 'Entity ID',
    },
    title: {
      type: 'string',
      description: 'Share title',
    },
    allowDuplication: {
      type: 'boolean',
      description: 'Whether to allow duplication of the shared entity',
      default: false,
    },
    parentShareId: {
      type: 'string',
      description: 'Parent share ID',
    },
    shareData: {
      type: 'string',
      description: 'Raw share data (JSON string)',
    },
    shareDataStorageKey: {
      type: 'string',
      description: 'Share data storage key',
    },
    coverStorageKey: {
      type: 'string',
      description: 'Cover storage key',
    },
    creditUsage: {
      type: 'number',
      description: 'Credit usage',
    },
  },
} as const;

export const CreateShareResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/ShareRecord',
          description: 'Share created',
        },
      },
    },
  ],
} as const;

export const ListShareResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Share record list',
          items: {
            $ref: '#/components/schemas/ShareRecord',
          },
        },
      },
    },
  ],
} as const;

export const DeleteShareRequestSchema = {
  type: 'object',
  required: ['shareId'],
  properties: {
    shareId: {
      type: 'string',
      description: 'Share ID',
    },
  },
} as const;

export const DuplicateShareRequestSchema = {
  type: 'object',
  required: ['shareId'],
  properties: {
    shareId: {
      type: 'string',
      description: 'Share ID',
    },
    projectId: {
      type: 'string',
      description: 'Project ID to duplicate the share to',
    },
    canvasId: {
      type: 'string',
      description: 'Target canvas ID',
    },
    title: {
      type: 'string',
      description: 'Custom title for the duplicated entity',
    },
  },
} as const;

export const DuplicateShareResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: 'Duplicated entity',
          $ref: '#/components/schemas/Entity',
        },
      },
    },
  ],
} as const;

export const ListLabelClassesResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Label class list',
          items: {
            $ref: '#/components/schemas/LabelClass',
          },
        },
      },
    },
  ],
} as const;

export const CreateLabelClassRequestSchema = {
  type: 'object',
  required: ['name', 'displayName', 'prompt'],
  properties: {
    name: {
      type: 'string',
      description: 'Label class name',
      example: 'my_class',
    },
    displayName: {
      type: 'string',
      description: 'Label display name',
      example: 'My Class',
    },
    icon: {
      description: 'Label icon',
      $ref: '#/components/schemas/Icon',
    },
    prompt: {
      type: 'string',
      description: 'Label creation instruction prompt',
      example: 'Extract labels for the tech-related keywords',
    },
  },
} as const;

export const UpdateLabelClassRequestSchema = {
  type: 'object',
  required: ['labelClassId'],
  properties: {
    labelClassId: {
      type: 'string',
      description: 'Label class ID',
    },
    name: {
      type: 'string',
      description: 'Label class name',
      example: 'My Class',
    },
    displayName: {
      type: 'string',
      description: 'Label display name',
      example: 'My Class',
    },
    icon: {
      description: 'Label icon',
      $ref: '#/components/schemas/Icon',
    },
    prompt: {
      type: 'string',
      description: 'Label creation instruction prompt',
      example: 'Extract labels for the tech-related keywords',
    },
  },
} as const;

export const UpsertLabelClassResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: 'Label class upserted',
          $ref: '#/components/schemas/LabelClass',
        },
      },
    },
  ],
} as const;

export const DeleteLabelClassRequestSchema = {
  type: 'object',
  required: ['labelClassId'],
  properties: {
    labelClassId: {
      type: 'string',
      description: 'Label class ID to delete',
      example: 'lc-g30e1b80b5g1itbemc0g5jj3',
    },
  },
} as const;

export const ListLabelInstancesResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Label list',
          items: {
            $ref: '#/components/schemas/LabelInstance',
          },
        },
      },
    },
  ],
} as const;

export const CreateLabelInstanceRequestSchema = {
  type: 'object',
  required: ['labelClassId', 'valueList', 'entityType', 'entityId'],
  properties: {
    labelClassId: {
      type: 'string',
      description: 'Label class ID',
      example: 'lc-g30e1b80b5g1itbemc0g5jj3',
    },
    valueList: {
      type: 'array',
      description: 'Label value list',
      items: {
        type: 'string',
        example: 'label-1',
      },
    },
    entityType: {
      description: 'Label entity type',
      $ref: '#/components/schemas/EntityType',
    },
    entityId: {
      description: 'Label entity ID',
      type: 'string',
    },
  },
} as const;

export const UpdateLabelInstanceRequestSchema = {
  type: 'object',
  properties: {
    labelId: {
      type: 'string',
      description: 'Label ID to update',
    },
    value: {
      type: 'string',
      description: 'Updated label value',
    },
  },
} as const;

export const UpsertLabelInstanceResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Label instance upserted',
          items: {
            $ref: '#/components/schemas/LabelInstance',
          },
        },
      },
    },
  ],
} as const;

export const DeleteLabelInstanceRequestSchema = {
  type: 'object',
  required: ['labelId'],
  properties: {
    labelId: {
      type: 'string',
      description: 'Label ID to delete',
      example: 'lb-g30e1b80b5g1itbemc0g5jj3',
    },
  },
} as const;

export const ActionSchema = {
  type: 'object',
  required: ['actionType', 'actionName'],
  properties: {
    actionType: {
      type: 'string',
      description: 'Action type',
      $ref: '#/components/schemas/ActionType',
    },
    actionName: {
      type: 'string',
      description: 'Action name',
    },
    icon: {
      description: 'Action icon',
      $ref: '#/components/schemas/Icon',
    },
  },
} as const;

export const ListActionResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Action list',
          items: {
            $ref: '#/components/schemas/Action',
          },
        },
      },
    },
  ],
} as const;

export const ListSkillResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Skill list',
          items: {
            $ref: '#/components/schemas/Skill',
          },
        },
      },
    },
  ],
} as const;

export const ListSkillInstanceResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Skill list',
          items: {
            $ref: '#/components/schemas/SkillInstance',
          },
        },
      },
    },
  ],
} as const;

export const SkillInstanceCreateParamSchema = {
  type: 'object',
  required: ['displayName'],
  properties: {
    tplName: {
      type: 'string',
      description: 'Skill template name',
      example: 'online-search',
    },
    displayName: {
      type: 'string',
      description: 'Skill display name',
      example: 'My Custom Skill',
    },
    description: {
      type: 'string',
      description: 'Skill description',
    },
    icon: {
      description: 'Skill instance icon',
      $ref: '#/components/schemas/Icon',
    },
    tplConfig: {
      description: 'Skill template config',
      $ref: '#/components/schemas/SkillTemplateConfig',
    },
  },
} as const;

export const CreateSkillInstanceRequestSchema = {
  type: 'object',
  required: ['instanceList'],
  properties: {
    instanceList: {
      type: 'array',
      description: 'Skill instances to upsert',
      items: {
        $ref: '#/components/schemas/SkillInstanceCreateParam',
      },
    },
  },
} as const;

export const CreateSkillInstanceResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Skill instance list',
          items: {
            $ref: '#/components/schemas/SkillInstance',
          },
        },
      },
    },
  ],
} as const;

export const UpdateSkillInstanceRequestSchema = {
  type: 'object',
  required: ['skillId'],
  properties: {
    skillId: {
      type: 'string',
      description: 'Skill ID',
      example: 's-g30e1b80b5g1itbemc0g5jj3',
    },
    displayName: {
      type: 'string',
      description: 'Skill display name',
      example: 'My Custom Skill',
    },
    description: {
      type: 'string',
      description: 'Skill description',
    },
    icon: {
      description: 'Skill instance icon',
      $ref: '#/components/schemas/Icon',
    },
    tplConfig: {
      description: 'Skill template config',
      $ref: '#/components/schemas/SkillTemplateConfig',
    },
  },
} as const;

export const UpdateSkillInstanceResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: 'Skill instance list',
          $ref: '#/components/schemas/SkillInstance',
        },
      },
    },
  ],
} as const;

export const PinSkillInstanceRequestSchema = {
  type: 'object',
  required: ['skillId'],
  properties: {
    skillId: {
      type: 'string',
      description: 'Skill ID to pin',
    },
  },
} as const;

export const UnpinSkillInstanceRequestSchema = {
  type: 'object',
  required: ['skillId'],
  properties: {
    skillId: {
      type: 'string',
      description: 'Skill ID to unpin',
    },
  },
} as const;

export const DeleteSkillInstanceRequestSchema = {
  type: 'object',
  required: ['skillId'],
  properties: {
    skillId: {
      type: 'string',
      description: 'Skill ID to delete',
    },
  },
} as const;

export const SkillInputSchema = {
  type: 'object',
  description: 'Skill input',
  properties: {
    query: {
      type: 'string',
      description: 'User query',
    },
    originalQuery: {
      type: 'string',
      description: 'Original user query',
    },
    images: {
      type: 'array',
      description: 'Image list (storage keys)',
      deprecated: true,
      items: {
        type: 'string',
      },
    },
  },
} as const;

export const SkillContextResourceItemSchema = {
  type: 'object',
  description: 'Skill context resource item',
  properties: {
    resourceId: {
      type: 'string',
      description: 'Resource ID (if empty, this will be considered as external resource)',
    },
    resource: {
      description: 'Resource',
      $ref: '#/components/schemas/Resource',
    },
    isCurrent: {
      type: 'boolean',
      description: 'Whether this resource is current',
    },
    metadata: {
      type: 'object',
      description: 'Resource context metadata',
    },
  },
} as const;

export const SkillContextDocumentItemSchema = {
  type: 'object',
  description: 'Skill context document item',
  properties: {
    docId: {
      type: 'string',
      description: 'Document ID',
    },
    document: {
      description: 'Document',
      $ref: '#/components/schemas/Document',
    },
    isCurrent: {
      type: 'boolean',
      description: 'Whether this document is current',
    },
    metadata: {
      type: 'object',
      description: 'Canvas context metadata',
    },
  },
} as const;

export const SkillContextCodeArtifactItemSchema = {
  type: 'object',
  description: 'Skill context code artifact item',
  properties: {
    artifactId: {
      type: 'string',
      description: 'Artifact ID',
    },
    codeArtifact: {
      description: 'Code artifact',
      $ref: '#/components/schemas/CodeArtifact',
    },
    isCurrent: {
      type: 'boolean',
      description: 'Whether this code artifact is current',
    },
    metadata: {
      type: 'object',
      description: 'Code artifact context metadata',
    },
  },
} as const;

export const SkillContextContentItemSchema = {
  type: 'object',
  description: 'Skill context content item',
  required: ['content'],
  properties: {
    content: {
      type: 'string',
      description: 'Content',
    },
    metadata: {
      type: 'object',
      description: 'Content context metadata',
    },
  },
} as const;

export const SkillContextUrlItemSchema = {
  type: 'object',
  description: 'Skill context url item',
  required: ['url'],
  properties: {
    url: {
      type: 'string',
      description: 'URL',
    },
    metadata: {
      type: 'object',
      description: 'URL context metadata',
    },
  },
} as const;

export const SkillContextMediaItemSchema = {
  type: 'object',
  description: 'Skill context media item',
  required: ['mediaType', 'entityId', 'title', 'url', 'storageKey'],
  properties: {
    mediaType: {
      type: 'string',
      description: 'Media type',
      $ref: '#/components/schemas/MediaType',
    },
    entityId: {
      type: 'string',
      description: 'Media entity ID',
    },
    title: {
      type: 'string',
      description: 'Media title',
    },
    url: {
      type: 'string',
      description: 'Media URL',
    },
    storageKey: {
      type: 'string',
      description: 'Media storage key',
    },
    metadata: {
      type: 'object',
      description: 'Media context metadata',
    },
  },
} as const;

export const SkillContextFileItemSchema = {
  type: 'object',
  description: 'Skill context file item',
  required: ['fileId'],
  properties: {
    fileId: {
      type: 'string',
      description: 'File ID',
    },
    file: {
      description: 'File object',
      $ref: '#/components/schemas/DriveFile',
    },
    variableId: {
      type: 'string',
      description: 'Variable ID if this file is from a workflow variable',
    },
    variableName: {
      type: 'string',
      description: 'Variable name if this file is from a workflow variable',
    },
  },
} as const;

export const SkillContextResultItemSchema = {
  type: 'object',
  description: 'Skill context result item',
  properties: {
    resultId: {
      type: 'string',
      description: 'Result ID',
    },
    result: {
      description: 'Result',
      $ref: '#/components/schemas/ActionResult',
    },
  },
} as const;

export const SkillContextSchema = {
  type: 'object',
  description: 'Skill invocation context',
  properties: {
    resources: {
      type: 'array',
      description: 'Context resources',
      items: {
        $ref: '#/components/schemas/SkillContextResourceItem',
      },
    },
    documents: {
      type: 'array',
      description: 'Context documents',
      items: {
        $ref: '#/components/schemas/SkillContextDocumentItem',
      },
    },
    codeArtifacts: {
      type: 'array',
      description: 'Context code artifacts',
      items: {
        $ref: '#/components/schemas/SkillContextCodeArtifactItem',
      },
    },
    contentList: {
      type: 'array',
      description: 'Context content list',
      items: {
        $ref: '#/components/schemas/SkillContextContentItem',
      },
    },
    urls: {
      type: 'array',
      description: 'List of URLs',
      items: {
        $ref: '#/components/schemas/SkillContextUrlItem',
      },
      deprecated: true,
    },
    mediaList: {
      type: 'array',
      description: 'List of media',
      items: {
        $ref: '#/components/schemas/SkillContextMediaItem',
      },
    },
    files: {
      type: 'array',
      description: 'List of files',
      items: {
        $ref: '#/components/schemas/SkillContextFileItem',
      },
    },
    results: {
      type: 'array',
      description: 'List of results',
      items: {
        $ref: '#/components/schemas/SkillContextResultItem',
      },
    },
  },
} as const;

export const SelectionKeySchema = {
  type: 'string',
  enum: [
    'documentSelection',
    'resourceSelection',
    'skillResponseSelection',
    'extensionWeblinkSelection',
    'documentCursorSelection',
    'documentBeforeCursorSelection',
    'documentAfterCursorSelection',
  ],
} as const;

export const ActionTypeSchema = {
  type: 'string',
  enum: ['skill', 'tool', 'media'],
} as const;

export const AgentModeSchema = {
  type: 'string',
  enum: ['copilot_agent', 'node_agent'],
} as const;

export const InvokeSkillRequestSchema = {
  type: 'object',
  properties: {
    input: {
      description: 'Skill input',
      $ref: '#/components/schemas/SkillInput',
    },
    title: {
      description: 'Agent title',
      type: 'string',
    },
    context: {
      description: 'Skill invocation context',
      $ref: '#/components/schemas/SkillContext',
    },
    resultHistory: {
      type: 'array',
      description: 'Skill result history',
      items: {
        $ref: '#/components/schemas/ActionResult',
      },
      deprecated: true,
    },
    runtimeConfig: {
      description: 'Skill runtime config',
      $ref: '#/components/schemas/SkillRuntimeConfig',
      deprecated: true,
    },
    tplConfig: {
      description: 'Skill template config',
      $ref: '#/components/schemas/SkillTemplateConfig',
      deprecated: true,
    },
    skillName: {
      type: 'string',
      description: 'Skill name (if not provided, commonQnA will be used)',
      deprecated: true,
    },
    target: {
      description: 'Skill invocation target',
      $ref: '#/components/schemas/Entity',
    },
    projectId: {
      type: 'string',
      description: 'Project ID',
    },
    resultId: {
      type: 'string',
      description: `Result ID associated with this invocation.
1) If not provided, a new resultId will be generated.
2) If there is no existing result with this resultId, it will be created and run.
3) If there is an existing result with this resultId, it will be rerun.
`,
    },
    locale: {
      type: 'string',
      description: 'Selected output locale',
    },
    modelName: {
      type: 'string',
      description: 'Selected model',
      deprecated: true,
    },
    modelItemId: {
      type: 'string',
      description: 'Selected model item ID',
    },
    skillId: {
      type: 'string',
      description: 'Skill ID',
    },
    triggerId: {
      type: 'string',
      description: 'Trigger ID',
    },
    selectedMcpServers: {
      type: 'array',
      description: 'Selected MCP servers (deprecated, use `tools` instead)',
      items: {
        type: 'string',
      },
      deprecated: true,
    },
    toolsets: {
      type: 'array',
      description: 'Selected toolsets',
      items: {
        $ref: '#/components/schemas/GenericToolset',
      },
    },
    mode: {
      description: 'Agent mode',
      $ref: '#/components/schemas/AgentMode',
      default: 'node_agent',
    },
    copilotSessionId: {
      type: 'string',
      description: 'Copilot session ID',
    },
    workflowExecutionId: {
      type: 'string',
      description: 'Workflow execution ID for workflow context',
    },
    workflowNodeExecutionId: {
      type: 'string',
      description: 'Workflow node execution ID for workflow context',
    },
  },
} as const;

export const InvokeSkillResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        resultId: {
          type: 'string',
          description: 'Skill result ID',
        },
      },
    },
  ],
} as const;

export const ListSkillTriggerResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Skill trigger list',
          items: {
            $ref: '#/components/schemas/SkillTrigger',
          },
        },
      },
    },
  ],
} as const;

export const SkillTriggerCreateParamSchema = {
  type: 'object',
  required: ['skillId', 'displayName', 'triggerType'],
  properties: {
    skillId: {
      type: 'string',
      description: 'Skill ID',
      example: 'sk-g30e1b80b5g1itbemc0g5jj3',
    },
    displayName: {
      type: 'string',
      description: 'Trigger display name',
      example: 'My trigger',
    },
    triggerType: {
      description: 'Trigger type',
      $ref: '#/components/schemas/SkillTriggerType',
    },
    simpleEventName: {
      description: 'Simple event name (only required when trigger type is `simpleEvent`)',
      $ref: '#/components/schemas/SimpleEventName',
    },
    timerConfig: {
      description: 'Timer config (only required when trigger type is `timer`)',
      $ref: '#/components/schemas/TimerTriggerConfig',
    },
    input: {
      description: 'Skill input',
      $ref: '#/components/schemas/SkillInput',
    },
    context: {
      description: 'Skill invocation context',
      $ref: '#/components/schemas/SkillContext',
    },
    tplConfig: {
      description: 'Skill template config',
      $ref: '#/components/schemas/SkillTemplateConfig',
    },
    enabled: {
      type: 'boolean',
      description: 'Whether this trigger is enabled',
    },
  },
} as const;

export const CreateSkillTriggerRequestSchema = {
  type: 'object',
  required: ['triggerList'],
  properties: {
    triggerList: {
      type: 'array',
      description: 'Skill triggers to upsert',
      items: {
        $ref: '#/components/schemas/SkillTriggerCreateParam',
      },
    },
  },
} as const;

export const CreateSkillTriggerResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Skill trigger list',
          items: {
            $ref: '#/components/schemas/SkillTrigger',
          },
        },
      },
    },
  ],
} as const;

export const UpdateSkillTriggerRequestSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/SkillTriggerCreateParam',
    },
    {
      type: 'object',
      required: ['triggerId'],
      properties: {
        triggerId: {
          type: 'string',
          description: 'Trigger ID',
          example: 'tr-g30e1b80b5g1itbemc0g5jj3',
        },
      },
    },
  ],
} as const;

export const UpdateSkillTriggerResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: 'Updated skill trigger',
          $ref: '#/components/schemas/SkillTrigger',
        },
      },
    },
  ],
} as const;

export const DeleteSkillTriggerRequestSchema = {
  type: 'object',
  required: ['triggerId'],
  properties: {
    triggerId: {
      type: 'string',
      description: 'Trigger ID to delete',
    },
  },
} as const;

export const MediaTypeSchema = {
  type: 'string',
  description: 'media type',
  enum: ['image', 'video', 'audio'],
  example: 'image',
} as const;

export const MediaGenerateRequestSchema = {
  type: 'object',
  required: ['mediaType', 'prompt'],
  properties: {
    mediaType: {
      $ref: '#/components/schemas/MediaType',
    },
    model: {
      type: 'string',
      description: 'Model name for content generation',
    },
    title: {
      type: 'string',
      description: 'Title of the generated media',
    },
    targetType: {
      description: 'Target type',
      $ref: '#/components/schemas/EntityType',
    },
    targetId: {
      type: 'string',
      description: 'Target ID',
    },
    providerItemId: {
      type: 'string',
      description: 'Provider item ID',
    },
    provider: {
      type: 'string',
      description: 'Optional provider selection (use providerItemId instead)',
      nullable: true,
    },
    prompt: {
      type: 'string',
      description: 'Text prompt for content generation',
    },
    wait: {
      type: 'boolean',
      description: 'Whether to wait for the generation to complete',
      default: false,
    },
    resultId: {
      type: 'string',
      description: 'Media generation result ID',
    },
    parentResultId: {
      type: 'string',
      description: 'Parent result ID for the media generation (usually the actor agent result ID)',
    },
    parentResultVersion: {
      type: 'number',
      description:
        'Parent result version for the media generation (usually the actor agent result version)',
    },
    apiKey: {
      type: 'string',
      description: 'API key for the provider',
    },
    inputParameters: {
      type: 'array',
      description: 'Input parameter configurations',
      items: {
        $ref: '#/components/schemas/MediaModelParameter',
      },
    },
    input: {
      type: 'object',
      description: 'Flexible key-value pairs for additional input parameters',
      additionalProperties: true,
    },
    unitCost: {
      type: 'number',
      description: 'Unit cost for the media generation',
    },
  },
} as const;

export const MediaGenerationResultSchema = {
  type: 'object',
  required: ['resultId'],
  properties: {
    resultId: {
      type: 'string',
      description: 'Media generation result ID',
      example: 'ar-g30e1b80b5g1itbemc0g5jj3',
    },
    file: {
      $ref: '#/components/schemas/DriveFile',
      description: 'Generated file',
    },
    outputUrl: {
      type: 'string',
      description: 'Media generation output URL (only available when `wait` is true)',
      deprecated: true,
    },
    storageKey: {
      type: 'string',
      description: 'Media generation output storage key (only available when `wait` is true)',
      deprecated: true,
    },
    originalResult: {
      type: 'object',
      description: 'Media generation original result from provider',
    },
  },
} as const;

export const MediaGenerateResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/MediaGenerationResult',
        },
      },
    },
  ],
} as const;

export const FishAudioTextToSpeechRequestSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/MediaGenerateRequest',
    },
    {
      type: 'object',
      required: ['text'],
      properties: {
        text: {
          type: 'string',
          description: 'Text to convert to speech',
          example: 'Hello, welcome to Refly',
          minLength: 1,
          maxLength: 5000,
        },
        referenceId: {
          type: 'string',
          description: 'Voice model reference ID (optional, uses default if not specified)',
          example: '7f92f8afb8ec43bf81429cc1c9199cb1',
        },
        references: {
          type: 'array',
          description:
            'Reference audio files for voice cloning (alternative to referenceId). Provide uploaded audio files with optional transcripts.',
          items: {
            type: 'object',
            required: ['entityId'],
            properties: {
              entityId: {
                type: 'string',
                description: 'Entity ID of the uploaded audio file from file upload',
                example: 'r-vkwhup837t2jzicp87v8i6q5',
              },
              text: {
                type: 'string',
                description:
                  'Optional transcript text of the audio. If not provided, will be automatically transcribed using speech-to-text.',
                example: 'Hello, this is a sample voice recording',
              },
            },
          },
        },
        format: {
          type: 'string',
          description: 'Output audio format',
          enum: ['mp3', 'wav', 'opus', 'pcm'],
          default: 'mp3',
          example: 'mp3',
        },
        mp3Bitrate: {
          type: 'integer',
          description: 'MP3 bitrate (only for mp3 format)',
          enum: [64, 128, 192],
          default: 128,
          example: 128,
        },
        sampleRate: {
          type: 'integer',
          description: 'Sample rate in Hz',
          example: 44100,
        },
        temperature: {
          type: 'number',
          description: 'Controls randomness (0-1)',
          minimum: 0,
          maximum: 1,
          default: 0.9,
          example: 0.9,
        },
        topP: {
          type: 'number',
          description: 'Nucleus sampling diversity control (0-1)',
          minimum: 0,
          maximum: 1,
          default: 0.9,
          example: 0.9,
        },
        normalize: {
          type: 'boolean',
          description: 'Whether to normalize audio',
          default: true,
        },
        chunkLength: {
          type: 'integer',
          description: 'Chunk length for processing',
          minimum: 100,
          maximum: 300,
          default: 200,
        },
        wait: {
          type: 'boolean',
        },
      },
      description: 'Whether to wait for the generation to complete',
      default: false,
    },
  ],
} as const;

export const FishAudioTextToSpeechResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponseV2',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'TTS response data (present when status is success)',
          properties: {
            audioUrl: {
              type: 'string',
              format: 'uri',
              description: 'Public URL of the generated audio',
              example: 'https://nfyc.top/static/abc123.mp3',
            },
            entityId: {
              type: 'string',
              description: 'Entity ID of the generated audio file',
              example: 'r-vkwhup837t2jzicp87v8i6q5',
            },
            duration: {
              type: 'number',
              description: 'Audio duration in seconds',
              example: 5.2,
            },
            format: {
              type: 'string',
              description: 'Audio format',
              example: 'mp3',
            },
            size: {
              type: 'integer',
              description: 'File size in bytes',
              example: 102400,
            },
            resultId: {
              type: 'string',
              description: 'Audio generation result ID',
              example: 'ar-g30e1b80b5g1itbemc0g5jj3',
            },
            storageKey: {
              type: 'string',
              description: 'Storage key for the generated audio (only available when wait is true)',
            },
          },
        },
      },
    },
  ],
} as const;

export const FishAudioSpeechToTextRequestSchema = {
  type: 'object',
  required: ['entityId'],
  properties: {
    entityId: {
      type: 'string',
      description: 'Entity ID of the uploaded audio file to transcribe',
      example: 'r-vkwhup837t2jzicp87v8i6q5',
    },
    language: {
      type: 'string',
      description: 'Language code for speech recognition (e.g., "zh", "en")',
      example: 'en',
    },
    ignoreTimestamps: {
      type: 'boolean',
      description: 'Whether to ignore timestamps (increases latency for audio shorter than 30s)',
      default: true,
    },
  },
} as const;

export const FishAudioSpeechToTextResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponseV2',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'STT response data (present when status is success)',
          properties: {
            text: {
              type: 'string',
              description: 'Transcribed text',
              example: 'Hello, welcome to Refly',
            },
            duration: {
              type: 'number',
              description: 'Audio duration in seconds',
              example: 5.2,
            },
            segments: {
              type: 'array',
              description: 'Text segments with timestamps',
              items: {
                type: 'object',
                properties: {
                  text: {
                    type: 'string',
                    description: 'Segment text',
                  },
                  start: {
                    type: 'number',
                    description: 'Start time in seconds',
                  },
                  end: {
                    type: 'number',
                    description: 'End time in seconds',
                  },
                },
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const HeyGenGenerateVideoRequestSchema = {
  type: 'object',
  required: ['scenes'],
  properties: {
    scenes: {
      type: 'array',
      description: 'Video scenes (1-50 items)',
      minItems: 1,
      maxItems: 50,
      items: {
        type: 'object',
        required: ['voice'],
        properties: {
          character: {
            type: 'object',
            required: ['avatarId'],
            properties: {
              type: {
                type: 'string',
                enum: ['avatar', 'talking_photo'],
                default: 'avatar',
                description: 'Character type',
              },
              avatarId: {
                type: 'string',
                description: 'Avatar ID from HeyGen',
                example: 'avatar_123',
              },
              avatarStyle: {
                type: 'string',
                enum: ['normal', 'circle', 'closeUp'],
                default: 'normal',
                description: 'Avatar display style',
              },
              scale: {
                type: 'number',
                minimum: 0,
                maximum: 5,
                description: 'Avatar scale',
              },
              offset: {
                type: 'object',
                description: 'Avatar position offset',
                properties: {
                  x: {
                    type: 'number',
                  },
                  y: {
                    type: 'number',
                  },
                },
              },
            },
          },
          voice: {
            type: 'object',
            required: ['type'],
            properties: {
              type: {
                type: 'string',
                enum: ['text', 'audio', 'silence'],
                description: 'Voice type',
              },
              voiceId: {
                type: 'string',
                description: 'Voice ID (for text type)',
              },
              inputText: {
                type: 'string',
                description: 'Text to speak (for text type)',
              },
              audioUrl: {
                type: 'string',
                description: 'Audio URL (for audio type, lowest priority)',
              },
              storageKey: {
                type: 'string',
                description:
                  'Storage key of the audio file (format "static/{uuid}", for audio type, takes priority over audioUrl)',
              },
              speed: {
                type: 'number',
                minimum: 0.5,
                maximum: 1.5,
                description: 'Speech speed',
              },
              pitch: {
                type: 'number',
                minimum: -50,
                maximum: 50,
                description: 'Voice pitch',
              },
              emotion: {
                type: 'string',
                description: 'Voice emotion',
              },
            },
          },
          background: {
            type: 'object',
            description: 'Scene background',
            properties: {
              type: {
                type: 'string',
                enum: ['color', 'image', 'video'],
                description: 'Background type',
              },
              url: {
                type: 'string',
                description: 'Background image/video URL (lowest priority)',
              },
              storageKey: {
                type: 'string',
                description:
                  'Storage key of the background file (format "static/{uuid}", takes priority over url)',
              },
              color: {
                type: 'string',
                description: 'Background color (hex)',
                example: '#f6f6fc',
              },
              playStyle: {
                type: 'string',
                enum: ['freeze', 'loop', 'fit_to_scene', 'once'],
                default: 'fit_to_scene',
                description: 'Video playback mode (only for type "video")',
              },
            },
          },
        },
      },
    },
    dimension: {
      type: 'object',
      description: 'Video dimensions',
      properties: {
        width: {
          type: 'integer',
          example: 1280,
        },
        height: {
          type: 'integer',
          example: 720,
        },
      },
    },
    aspectRatio: {
      type: 'string',
      description: 'Video aspect ratio',
      example: '16:9',
    },
    test: {
      type: 'boolean',
      description: 'Test mode (adds watermark)',
      default: false,
    },
    title: {
      type: 'string',
      description: 'Video title',
    },
    callbackId: {
      type: 'string',
      description: 'Custom callback identifier',
    },
    caption: {
      type: 'boolean',
      description: 'Add captions',
      default: false,
    },
    parentResultId: {
      type: 'string',
      description: 'Parent action result ID for context inheritance',
    },
  },
} as const;

export const HeyGenGenerateVideoResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponseV2',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            videoId: {
              type: 'string',
              description: 'Generated video ID',
              example: 'video_abc123',
            },
            status: {
              type: 'string',
              enum: ['pending', 'processing', 'completed', 'failed'],
              description: 'Video generation status',
            },
            videoUrl: {
              type: 'string',
              format: 'uri',
              description: 'Video URL (when completed)',
            },
            thumbnailUrl: {
              type: 'string',
              format: 'uri',
              description: 'Thumbnail URL',
            },
            duration: {
              type: 'number',
              description: 'Video duration in seconds',
            },
            error: {
              type: 'object',
              description: 'Error details (when failed)',
              properties: {
                code: {
                  type: 'string',
                },
                message: {
                  type: 'string',
                },
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const SandboxExecuteParamsSchema = {
  type: 'object',
  required: ['code', 'language'],
  properties: {
    code: {
      type: 'string',
      description: 'Code to execute in the sandbox',
    },
    language: {
      type: 'string',
      description: 'Programming language for code execution',
      enum: ['python', 'javascript', 'shell'],
      example: 'python',
    },
  },
} as const;

export const SandboxExecuteContextSchema = {
  type: 'object',
  properties: {
    parentResultId: {
      type: 'string',
      description: 'Parent action result ID for context inheritance',
    },
    canvasId: {
      type: 'string',
      description: 'Canvas ID for context',
    },
    targetId: {
      type: 'string',
      description: 'Target entity ID',
    },
    targetType: {
      type: 'string',
      description: 'Target entity type',
    },
    model: {
      type: 'string',
      description: 'Model name for credit tracking',
    },
    providerItemId: {
      type: 'string',
      description: 'Provider item ID for credit tracking',
    },
    version: {
      type: 'number',
      description: 'Result version for file registration',
    },
  },
} as const;

export const SandboxExecuteRequestSchema = {
  type: 'object',
  required: ['params'],
  properties: {
    params: {
      $ref: '#/components/schemas/SandboxExecuteParams',
    },
    context: {
      $ref: '#/components/schemas/SandboxExecuteContext',
    },
  },
} as const;

export const SandboxExecuteResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponseV2',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: `Execution result data. Present when status is 'success'.
- exitCode=0: Code executed successfully
- exitCode!=0: Code error (syntax error, runtime exception, etc.)
`,
          properties: {
            output: {
              type: 'string',
              description: 'Combined output from stdout and execution result text',
            },
            error: {
              type: 'string',
              description:
                'Error message extracted from stderr/traceback (ANSI stripped, truncated if too long)',
            },
            exitCode: {
              type: 'integer',
              description: 'Exit code of the execution (0=success, non-zero=code error)',
            },
            executionTime: {
              type: 'integer',
              description: 'Execution time in milliseconds',
            },
            files: {
              type: 'array',
              description:
                'List of files generated by the code execution (available even when exitCode!=0)',
              items: {
                $ref: '#/components/schemas/DriveFile',
              },
            },
            warnings: {
              type: 'array',
              description:
                'System warnings from sandbox (e.g., file rename due to conflict, temporary directory creation)',
              items: {
                type: 'string',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const PilotStepStatusSchema = {
  type: 'string',
  enum: ['init', 'executing', 'finish', 'failed'],
} as const;

export const PilotStepSchema = {
  type: 'object',
  properties: {
    stepId: {
      type: 'string',
      description: 'Pilot step ID',
    },
    name: {
      type: 'string',
      description: 'Pilot step name',
    },
    epoch: {
      type: 'number',
      description: 'Pilot step epoch',
    },
    entityId: {
      type: 'string',
      description: 'Pilot step entity ID',
    },
    entityType: {
      type: 'string',
      description: 'Pilot step entity type',
    },
    status: {
      description: 'Pilot step status',
      $ref: '#/components/schemas/PilotStepStatus',
    },
    rawOutput: {
      type: 'string',
      description: 'Pilot step raw output',
    },
    actionResult: {
      description: 'Pilot step action result',
      $ref: '#/components/schemas/ActionResult',
    },
    createdAt: {
      type: 'string',
      description: 'Pilot step created at',
    },
    updatedAt: {
      type: 'string',
      description: 'Pilot step updated at',
    },
  },
} as const;

export const PilotSessionStatusSchema = {
  type: 'string',
  enum: ['init', 'executing', 'waiting', 'finish', 'failed'],
} as const;

export const PilotSessionSchema = {
  type: 'object',
  required: [
    'sessionId',
    'status',
    'targetType',
    'targetId',
    'title',
    'input',
    'currentEpoch',
    'maxEpoch',
  ],
  properties: {
    sessionId: {
      type: 'string',
      description: 'Pilot session ID',
    },
    title: {
      type: 'string',
      description: 'Pilot session title',
    },
    input: {
      description: 'Pilot session input',
      $ref: '#/components/schemas/SkillInput',
    },
    status: {
      description: 'Pilot session status',
      $ref: '#/components/schemas/PilotSessionStatus',
    },
    targetType: {
      description: 'Pilot session target type',
      $ref: '#/components/schemas/EntityType',
    },
    targetId: {
      type: 'string',
      description: 'Pilot session target ID',
    },
    currentEpoch: {
      type: 'number',
      description: 'Pilot session current epoch',
    },
    maxEpoch: {
      type: 'number',
      description: 'Pilot session max epoch',
    },
    steps: {
      type: 'array',
      description: 'Pilot steps',
      items: {
        $ref: '#/components/schemas/PilotStep',
      },
    },
    createdAt: {
      type: 'string',
      description: 'Pilot session created at',
    },
    updatedAt: {
      type: 'string',
      description: 'Pilot session updated at',
    },
  },
} as const;

export const CreatePilotSessionRequestSchema = {
  type: 'object',
  required: ['targetId', 'targetType', 'input'],
  properties: {
    targetId: {
      type: 'string',
      description: 'Pilot session target ID',
    },
    targetType: {
      description: 'Pilot session target type',
      $ref: '#/components/schemas/EntityType',
    },
    maxEpoch: {
      type: 'number',
      description: 'Pilot session max epoch',
      default: 2,
    },
    title: {
      type: 'string',
      description: 'Pilot session title',
    },
    input: {
      description: 'Pilot session input',
      $ref: '#/components/schemas/SkillInput',
    },
    providerItemId: {
      type: 'string',
      description: 'Pilot session provider item ID',
    },
  },
} as const;

export const UpdatePilotSessionRequestSchema = {
  type: 'object',
  required: ['sessionId'],
  properties: {
    sessionId: {
      type: 'string',
      description: 'Pilot session ID',
    },
    input: {
      description: 'Pilot session input',
      $ref: '#/components/schemas/SkillInput',
    },
    maxEpoch: {
      type: 'number',
      description: 'Pilot session max epoch',
      default: 2,
    },
  },
} as const;

export const RecoverPilotSessionRequestSchema = {
  type: 'object',
  required: ['sessionId'],
  properties: {
    sessionId: {
      type: 'string',
      description: 'Pilot session ID to recover',
    },
    stepIds: {
      type: 'array',
      description:
        'Optional array of specific step IDs to recover. If not provided, recovers all failed steps in the current epoch.',
      items: {
        type: 'string',
      },
    },
  },
} as const;

export const UpsertPilotSessionResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: 'Upserted pilot session',
          $ref: '#/components/schemas/PilotSession',
        },
      },
    },
  ],
} as const;

export const ListPilotSessionsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Pilot session list',
          items: {
            $ref: '#/components/schemas/PilotSession',
          },
        },
      },
    },
  ],
} as const;

export const GetPilotSessionDetailResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: 'Pilot session detail',
          $ref: '#/components/schemas/PilotSession',
        },
      },
    },
  ],
} as const;

export const CopilotSessionSchema = {
  type: 'object',
  properties: {
    sessionId: {
      type: 'string',
      description: 'Copilot session ID',
    },
    title: {
      type: 'string',
      description: 'Copilot session title',
    },
    canvasId: {
      type: 'string',
      description: 'Copilot session canvas ID',
    },
    source: {
      $ref: '#/components/schemas/CopilotSessionSource',
    },
    lastResultId: {
      type: 'string',
      description: '会话中最近一次执行结果 ID（仅列表接口返回）',
      example: 'ar-gmzylhvg9gkt031u2q5ceztu',
    },
    lastResultStatus: {
      $ref: '#/components/schemas/ActionStatus',
    },
    createdAt: {
      type: 'string',
      description: 'Copilot session created at',
    },
    updatedAt: {
      type: 'string',
      description: 'Copilot session updated at',
    },
    results: {
      type: 'array',
      description: 'Copilot session results (only returned in detail API)',
      items: {
        $ref: '#/components/schemas/ActionResult',
      },
    },
  },
} as const;

export const CopilotSessionSourceSchema = {
  type: 'string',
  description: 'Copilot 会话来源；canvas 为画布内 Copilot，workbench 为 AI 工作台',
  enum: ['canvas', 'workbench'],
} as const;

export const DeleteCopilotSessionRequestSchema = {
  type: 'object',
  required: ['sessionId'],
  properties: {
    sessionId: {
      type: 'string',
      description: 'Copilot 会话 ID',
      example: 'cs-a1b2c3d4e5f6g7h8i9j0k1l2',
    },
  },
} as const;

export const MaterializeCopilotSessionRequestSchema = {
  type: 'object',
  required: ['sessionId', 'resultId'],
  properties: {
    sessionId: {
      type: 'string',
      description: 'AI 工作台会话 ID',
      example: 'cs-a1b2c3d4e5f6g7h8i9j0k1l2',
    },
    resultId: {
      type: 'string',
      description: '生成或修改了工作流计划的执行结果 ID',
      example: 'ar-gmzylhvg9gkt031u2q5ceztu',
    },
  },
} as const;

export const MaterializedWorkflowSchema = {
  type: 'object',
  required: ['canvasId', 'created'],
  properties: {
    canvasId: {
      type: 'string',
      description: '工作流（画布）ID，前端据此拼接 /workflow/{canvasId} 链接',
      example: 'c-r57sg188kbn6e1ynnxsak1xf',
    },
    created: {
      type: 'boolean',
      description: '本次是否新建了画布；false 表示覆盖更新了会话已绑定的画布',
    },
    title: {
      type: 'string',
      description: '工作流标题',
      example: '用 GitHub 创建仓库',
    },
    nodeCount: {
      type: 'integer',
      description: '落画布后的节点数',
      minimum: 0,
    },
  },
} as const;

export const MaterializeCopilotSessionResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/MaterializedWorkflow',
        },
      },
    },
  ],
} as const;

export const ListCopilotSessionsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Copilot session list',
          items: {
            $ref: '#/components/schemas/CopilotSession',
          },
        },
      },
    },
  ],
} as const;

export const GetCopilotSessionDetailResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: 'Copilot session detail',
          $ref: '#/components/schemas/CopilotSession',
        },
      },
    },
  ],
} as const;

export const UpdateUserSettingsRequestSchema = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      description: 'User name',
      example: 'john_name',
    },
    nickname: {
      type: 'string',
      description: 'User nickname',
      example: 'John Doe',
    },
    avatar: {
      type: 'string',
      description: 'User avatar',
    },
    avatarStorageKey: {
      type: 'string',
      description: 'User avatar storage key',
    },
    uiLocale: {
      type: 'string',
      description: 'UI locale',
      example: 'en',
    },
    outputLocale: {
      type: 'string',
      description: 'Output locale',
      example: 'en',
    },
    preferences: {
      description: 'User preferences',
      $ref: '#/components/schemas/UserPreferences',
    },
    onboarding: {
      description: 'Onboarding config',
      $ref: '#/components/schemas/OnboardingConfig',
    },
  },
} as const;

export const CheckSettingsFieldResultSchema = {
  type: 'object',
  required: ['field', 'value', 'available'],
  properties: {
    field: {
      type: 'string',
      description: 'Settings field',
    },
    value: {
      type: 'string',
      description: 'Settings field value',
    },
    available: {
      type: 'boolean',
      description: 'Whether the field value is available',
    },
  },
} as const;

export const CheckSettingsFieldResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: 'Settings field check result',
          $ref: '#/components/schemas/CheckSettingsFieldResult',
        },
      },
    },
  ],
} as const;

export const CreateCheckoutSessionRequestSchema = {
  type: 'object',
  required: ['planType'],
  properties: {
    planType: {
      description: 'Subscription plan type',
      $ref: '#/components/schemas/SubscriptionPlanType',
    },
    interval: {
      description: 'Subscription billing interval',
      $ref: '#/components/schemas/SubscriptionInterval',
    },
    voucherId: {
      type: 'string',
      description: 'Optional voucher ID to apply discount',
    },
    voucherEntryPoint: {
      type: 'string',
      description:
        'Entry point where voucher was applied (e.g., claimed_popup, discount_popup, credit_insufficient_modal, pricing_page)',
    },
    voucherUserType: {
      type: 'string',
      description: 'User type when voucher was applied (e.g., new, returning)',
    },
    currentPlan: {
      type: 'string',
      description: 'Current plan',
    },
    source: {
      type: 'string',
      description: 'Source',
    },
  },
} as const;

export const CreateCreditPackCheckoutSessionRequestSchema = {
  type: 'object',
  required: ['packId'],
  properties: {
    packId: {
      type: 'string',
      description: 'Credit pack identifier',
    },
    currentPlan: {
      type: 'string',
      description: 'Current plan',
    },
    source: {
      type: 'string',
      description: 'Source',
    },
  },
} as const;

export const CreateCheckoutSessionResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Checkout session',
          properties: {
            url: {
              type: 'string',
              description: 'Checkout session URL',
            },
          },
        },
      },
    },
  ],
} as const;

export const CreatePortalSessionResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Portal session',
          properties: {
            url: {
              type: 'string',
              description: 'Portal session URL',
            },
          },
        },
      },
    },
  ],
} as const;

export const CreatePayOrderResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Payment order',
          properties: {
            payOrderId: {
              type: 'string',
              description: 'Payment order ID',
            },
            planType: {
              type: 'string',
              description: 'Plan type',
            },
            interval: {
              type: 'string',
              description: 'Billing interval',
            },
            amount: {
              type: 'number',
              description: 'Payment amount',
            },
            uid: {
              type: 'string',
              description: 'User ID',
            },
            checkoutUrl: {
              type: 'string',
              description: 'Checkout page URL',
            },
          },
        },
      },
    },
  ],
} as const;

export const GetPayOrderResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Payment order details',
          properties: {
            payOrderId: {
              type: 'string',
              description: 'Payment order ID',
            },
            uid: {
              type: 'string',
              description: 'User ID',
            },
            planType: {
              type: 'string',
              description: 'Plan type',
            },
            lookupKey: {
              type: 'string',
              description: 'Lookup key',
            },
            paymentStatus: {
              type: 'string',
              description: 'Payment status',
            },
            createdAt: {
              type: 'string',
              format: 'date-time',
              description: 'Order creation time',
            },
            error: {
              type: 'string',
              description: 'Error message if order not found',
            },
          },
        },
      },
    },
  ],
} as const;

export const SubmitFormRequestSchema = {
  type: 'object',
  required: ['formSubmission'],
  properties: {
    formSubmission: {
      description: 'Form submission',
      $ref: '#/components/schemas/FormSubmission',
    },
  },
} as const;

export const GetFormDefinitionResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Form definition',
          $ref: '#/components/schemas/FormDefinition',
        },
      },
    },
  ],
} as const;

export const HasFilledFormResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Has filled form data',
          properties: {
            hasFilledForm: {
              type: 'boolean',
              description: 'Whether the user has filled the form',
              default: false,
            },
            identity: {
              type: 'string',
              description: 'User identity',
            },
          },
        },
      },
    },
  ],
} as const;

export const GetCreditRechargeResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Credit recharge data with pagination',
          properties: {
            data: {
              type: 'array',
              description: 'Credit recharge list',
              items: {
                $ref: '#/components/schemas/CreditRecharge',
              },
            },
            total: {
              type: 'integer',
              description: 'Total number of records',
            },
            page: {
              type: 'integer',
              description: 'Current page number',
            },
            pageSize: {
              type: 'integer',
              description: 'Number of items per page',
            },
          },
        },
      },
    },
  ],
} as const;

export const GetCreditUsageResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Credit usage data with pagination',
          properties: {
            data: {
              type: 'array',
              description: 'Credit usage list',
              items: {
                $ref: '#/components/schemas/CreditUsage',
              },
            },
            total: {
              type: 'integer',
              description: 'Total number of records',
            },
            page: {
              type: 'integer',
              description: 'Current page number',
            },
            pageSize: {
              type: 'integer',
              description: 'Number of items per page',
            },
          },
        },
      },
    },
  ],
} as const;

export const getCreditBalanceResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Credit balance',
          properties: {
            creditBalance: {
              type: 'number',
              description: 'Credit balance',
            },
            creditAmount: {
              type: 'number',
              description: 'Credit amount',
            },
            regularCredits: {
              type: 'number',
              description: 'Regular credits (from purchases, gifts, subscriptions)',
            },
            templateEarningsCredits: {
              type: 'number',
              description: 'Template earnings credits',
            },
            cumulativeEarningsCredits: {
              type: 'number',
              description: 'Cumulative earnings credits',
            },
          },
        },
      },
    },
  ],
} as const;

export const GetCreditUsageByResultIdResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Credit usage by result ID',
          properties: {
            total: {
              type: 'number',
              description: 'Total credit usage by result ID',
            },
            usages: {
              type: 'array',
              description: 'Credit usage list by result ID',
              items: {
                $ref: '#/components/schemas/CreditUsage',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const GetCreditUsageByExecutionIdResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Credit usage by execution ID',
          properties: {
            total: {
              type: 'number',
              description: 'Total credit usage by execution ID',
            },
            usages: {
              type: 'array',
              description: 'Credit usage list by execution ID',
              items: {
                $ref: '#/components/schemas/CreditUsage',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const GetCreditUsageByCanvasIdResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Credit usage by canvas ID',
          properties: {
            total: {
              type: 'number',
              description: 'Total credit usage by canvas ID',
            },
            usages: {
              type: 'array',
              description: 'Credit usage list by canvas ID',
              items: {
                $ref: '#/components/schemas/CreditUsage',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const GetCanvasCommissionByCanvasIdResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Canvas commission by canvas ID',
          properties: {
            total: {
              type: 'number',
              description: 'Total canvas commission by canvas ID',
            },
          },
        },
      },
    },
  ],
} as const;

export const InvitationCodeSchema = {
  type: 'object',
  properties: {
    code: {
      type: 'string',
      description: 'Invitation code',
    },
    inviterUid: {
      type: 'string',
      description: 'Inviter UID',
    },
    inviteeUid: {
      type: 'string',
      description: 'Invitee UID',
    },
    status: {
      type: 'string',
      description: 'Invitation status',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Invitation creation time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Invitation update time',
    },
  },
} as const;

export const ActivateInvitationCodeRequestSchema = {
  type: 'object',
  properties: {
    code: {
      type: 'string',
      description: 'Invitation code',
    },
  },
} as const;

export const ListInvitationCodesResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Invitation code list',
          items: {
            $ref: '#/components/schemas/InvitationCode',
          },
        },
      },
    },
  ],
} as const;

export const HasBeenInvitedResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Has been invited data',
          properties: {
            hasBeenInvited: {
              type: 'boolean',
              description: 'Whether user has been invited',
              default: false,
            },
          },
        },
      },
    },
  ],
} as const;

export const SubscriptionPlanSchema = {
  type: 'object',
  properties: {
    planType: {
      type: 'string',
      description: 'Subscription plan type',
    },
    interval: {
      type: 'string',
      description: 'Billing interval (monthly, yearly, etc.)',
    },
    lookupKey: {
      type: 'string',
      description: 'Lookup key for the plan',
    },
    creditQuota: {
      type: 'number',
      description: 'Credit quota per month',
    },
    dailyGiftCreditQuota: {
      type: 'number',
      description: 'Daily gift credit quota',
    },
    t1TokenQuota: {
      type: 'number',
      description: 'Token quota per month (T1)',
    },
    t2TokenQuota: {
      type: 'number',
      description: 'Token quota per month (T2)',
    },
    objectStorageQuota: {
      type: 'string',
      description: 'Object storage quota (in bytes)',
    },
    vectorStorageQuota: {
      type: 'string',
      description: 'Vector storage quota (in bytes)',
    },
    price: {
      type: 'number',
      description: 'Price in cents',
    },
    enabled: {
      type: 'boolean',
      description: 'Whether this plan is enabled',
    },
  },
} as const;

export const GetSubscriptionPlansResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Subscription plans',
          items: {
            $ref: '#/components/schemas/SubscriptionPlan',
          },
        },
      },
    },
  ],
} as const;

export const SubscriptionUsageDataSchema = {
  type: 'object',
  properties: {
    token: {
      description: 'Token usage meter',
      $ref: '#/components/schemas/TokenUsageMeter',
    },
    storage: {
      description: 'Storage usage meter',
      $ref: '#/components/schemas/StorageUsageMeter',
    },
    fileParsing: {
      description: 'File parsing meter',
      $ref: '#/components/schemas/FileParsingMeter',
    },
  },
} as const;

export const GetSubscriptionUsageResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Subscription usage',
          $ref: '#/components/schemas/SubscriptionUsageData',
        },
      },
    },
  ],
} as const;

export const WebSearchRequestSchema = {
  type: 'object',
  properties: {
    q: {
      type: 'string',
      description: 'Search query',
    },
    hl: {
      type: 'string',
      description: 'Language/locale code',
    },
    gl: {
      type: 'string',
      description: 'Country/region code',
    },
    location: {
      type: 'string',
      description: 'Location string',
    },
    limit: {
      type: 'number',
      description: 'Web search result limit',
      default: 10,
    },
  },
} as const;

export const BatchWebSearchRequestSchema = {
  type: 'object',
  properties: {
    limit: {
      type: 'number',
      description: 'Web search result limit',
      default: 10,
    },
    queries: {
      type: 'array',
      description: 'Web search queries',
      items: {
        $ref: '#/components/schemas/WebSearchRequest',
      },
    },
  },
} as const;

export const MultiLingualWebSearchRequestSchema = {
  type: 'object',
  required: ['query', 'searchLocaleList'],
  properties: {
    query: {
      type: 'string',
      description: 'Search query',
    },
    searchLocaleList: {
      type: 'array',
      description: 'List of search locales',
      items: {
        type: 'string',
      },
    },
    displayLocale: {
      type: 'string',
      description: 'Display locale',
      default: 'auto',
    },
    searchLimit: {
      type: 'number',
      description: 'Web search result limit per locale',
    },
    enableRerank: {
      type: 'boolean',
      description: 'Whether to enable reranking of search results',
    },
    rerankLimit: {
      type: 'number',
      description: 'Limit for reranked results',
    },
    rerankRelevanceThreshold: {
      type: 'number',
      description: 'Relevance threshold for reranking',
    },
  },
} as const;

export const MultiLingualWebSearchResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'Multilingual web search results',
          required: ['sources', 'searchSteps'],
          properties: {
            sources: {
              type: 'array',
              description: 'Search result sources',
              items: {
                $ref: '#/components/schemas/Source',
              },
            },
            searchSteps: {
              type: 'array',
              description: 'Search steps',
              items: {
                $ref: '#/components/schemas/SearchStep',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const WebSearchResultSchema = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      description: 'Web search result name',
    },
    url: {
      type: 'string',
      description: 'Web search result url',
    },
    snippet: {
      type: 'string',
      description: 'Web search result snippet',
    },
    locale: {
      type: 'string',
      description: 'Web search result locale',
    },
  },
} as const;

export const WebSearchResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Web search results',
          items: {
            $ref: '#/components/schemas/WebSearchResult',
          },
        },
      },
    },
  ],
} as const;

export const RerankResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Reranked results',
          items: {
            $ref: '#/components/schemas/SearchResult',
          },
        },
      },
    },
  ],
} as const;

export const SearchOptionsSchema = {
  type: 'object',
  description: 'Search options for internal use',
  properties: {
    enableReranker: {
      type: 'boolean',
      description: 'Whether to enable reranker',
      default: true,
    },
  },
} as const;

export const SearchDomainSchema = {
  type: 'string',
  enum: ['resource', 'document', 'canvas'],
} as const;

export const SearchModeSchema = {
  type: 'string',
  enum: ['keyword', 'vector', 'hybrid'],
} as const;

export const SearchRequestSchema = {
  type: 'object',
  required: ['query'],
  properties: {
    query: {
      type: 'string',
      description: 'Search query (if empty, return last updated data)',
    },
    domains: {
      type: 'array',
      description: 'Search domains (if not specified, return all domains)',
      items: {
        $ref: '#/components/schemas/SearchDomain',
      },
    },
    entities: {
      type: 'array',
      description: 'Search entities',
      items: {
        $ref: '#/components/schemas/Entity',
      },
    },
    mode: {
      type: 'string',
      description: 'Search mode',
      $ref: '#/components/schemas/SearchMode',
      default: 'keyword',
    },
    projectId: {
      type: 'string',
      description: 'Project ID',
    },
    limit: {
      type: 'number',
      description: 'Search result limit for each domain',
      default: 5,
    },
  },
} as const;

export const SearchResultSnippetSchema = {
  type: 'object',
  properties: {
    text: {
      type: 'string',
      description: 'Search result content text',
    },
    highlightedText: {
      type: 'string',
      description: 'Search result highlighted content text with em html tags',
    },
  },
} as const;

export const SearchResultSchema = {
  type: 'object',
  required: ['id', 'domain', 'title'],
  properties: {
    id: {
      type: 'string',
      description: 'Search result ID to navigate to',
    },
    domain: {
      description: 'Search result domain',
      $ref: '#/components/schemas/SearchDomain',
    },
    title: {
      type: 'string',
      description: 'Search result title',
    },
    highlightedTitle: {
      type: 'string',
      description: 'Search result highlighted title with em html tags',
    },
    contentPreview: {
      type: 'string',
      description: 'Search result content preview',
    },
    snippets: {
      type: 'array',
      description: 'Search result content list with highlight marks',
      items: {
        $ref: '#/components/schemas/SearchResultSnippet',
      },
    },
    relevanceScore: {
      type: 'number',
      description: 'Search result relevance score',
    },
    metadata: {
      type: 'object',
      description: 'Search result metadata',
      additionalProperties: true,
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Data creation time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Data update time',
    },
  },
} as const;

export const SearchResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Search result',
          items: {
            $ref: '#/components/schemas/SearchResult',
          },
        },
      },
    },
  ],
} as const;

export const ScrapeWeblinkRequestSchema = {
  type: 'object',
  required: ['url'],
  properties: {
    url: {
      type: 'string',
      description: 'Weblink URL',
    },
  },
} as const;

export const ScrapeWeblinkResultSchema = {
  type: 'object',
  properties: {
    title: {
      type: 'string',
      description: 'Weblink title',
    },
    description: {
      type: 'string',
      description: 'Weblink description',
    },
    image: {
      type: 'string',
      description: 'Weblink image',
    },
  },
} as const;

export const ScrapeWeblinkResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: 'Weblink scrape result',
          $ref: '#/components/schemas/ScrapeWeblinkResult',
        },
      },
    },
  ],
} as const;

export const FileVisibilitySchema = {
  type: 'string',
  enum: ['public', 'private'],
} as const;

export const UploadRequestSchema = {
  type: 'object',
  required: ['file'],
  properties: {
    file: {
      type: 'string',
      format: 'binary',
      description: 'File to upload',
    },
    entityId: {
      type: 'string',
      description: 'Entity ID',
    },
    entityType: {
      description: 'Entity type',
      $ref: '#/components/schemas/EntityType',
    },
    visibility: {
      description: 'File visibility (default is private)',
      $ref: '#/components/schemas/FileVisibility',
    },
    storageKey: {
      type: 'string',
      description: 'Storage key (if provided, the file will be replaced if it already exists)',
    },
  },
} as const;

export const UploadResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: 'File upload result',
          required: ['url', 'storageKey'],
          properties: {
            url: {
              type: 'string',
              description: 'File URL',
            },
            storageKey: {
              type: 'string',
              description: 'Storage key',
            },
          },
        },
      },
    },
  ],
} as const;

export const ConvertRequestSchema = {
  type: 'object',
  required: ['file'],
  properties: {
    file: {
      type: 'string',
      format: 'binary',
      description: 'The file to convert',
    },
    from: {
      type: 'string',
      description: 'Source format (e.g., html)',
      default: 'html',
    },
    to: {
      type: 'string',
      description: 'Target format (e.g., markdown)',
      default: 'markdown',
    },
  },
} as const;

export const ConvertResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            content: {
              type: 'string',
              description: 'Converted markdown content',
            },
          },
        },
      },
    },
  ],
} as const;

export const PromptSuggestionSchema = {
  type: 'object',
  description: '提示词建议',
  required: ['prompt'],
  properties: {
    prompt: {
      type: 'object',
      description: '提示词（JSON 映射，键为语言代码，值为提示词）',
      additionalProperties: {
        type: 'string',
      },
    },
  },
} as const;

export const GetPromptSuggestionsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: '提示词建议列表',
          items: {
            $ref: '#/components/schemas/PromptSuggestion',
          },
        },
      },
    },
  ],
} as const;

export const MediaGenerationModelCapabilitiesSchema = {
  type: 'object',
  properties: {
    image: {
      type: 'boolean',
      description: 'Whether this model supports image generation',
    },
    video: {
      type: 'boolean',
      description: 'Whether this model supports video generation',
    },
    audio: {
      type: 'boolean',
      description: 'Whether this model supports audio generation',
    },
  },
} as const;

export const ModelCapabilitiesSchema = {
  type: 'object',
  properties: {
    functionCall: {
      type: 'boolean',
      description: 'Whether this model supports function calling',
    },
    vision: {
      type: 'boolean',
      description: 'Whether this model can take images as input',
    },
    multimodal: {
      type: 'boolean',
      description: 'Whether this model natively handles multiple input modalities',
    },
    reasoning: {
      type: 'boolean',
      description: 'Whether this model includes reasoning content',
    },
    contextCaching: {
      type: 'boolean',
      description: 'Whether this model supports context caching',
    },
    supportToolChoice: {
      type: 'boolean',
      description: 'Whether this model supports tool_choice parameter',
    },
    image: {
      type: 'boolean',
      description: 'Whether this model supports image generation',
    },
    video: {
      type: 'boolean',
      description: 'Whether this model supports video generation',
    },
    audio: {
      type: 'boolean',
      description: 'Whether this model supports audio generation',
    },
    workflowGeneration: {
      type: 'boolean',
      description: 'Whether this model supports workflow generation with nested tool schemas',
    },
  },
} as const;

export const ModelInfoSchema = {
  type: 'object',
  required: ['name', 'label', 'provider', 'contextLimit', 'maxOutput'],
  properties: {
    name: {
      type: 'string',
      description: 'Model name',
    },
    label: {
      type: 'string',
      description: 'Model label',
    },
    provider: {
      type: 'string',
      description: 'Model provider',
    },
    providerItemId: {
      type: 'string',
      description: 'Model provider item ID',
    },
    tier: {
      type: 'string',
      description: 'Model tier',
      $ref: '#/components/schemas/ModelTier',
    },
    contextLimit: {
      type: 'number',
      description: 'Model context limit (in tokens)',
    },
    maxOutput: {
      type: 'number',
      description: 'Model max output length (in tokens)',
    },
    capabilities: {
      description: 'Model capabilities',
      $ref: '#/components/schemas/ModelCapabilities',
    },
    isDefault: {
      type: 'boolean',
      description: 'Whether this model is the default model',
    },
    group: {
      type: 'string',
      description: 'Model group',
    },
    category: {
      type: 'string',
      description: 'Model category',
      $ref: '#/components/schemas/ProviderCategory',
    },
    creditBilling: {
      $ref: '#/components/schemas/CreditBilling',
      description: 'Credit billing info',
    },
    tooltip: {
      type: 'string',
      description: 'Tooltip text for the model (e.g., "Smart Routing")',
    },
    inputParameters: {
      type: 'array',
      description: 'Input parameter configurations',
      items: {
        $ref: '#/components/schemas/MediaModelParameter',
      },
    },
  },
} as const;

export const ListModelsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Model list',
          items: {
            $ref: '#/components/schemas/ModelInfo',
          },
        },
      },
    },
  ],
} as const;

export const ProviderCategorySchema = {
  type: 'string',
  enum: [
    'llm',
    'embedding',
    'reranker',
    'webSearch',
    'urlParsing',
    'pdfParsing',
    'mediaGeneration',
    'vision',
  ],
} as const;

export const ModelScopeSchema = {
  type: 'string',
  description: '模型归属：system 平台内置、user 当前用户自行添加',
  enum: ['system', 'user'],
  example: 'user',
} as const;

export const ModelSourceTypeSchema = {
  type: 'string',
  description: '添加来源：provider 从服务商目录选择、custom 自行填写服务地址',
  enum: ['provider', 'custom'],
  example: 'custom',
} as const;

export const ModelApiFormatSchema = {
  type: 'string',
  description:
    '请求协议格式：openai_chat_completions 走 /chat/completions、anthropic_messages 走 /messages',
  enum: ['openai_chat_completions', 'anthropic_messages'],
  example: 'openai_chat_completions',
} as const;

export const ModelTypeSchema = {
  type: 'string',
  description:
    '模型类型，是 category 与 capabilities 的唯一来源：language 语言、vision 视觉理解、multimodal 多模态、image_generation 图片生成、video_generation 视频生成',
  enum: ['language', 'image_generation', 'video_generation', 'multimodal', 'vision'],
  example: 'language',
} as const;

export const ModelListingSupportSchema = {
  type: 'string',
  description:
    '服务商 GET {baseUrl}/models 的可用程度：full 完整可用、partial 只列部分模型、none 不提供、unknown 未核实。非 full 时连通性测试只校验请求成功，不校验模型是否在列表内',
  enum: ['full', 'partial', 'none', 'unknown'],
  example: 'full',
} as const;

export const ModelMediaProtocolSchema = {
  type: 'string',
  description: '图片或视频生成所需的协议。各家路径与同步异步方式不同，本版本只实现了 openai_images',
  enum: [
    'openai_images',
    'openai_video',
    'zhipu_images',
    'zhipu_video_async',
    'dashscope_images',
    'dashscope_video_async',
    'volcengine_video_async',
    'minimax_video',
  ],
  example: 'openai_images',
} as const;

export const ModelCredentialFieldSchema = {
  type: 'object',
  description: '服务商需要用户填写的一项凭据',
  required: ['key', 'label', 'required', 'secret'],
  properties: {
    key: {
      type: 'string',
      description:
        '凭据字段名，提交时作为 credentials 的键；key 为 apiKey 时也可放在请求顶层的 apiKey',
      example: 'region',
    },
    label: {
      type: 'string',
      description: '表单展示的字段名称',
      example: '区域',
    },
    required: {
      type: 'boolean',
      description: '是否必填',
      example: true,
    },
    secret: {
      type: 'boolean',
      description:
        '是否为密钥类字段，前端应使用密码输入框；密钥加密存储，用户自己的模型在列表中回显明文供编辑回填，系统内置模型不回传',
      example: false,
    },
    placeholder: {
      type: 'string',
      description: '输入框占位提示',
      example: 'cn-beijing',
    },
  },
} as const;

export const ModelCatalogEntrySchema = {
  type: 'object',
  description: '服务商目录下的一个候选模型，仅作下拉候选，用户始终可以手填模型 ID',
  required: ['modelId', 'name', 'modelType'],
  properties: {
    modelId: {
      type: 'string',
      description: '请求时发给服务商的模型 ID',
      example: 'claude-sonnet-5',
    },
    name: {
      type: 'string',
      description: '下拉框展示名称',
      example: 'claude-sonnet-5',
    },
    modelType: {
      $ref: '#/components/schemas/ModelType',
    },
    contextLimit: {
      type: 'integer',
      minimum: 1,
      description: '已知的上下文长度默认值，用户留空时采用',
      example: 200000,
    },
    maxOutput: {
      type: 'integer',
      minimum: 1,
      description: '已知的最大输出长度默认值，用户留空时采用',
      example: 8192,
    },
    mediaProtocol: {
      $ref: '#/components/schemas/ModelMediaProtocol',
    },
    iconKey: {
      $ref: '#/components/schemas/ModelIconKey',
    },
    notes: {
      type: 'string',
      description: '该模型的特殊约定，例如只在特定端点可用',
      example: '仅订阅套餐端点可用',
    },
  },
} as const;

export const ModelIconKeySchema = {
  type: 'string',
  description: `模型或服务商 logo 标识，取值与 LobeHub Icons 的图标文件名一致（如 claude、qwen、deepseek、siliconcloud），前端据此映射到本地打包的 SVG。
模型列表中的值依次取记录上显式配置的 iconKey、按模型 ID 识别的模型系列、服务商目录的 logo，都识别不到时不返回。
取值集合会随目录扩充，前端遇到未知值或缺省时应回落到通用模型图标。`,
  example: 'claude',
} as const;

export const ModelBillingModeSchema = {
  type: 'string',
  description:
    '服务商的配置方式：pay_as_you_go 按量计费、coding_plan Coding Plan 订阅套餐、agent_plan Agent Plan 订阅套餐。套餐使用独立服务地址和套餐专属密钥，与按量计费密钥不能混用',
  enum: ['pay_as_you_go', 'coding_plan', 'agent_plan'],
  example: 'pay_as_you_go',
} as const;

export const ModelBillingModeOptionSchema = {
  type: 'object',
  description:
    '服务商的一种配置方式；每种方式有自己的服务地址、凭据字段和候选模型，切换配置方式时前端应改用这里的 credentialFields 与 models',
  required: ['key', 'name', 'baseUrlTemplate', 'credentialFields', 'models'],
  properties: {
    key: {
      $ref: '#/components/schemas/ModelBillingMode',
    },
    name: {
      type: 'string',
      description: '配置方式下拉项的展示名称',
      example: 'Coding Plan',
    },
    baseUrlTemplate: {
      type: 'string',
      description: '该配置方式的服务地址模板，规则同服务商的 baseUrlTemplate',
      example: 'https://api.z.ai/api/coding/paas/v4',
    },
    credentialFields: {
      type: 'array',
      description: '该配置方式需要填写的凭据字段',
      items: {
        $ref: '#/components/schemas/ModelCredentialField',
      },
    },
    models: {
      type: 'array',
      description: '该配置方式的候选模型清单；套餐通常只含部分语言模型',
      items: {
        $ref: '#/components/schemas/ModelCatalogEntry',
      },
    },
    modelListUrl: {
      type: 'string',
      description:
        '该配置方式的模型列表页，供“前往服务商平台查询”跳转；缺省时使用服务商的 modelListUrl',
      example: 'https://docs.z.ai/devpack/overview',
    },
    notes: {
      type: 'string',
      description: '该配置方式的特殊约定',
      example: '需使用套餐专属密钥',
    },
  },
} as const;

export const ModelProviderCatalogEntrySchema = {
  type: 'object',
  description: '模型服务商目录的一个条目，由后端静态维护，新增 OpenAI 兼容服务商不需要改运行时',
  required: [
    'key',
    'name',
    'iconKey',
    'apiFormat',
    'baseUrlTemplate',
    'credentialFields',
    'supportsModelListing',
    'models',
  ],
  properties: {
    key: {
      type: 'string',
      description: '服务商标识，添加模型时按此值提交',
      example: 'openai',
    },
    name: {
      type: 'string',
      description: '下拉框展示的服务商名称',
      example: 'OpenAI',
    },
    iconKey: {
      $ref: '#/components/schemas/ModelIconKey',
    },
    apiFormat: {
      $ref: '#/components/schemas/ModelApiFormat',
    },
    baseUrlTemplate: {
      type: 'string',
      description:
        '服务地址模板，可含 {region}、{resource}、{workspaceId} 占位符，由用户填写的同名凭据渲染',
      example: 'https://api.openai.com/v1',
    },
    credentialFields: {
      type: 'array',
      description: '该服务商需要填写的凭据字段',
      items: {
        $ref: '#/components/schemas/ModelCredentialField',
      },
    },
    supportsModelListing: {
      $ref: '#/components/schemas/ModelListingSupport',
    },
    models: {
      type: 'array',
      description: '候选模型清单',
      items: {
        $ref: '#/components/schemas/ModelCatalogEntry',
      },
    },
    imageProtocol: {
      $ref: '#/components/schemas/ModelMediaProtocol',
    },
    videoProtocol: {
      $ref: '#/components/schemas/ModelMediaProtocol',
    },
    notes: {
      type: 'string',
      description: '该服务商的特殊约定，例如订阅套餐使用独立端点',
      example: '订阅套餐需改用独立端点，否则扣的是余额而非套餐额度',
    },
    modelListUrl: {
      type: 'string',
      description:
        '服务商的模型列表页，供选择“使用其他模型”后的“前往服务商平台查询”跳转；未收录时不返回',
      example: 'https://platform.openai.com/docs/models',
    },
    billingModes: {
      type: 'array',
      description: `可选的配置方式，第一项为默认值（按量计费）。返回时前端在服务商下方展示“配置方式”下拉；不返回表示只有按量计费，不展示该下拉。
顶层的 baseUrlTemplate、credentialFields、models 与按量计费一项相同。`,
      items: {
        $ref: '#/components/schemas/ModelBillingModeOption',
      },
    },
  },
} as const;

export const ModelFormatOptionSchema = {
  type: 'object',
  description: '自定义配置可选的 API 格式',
  required: ['key', 'name', 'endpointPath'],
  properties: {
    key: {
      $ref: '#/components/schemas/ModelApiFormat',
    },
    name: {
      type: 'string',
      description: '展示名称',
      example: 'OpenAI Chat Completions 格式',
    },
    endpointPath: {
      type: 'string',
      description: '会自动拼到用户填写的服务地址末尾的请求路径',
      example: '/chat/completions',
    },
    hint: {
      type: 'string',
      description: '服务地址输入框下方的填写提示',
      example: '请填写与 OpenAI API 兼容的服务端点地址，不要以斜杠结尾。',
    },
  },
} as const;

export const ModelTypeOptionSchema = {
  type: 'object',
  description: '模型类型下拉项',
  required: ['key', 'name'],
  properties: {
    key: {
      $ref: '#/components/schemas/ModelType',
    },
    name: {
      type: 'string',
      description: '展示名称',
      example: '语言模型',
    },
  },
} as const;

export const ModelOptionsSchema = {
  type: 'object',
  description: '模型管理页面所需的全部静态选项',
  required: ['providers', 'customFormats', 'modelTypes'],
  properties: {
    providers: {
      type: 'array',
      description: '模型服务商目录',
      items: {
        $ref: '#/components/schemas/ModelProviderCatalogEntry',
      },
    },
    customFormats: {
      type: 'array',
      description: '自定义配置可选的 API 格式',
      items: {
        $ref: '#/components/schemas/ModelFormatOption',
      },
    },
    modelTypes: {
      type: 'array',
      description: '模型类型下拉项',
      items: {
        $ref: '#/components/schemas/ModelTypeOption',
      },
    },
    supportedMediaProtocols: {
      type: 'array',
      description: '本版本已实现运行时适配的媒体生成协议；不在其中的协议添加时会被拒绝',
      items: {
        $ref: '#/components/schemas/ModelMediaProtocol',
      },
    },
  },
} as const;

export const ManagedModelSchema = {
  type: 'object',
  description: '模型管理列表中的一条模型记录',
  required: [
    'itemId',
    'modelId',
    'name',
    'enabled',
    'scope',
    'sourceType',
    'providerKey',
    'providerName',
    'hasApiKey',
  ],
  properties: {
    itemId: {
      type: 'string',
      description: '模型记录 ID，修改、启停和删除都用它',
      example: 'pi-s6yk47gxa1cxmyerxnmiv9is',
    },
    modelId: {
      type: 'string',
      description: '请求时发给服务商的模型 ID',
      example: 'claude-haiku-4-5',
    },
    name: {
      type: 'string',
      description: '模型展示名称',
      example: 'claude-haiku-4-5',
    },
    enabled: {
      type: 'boolean',
      description: '是否启用',
      example: true,
    },
    scope: {
      $ref: '#/components/schemas/ModelScope',
    },
    sourceType: {
      $ref: '#/components/schemas/ModelSourceType',
    },
    modelType: {
      $ref: '#/components/schemas/ModelType',
    },
    apiFormat: {
      $ref: '#/components/schemas/ModelApiFormat',
    },
    providerKey: {
      type: 'string',
      description: '底层服务商标识',
      example: 'openai',
    },
    providerName: {
      type: 'string',
      description: '服务商显示名，作为模型卡片副标题；自定义配置取服务地址主机名',
      example: 'shuaiapi',
    },
    billingMode: {
      $ref: '#/components/schemas/ModelBillingMode',
    },
    iconKey: {
      $ref: '#/components/schemas/ModelIconKey',
    },
    baseUrl: {
      type: 'string',
      description: '服务地址',
      example: 'https://api.shuaiapi.com/v1',
    },
    hasApiKey: {
      type: 'boolean',
      description: '是否已配置密钥',
      example: true,
    },
    apiKey: {
      type: 'string',
      description: 'API 密钥明文，供编辑表单回显；仅用户自己的模型返回，系统内置模型永不返回',
      example: 'sk-xxxxxxxx',
    },
    credentials: {
      type: 'object',
      description:
        '其他凭据的当前取值，供编辑表单回填；用户自己的模型会包含 accessKeyId、secretAccessKey 等密钥类字段明文，系统内置模型只含非密钥字段',
      additionalProperties: {
        type: 'string',
      },
      example: {
        region: 'cn-beijing',
      },
    },
    contextLimit: {
      type: 'integer',
      minimum: 1,
      description: '上下文长度',
      example: 200000,
    },
    maxOutput: {
      type: 'integer',
      minimum: 1,
      description: '最大输出长度',
      example: 8192,
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '创建时间',
      example: '2026-09-22T10:11:02Z',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: '最后更新时间',
      example: '2026-09-22T10:11:36Z',
    },
  },
} as const;

export const CreateManagedModelRequestSchema = {
  description: '添加模型请求，按 sourceType 区分服务商目录与自定义配置两条路径',
  oneOf: [
    {
      $ref: '#/components/schemas/CreateProviderManagedModelRequest',
    },
    {
      $ref: '#/components/schemas/CreateCustomManagedModelRequest',
    },
  ],
  discriminator: {
    propertyName: 'sourceType',
    mapping: {
      provider: '#/components/schemas/CreateProviderManagedModelRequest',
      custom: '#/components/schemas/CreateCustomManagedModelRequest',
    },
  },
} as const;

export const CreateProviderManagedModelRequestSchema = {
  type: 'object',
  description: '从服务商目录添加模型；服务地址由目录的 baseUrlTemplate 渲染得到，无需填写',
  required: ['sourceType', 'providerKey', 'modelId', 'modelType'],
  properties: {
    sourceType: {
      type: 'string',
      enum: ['provider'],
      description: '固定为 provider',
      example: 'provider',
    },
    providerKey: {
      type: 'string',
      minLength: 1,
      maxLength: 64,
      description: '服务商标识，必须是 GET /v1/model/options 返回的某个 key',
      example: 'openai',
    },
    billingMode: {
      allOf: [
        {
          $ref: '#/components/schemas/ModelBillingMode',
        },
      ],
      description:
        '配置方式，须为该服务商 billingModes 中的某个 key；不传按按量计费。服务商没有 billingModes 时只接受 pay_as_you_go，否则返回 MODEL_INVALID_REQUEST',
    },
    modelId: {
      type: 'string',
      minLength: 1,
      maxLength: 255,
      description: '模型 ID，可从目录候选中选择，也可手填',
      example: 'claude-haiku-4-5',
    },
    modelType: {
      $ref: '#/components/schemas/ModelType',
    },
    apiKey: {
      type: 'string',
      minLength: 1,
      maxLength: 4096,
      description:
        'API 密钥，与 credentials.apiKey 等价；仅用于 credentialFields 含 apiKey 的服务商，AWS 不接受',
      example: 'sk-xxxxxxxx',
    },
    credentials: {
      type: 'object',
      description: `按 GET /v1/model/options 中该服务商（选了配置方式时按该配置方式）的 credentialFields 逐项提交，键为字段的 key，只接受列出的键。
除 apiKey 外的密钥类字段（secret=true）也放在这里，例如 AWS 的 accessKeyId、secretAccessKey；非密钥字段如 region、resource、workspaceId 用于渲染服务地址。
AWS 只支持 Access Key：accessKeyId（访问密钥）+ secretAccessKey（私钥）+ region（区域）均必填；不接受 apiKey、sessionToken，提交会返回 MODEL_INVALID_REQUEST。region 须为区域代码（如 us-east-1、ap-northeast-1），格式不符返回 MODEL_INVALID_REQUEST；必填项缺失返回 MODEL_MISSING_CREDENTIAL。`,
      additionalProperties: {
        type: 'string',
      },
      example: {
        region: 'us-east-1',
        accessKeyId: 'AKIAxxxxxxxxxxxxxxxx',
        secretAccessKey: 'xxxxxxxxxxxxxxxx',
      },
    },
    contextLimit: {
      type: 'integer',
      minimum: 1,
      maximum: 10000000,
      description: '上下文长度；留空时采用目录中的已知默认值',
      example: 200000,
    },
    maxOutput: {
      type: 'integer',
      minimum: 1,
      maximum: 1000000,
      description: '最大输出长度；留空时采用目录中的已知默认值',
      example: 8192,
    },
    enabled: {
      type: 'boolean',
      default: true,
      description: '是否立即启用',
      example: true,
    },
  },
} as const;

export const CreateCustomManagedModelRequestSchema = {
  type: 'object',
  description:
    '自定义配置添加模型，自行填写服务地址与协议格式；服务地址须填服务根地址，不能带具体请求终点，且不能指向内网',
  required: ['sourceType', 'apiFormat', 'baseUrl', 'modelId', 'modelType', 'apiKey'],
  properties: {
    sourceType: {
      type: 'string',
      enum: ['custom'],
      description: '固定为 custom',
      example: 'custom',
    },
    apiFormat: {
      $ref: '#/components/schemas/ModelApiFormat',
    },
    baseUrl: {
      type: 'string',
      minLength: 1,
      maxLength: 2048,
      description: '服务根地址，不要以斜杠结尾，也不要包含 /chat/completions 等请求终点',
      example: 'https://api.shuaiapi.com/v1',
    },
    modelId: {
      type: 'string',
      minLength: 1,
      maxLength: 255,
      description: '模型 ID',
      example: 'claude-haiku-4-5',
    },
    modelType: {
      $ref: '#/components/schemas/ModelType',
    },
    apiKey: {
      type: 'string',
      minLength: 1,
      maxLength: 4096,
      description: 'API 密钥',
      example: 'sk-xxxxxxxx',
    },
    contextLimit: {
      type: 'integer',
      minimum: 1,
      maximum: 10000000,
      description: '上下文长度',
      example: 200000,
    },
    maxOutput: {
      type: 'integer',
      minimum: 1,
      maximum: 1000000,
      description: '最大输出长度',
      example: 8192,
    },
    enabled: {
      type: 'boolean',
      default: true,
      description: '是否立即启用',
      example: true,
    },
  },
} as const;

export const UpdateManagedModelRequestSchema = {
  type: 'object',
  description: '修改模型请求；除 itemId 外只传需要改动的字段，不传 apiKey 表示沿用已保存的密钥',
  required: ['itemId'],
  properties: {
    itemId: {
      type: 'string',
      description: '模型记录 ID',
      example: 'pi-s6yk47gxa1cxmyerxnmiv9is',
    },
    providerKey: {
      type: 'string',
      minLength: 1,
      maxLength: 64,
      description: '服务商标识；仅服务商目录来源的模型可改',
      example: 'openai',
    },
    billingMode: {
      allOf: [
        {
          $ref: '#/components/schemas/ModelBillingMode',
        },
      ],
      description:
        '配置方式；仅服务商目录来源的模型可改，不传沿用已保存的值。套餐密钥与按量计费密钥不通用，切换时通常要同时提交新密钥',
    },
    apiFormat: {
      $ref: '#/components/schemas/ModelApiFormat',
    },
    baseUrl: {
      type: 'string',
      description: '服务根地址；仅自定义配置来源的模型可改',
      example: 'https://api.shuaiapi.com/v1',
    },
    modelId: {
      type: 'string',
      minLength: 1,
      maxLength: 255,
      description: '模型 ID',
      example: 'claude-haiku-4-5',
    },
    modelType: {
      $ref: '#/components/schemas/ModelType',
    },
    apiKey: {
      type: 'string',
      minLength: 1,
      maxLength: 4096,
      description:
        '新的 API 密钥；不传表示沿用已保存的密钥。传入后按 credentials 的规则整组替换已保存密钥',
      example: 'sk-xxxxxxxx',
    },
    credentials: {
      type: 'object',
      description: `需要更新的其他凭据，键规则与添加时相同。
只要提交任一密钥类字段（含顶层 apiKey），已保存的密钥整组替换，同组密钥须一并提交，例如 AWS 换 Access Key 时要同时传 accessKeyId 与 secretAccessKey。
只提交 region 等非密钥字段时沿用已保存的密钥；切换配置方式后新方式不需要的旧字段会被丢弃。`,
      additionalProperties: {
        type: 'string',
      },
      example: {
        region: 'us-west-2',
      },
    },
    contextLimit: {
      type: 'integer',
      minimum: 1,
      maximum: 10000000,
      nullable: true,
      description: '上下文长度；传 null 表示清空',
      example: 200000,
    },
    maxOutput: {
      type: 'integer',
      minimum: 1,
      maximum: 1000000,
      nullable: true,
      description: '最大输出长度；传 null 表示清空',
      example: 4096,
    },
    enabled: {
      type: 'boolean',
      description: '是否启用',
      example: true,
    },
  },
} as const;

export const UpdateManagedModelStatusRequestSchema = {
  type: 'object',
  description: '启用或停用模型请求',
  required: ['itemId', 'enabled'],
  properties: {
    itemId: {
      type: 'string',
      description: '模型记录 ID',
      example: 'pi-s6yk47gxa1cxmyerxnmiv9is',
    },
    enabled: {
      type: 'boolean',
      description: '目标状态，true 启用、false 停用',
      example: false,
    },
  },
} as const;

export const DeleteManagedModelRequestSchema = {
  type: 'object',
  description: '删除模型请求',
  required: ['itemId'],
  properties: {
    itemId: {
      type: 'string',
      description: '模型记录 ID',
      example: 'pi-s6yk47gxa1cxmyerxnmiv9is',
    },
  },
} as const;

export const ModelOptionsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/ModelOptions',
        },
      },
    },
  ],
} as const;

export const ListManagedModelsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: '模型列表，内置模型与用户模型合并返回',
          items: {
            $ref: '#/components/schemas/ManagedModel',
          },
        },
      },
    },
  ],
} as const;

export const ManagedModelResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/ManagedModel',
        },
      },
    },
  ],
} as const;

export const ProviderSchema = {
  type: 'object',
  description: 'General provider info',
  required: ['providerId', 'providerKey', 'categories', 'name', 'enabled'],
  properties: {
    providerId: {
      type: 'string',
      description: 'Provider ID',
    },
    providerKey: {
      type: 'string',
      description: 'Provider key',
    },
    name: {
      type: 'string',
      description: 'Provider name',
    },
    categories: {
      type: 'array',
      description: 'Provider categories',
      items: {
        $ref: '#/components/schemas/ProviderCategory',
      },
    },
    baseUrl: {
      type: 'string',
      description: 'Provider base URL',
    },
    enabled: {
      type: 'boolean',
      description: 'Whether the provider is enabled',
    },
    isGlobal: {
      type: 'boolean',
      description: 'Whether the provider is global',
    },
    apiKey: {
      type: 'string',
      description: 'Provider API key (this will never be exposed to the frontend)',
    },
  },
} as const;

export const LLMModelConfigSchema = {
  type: 'object',
  description: 'Provider config for LLMs',
  required: ['modelId', 'modelName'],
  properties: {
    modelId: {
      type: 'string',
      description: 'Model ID',
    },
    modelName: {
      type: 'string',
      description: 'Model name',
    },
    contextLimit: {
      type: 'number',
      description: 'Model context limit (in tokens)',
    },
    maxOutput: {
      type: 'number',
      description: 'Model max output length (in tokens)',
    },
    disallowTemperature: {
      type: 'boolean',
      description: 'Whether the model disallow setting custom temperature',
    },
    capabilities: {
      description: 'Model capabilities',
      $ref: '#/components/schemas/ModelCapabilities',
    },
    tooltip: {
      type: 'string',
      description: 'Tooltip text for the model (e.g., "Smart Routing")',
    },
  },
} as const;

export const MediaModelParameterSchema = {
  type: 'object',
  description: 'Media generation parameter configuration',
  required: ['name', 'type', 'required', 'visible'],
  properties: {
    name: {
      type: 'string',
      description: 'Parameter name',
    },
    type: {
      type: 'string',
      description: 'Parameter type',
      enum: ['url', 'text', 'option'],
    },
    value: {
      oneOf: [
        {
          type: 'string',
          description: 'String value for url/text type or option value',
        },
        {
          type: 'array',
          description: 'Array of URLs for url type',
          items: {
            type: 'string',
          },
        },
        {
          type: 'number',
          description: 'Numeric value for option type',
        },
        {
          type: 'integer',
          description: 'Integer value for option type',
        },
        {
          type: 'boolean',
          description: 'Boolean value for option type',
        },
      ],
    },
    options: {
      type: 'array',
      description: 'Available options for option type',
      items: {
        oneOf: [
          {
            type: 'string',
            description: 'String option',
          },
          {
            type: 'number',
            description: 'Numeric option',
          },
          {
            type: 'integer',
            description: 'Integer option',
          },
          {
            type: 'boolean',
            description: 'Boolean option',
          },
        ],
      },
    },
    description: {
      type: 'string',
      description: 'Parameter description',
    },
    required: {
      type: 'boolean',
      description: 'Whether this parameter is required',
    },
    visible: {
      type: 'boolean',
      description: 'Whether this parameter should be displayed in UI',
    },
  },
} as const;

export const MediaGenerationModelConfigSchema = {
  type: 'object',
  description: 'Provider config for media generation',
  required: ['modelId', 'modelName'],
  properties: {
    modelId: {
      type: 'string',
      description: 'Model ID',
    },
    modelName: {
      type: 'string',
      description: 'Model name',
    },
    capabilities: {
      description: 'Model capabilities',
      $ref: '#/components/schemas/MediaGenerationModelCapabilities',
    },
    description: {
      type: 'string',
      description: 'Model description',
    },
    tooltip: {
      type: 'string',
      description: 'Tooltip text for the model (e.g., "Smart Routing")',
    },
    supportedLanguages: {
      type: 'array',
      description: 'Supported languages for translation',
      items: {
        type: 'string',
        example: ['en', 'zh', 'ja', 'ko'],
      },
    },
    inputParameters: {
      type: 'array',
      description: 'Input parameter configurations',
      items: {
        $ref: '#/components/schemas/MediaModelParameter',
      },
    },
    outputParameters: {
      type: 'array',
      description: 'Output parameter configurations',
      items: {
        $ref: '#/components/schemas/MediaModelParameter',
      },
    },
    baseModel: {
      type: 'string',
      description: 'Base model for the model',
    },
  },
} as const;

export const EmbeddingModelConfigSchema = {
  type: 'object',
  description: 'Provider config for embeddings',
  required: ['modelId', 'dimensions'],
  properties: {
    modelId: {
      type: 'string',
      description: 'Embedding model ID',
    },
    modelName: {
      type: 'string',
      description: 'Embedding model name',
    },
    dimensions: {
      type: 'number',
      description: 'Embedding model dimension',
    },
    batchSize: {
      type: 'number',
      description: 'Embedding model batch size',
    },
  },
} as const;

export const RerankerModelConfigSchema = {
  type: 'object',
  description: 'Provider config for rerankers',
  required: ['modelId'],
  properties: {
    modelId: {
      type: 'string',
      description: 'Reranking model ID',
    },
    modelName: {
      type: 'string',
      description: 'Reranking model name',
    },
    topN: {
      type: 'number',
      description: 'Number of top results to return',
    },
    relevanceThreshold: {
      type: 'number',
      description: 'Minimum relevance score threshold (0.0-1.0)',
    },
  },
} as const;

export const ProviderItemConfigSchema = {
  oneOf: [
    {
      $ref: '#/components/schemas/LLMModelConfig',
    },
    {
      $ref: '#/components/schemas/EmbeddingModelConfig',
    },
    {
      $ref: '#/components/schemas/RerankerModelConfig',
    },
    {
      $ref: '#/components/schemas/MediaGenerationModelConfig',
    },
  ],
} as const;

export const CreditBillingSchema = {
  type: 'object',
  description: 'Credit billing configuration for provider items',
  required: ['unit', 'inputCost', 'outputCost', 'minCharge'],
  properties: {
    unit: {
      type: 'string',
      description: 'Measurement unit (e.g., token, product, second)',
      example: '5k_tokens',
    },
    inputCost: {
      type: 'number',
      description: 'Credit consumption per unit for input tokens',
      minimum: 0,
    },
    outputCost: {
      type: 'number',
      description: 'Credit consumption per unit for output tokens',
      minimum: 0,
    },
    cacheReadCost: {
      type: 'number',
      description:
        'Credit consumption per unit for cache read tokens (typically 10% of input cost)',
      minimum: 0,
    },
    cacheWriteCost: {
      type: 'number',
      description:
        'Credit consumption per unit for cache write tokens (typically higher than input cost)',
      minimum: 0,
    },
    minCharge: {
      type: 'number',
      description: 'Minimum credit charge per request',
      minimum: 0,
    },
    isEarlyBirdFree: {
      type: 'boolean',
      description: 'Whether this billing is free for early bird users',
      default: false,
    },
  },
} as const;

export const ProviderItemOptionSchema = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      description: 'Provider item name',
    },
    category: {
      description: 'Provider category',
      $ref: '#/components/schemas/ProviderCategory',
    },
    tier: {
      type: 'string',
      description: 'Provider item tier',
      $ref: '#/components/schemas/ModelTier',
    },
    config: {
      type: 'object',
      description: 'Provider item config',
      $ref: '#/components/schemas/ProviderItemConfig',
    },
  },
} as const;

export const ProviderItemSchema = {
  type: 'object',
  required: ['itemId', 'name', 'providerId', 'category', 'enabled'],
  properties: {
    itemId: {
      type: 'string',
      description: 'Provider item ID',
    },
    name: {
      type: 'string',
      description: 'Provider item name',
    },
    enabled: {
      type: 'boolean',
      description: 'Whether the provider item is enabled',
    },
    category: {
      description: 'Provider category',
      $ref: '#/components/schemas/ProviderCategory',
    },
    tier: {
      description: 'Provider item tier',
      $ref: '#/components/schemas/ModelTier',
    },
    providerId: {
      type: 'string',
      description: 'Provider ID',
    },
    provider: {
      type: 'object',
      description: 'Provider detail info',
      $ref: '#/components/schemas/Provider',
    },
    config: {
      description: 'Provider item config',
      $ref: '#/components/schemas/ProviderItemConfig',
    },
    order: {
      type: 'number',
      description: 'Provider item order',
    },
    group: {
      type: 'string',
      description: 'Provider item group',
    },
    creditBilling: {
      description: 'Credit billing info',
      $ref: '#/components/schemas/CreditBilling',
    },
  },
} as const;

export const CreditRechargeSchema = {
  type: 'object',
  description: 'Credit recharge record for user balance management',
  required: [
    'rechargeId',
    'uid',
    'amount',
    'balance',
    'enabled',
    'expiresAt',
    'createdAt',
    'updatedAt',
  ],
  properties: {
    rechargeId: {
      type: 'string',
      description: 'Unique recharge record ID',
    },
    uid: {
      type: 'string',
      description: 'User UID who owns this recharge record',
    },
    amount: {
      type: 'integer',
      description: 'Original recharge amount in credits',
      minimum: 0,
    },
    balance: {
      type: 'integer',
      description: 'Remaining balance for this recharge record',
      minimum: 0,
    },
    enabled: {
      type: 'boolean',
      description: 'Whether this recharge record is enabled (false after 30 days)',
      default: true,
    },
    source: {
      type: 'string',
      description: 'Recharge source type',
      enum: ['subscription', 'purchase', 'gift', 'promotion', 'refund', 'commission'],
      default: 'purchase',
    },
    description: {
      type: 'string',
      description: 'Optional description for this recharge',
    },
    appId: {
      type: 'string',
      description: 'Related app ID (if applicable)',
    },
    extraData: {
      type: 'string',
      description: 'Extra data for this recharge (JSON)',
      $ref: '#/components/schemas/CreditRechargeExtraData',
    },
    shareId: {
      type: 'string',
      description: 'Related share ID (if applicable)',
    },
    title: {
      type: 'string',
      description: 'Related share title (if applicable)',
    },
    expiresAt: {
      type: 'string',
      format: 'date-time',
      description: 'Expiration timestamp (30 days from creation)',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Record creation timestamp',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Record last update timestamp',
    },
  },
} as const;

export const CreditUsageSchema = {
  type: 'object',
  description: 'Credit usage record for tracking consumption',
  required: ['usageId', 'uid', 'amount', 'usageType', 'createdAt'],
  properties: {
    usageId: {
      type: 'string',
      description: 'Unique usage record ID',
    },
    uid: {
      type: 'string',
      description: 'User UID who consumed the credits',
    },
    amount: {
      type: 'integer',
      description: 'Amount of credits consumed',
      minimum: 0,
    },
    providerItemId: {
      type: 'string',
      description: 'Provider item ID that consumed the credits',
    },
    modelName: {
      type: 'string',
      description: 'Model name used for this consumption',
    },
    usageType: {
      type: 'string',
      description: 'Type of usage that consumed credits',
      enum: ['model_call', 'media_generation', 'embedding', 'reranking', 'commission', 'other'],
      default: 'model_call',
    },
    actionResultId: {
      type: 'string',
      description: 'Related action result ID (if applicable)',
    },
    pilotSessionId: {
      type: 'string',
      description: 'Related pilot session ID (if applicable)',
    },
    description: {
      type: 'string',
      description: 'Optional description for this usage',
    },
    modelUsageDetails: {
      type: 'string',
      description: 'Model usage details for skill execution (JSON array of model usage)',
    },
    appId: {
      type: 'string',
      description: 'Related app ID (if applicable)',
    },
    extraData: {
      type: 'string',
      description: 'Extra data for this usage (JSON)',
      $ref: '#/components/schemas/CreditUsageExtraData',
    },
    shareId: {
      type: 'string',
      description: 'Related share ID (if applicable)',
    },
    title: {
      type: 'string',
      description: 'Related share title (if applicable)',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Usage record creation timestamp',
    },
  },
} as const;

export const CreditRechargeExtraDataSchema = {
  type: 'object',
  description: 'Extra data for credit recharge',
  properties: {
    appId: {
      type: 'string',
      description: 'Related app ID',
    },
    executionId: {
      type: 'string',
      description: 'Related execution ID',
    },
    shareId: {
      type: 'string',
      description: 'Related share ID',
    },
    title: {
      type: 'string',
      description: 'Related share title',
    },
    commissionRate: {
      type: 'number',
      description: 'Commission rate',
    },
  },
} as const;

export const CreditUsageExtraDataSchema = {
  type: 'object',
  description: 'Extra data for credit usage',
  properties: {
    appId: {
      type: 'string',
      description: 'Related app ID (if applicable)',
    },
    executionId: {
      type: 'string',
      description: 'Related execution ID',
    },
    shareId: {
      type: 'string',
      description: 'Related share ID',
    },
    title: {
      type: 'string',
      description: 'Related share title',
    },
    commissionRate: {
      type: 'number',
      description: 'Commission rate',
    },
    toolsetKey: {
      type: 'string',
      description: 'Related toolset key',
    },
    toolsetName: {
      type: 'string',
      description: 'Related toolset name',
    },
    toolKey: {
      type: 'string',
      description: 'Related tool key',
    },
    toolName: {
      type: 'string',
      description: 'Related tool name',
    },
  },
} as const;

export const ListProvidersResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/Provider',
          },
        },
      },
    },
  ],
} as const;

export const UpsertProviderRequestSchema = {
  type: 'object',
  properties: {
    providerId: {
      type: 'string',
      description: 'Provider ID (only for update)',
    },
    providerKey: {
      type: 'string',
      description: 'Provider key',
    },
    name: {
      type: 'string',
      description: 'Provider name',
    },
    categories: {
      type: 'array',
      description: 'Provider categories',
      items: {
        $ref: '#/components/schemas/ProviderCategory',
      },
    },
    apiKey: {
      type: 'string',
      description: 'Provider API key',
    },
    baseUrl: {
      type: 'string',
      description: 'Provider base URL',
    },
    enabled: {
      type: 'boolean',
      description: 'Whether the provider is enabled',
    },
  },
} as const;

export const UpsertProviderResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/Provider',
        },
      },
    },
  ],
} as const;

export const DeleteProviderRequestSchema = {
  type: 'object',
  required: ['providerId'],
  properties: {
    providerId: {
      type: 'string',
      description: 'Provider ID',
    },
  },
} as const;

export const TestProviderConnectionRequestSchema = {
  type: 'object',
  required: ['providerId'],
  properties: {
    providerId: {
      type: 'string',
      description: 'Provider ID to test',
    },
    category: {
      description: 'Provider category to test (optional)',
      $ref: '#/components/schemas/ProviderCategory',
    },
  },
} as const;

export const ProviderTestResultSchema = {
  type: 'object',
  properties: {
    providerId: {
      type: 'string',
      description: 'Provider ID',
    },
    providerKey: {
      type: 'string',
      description: 'Provider key',
    },
    name: {
      type: 'string',
      description: 'Provider name',
    },
    baseUrl: {
      type: 'string',
      description: 'Provider base URL',
    },
    categories: {
      type: 'array',
      description: 'Provider categories',
      items: {
        type: 'string',
      },
    },
    status: {
      type: 'string',
      description: 'Test result status',
      enum: ['success', 'failed', 'unknown'],
    },
    message: {
      type: 'string',
      description: 'Test result message',
    },
    details: {
      type: 'object',
      description: 'Detailed test results',
      additionalProperties: true,
    },
    timestamp: {
      type: 'string',
      description: 'Test timestamp',
      format: 'date-time',
    },
  },
} as const;

export const TestProviderConnectionResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/ProviderTestResult',
        },
      },
    },
  ],
} as const;

export const ListProviderItemOptionsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/ProviderItemOption',
          },
        },
      },
    },
  ],
} as const;

export const ListProviderItemsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/ProviderItem',
          },
        },
      },
    },
  ],
} as const;

export const UpsertProviderItemRequestSchema = {
  type: 'object',
  properties: {
    itemId: {
      type: 'string',
      description: 'Provider item ID (only for update)',
    },
    providerId: {
      type: 'string',
      description: 'Provider ID',
    },
    globalItemId: {
      type: 'string',
      description: 'Global provider item ID',
    },
    name: {
      type: 'string',
      description: 'Provider item name',
    },
    category: {
      description: 'Provider category',
      $ref: '#/components/schemas/ProviderCategory',
    },
    enabled: {
      type: 'boolean',
      description: 'Whether the provider item is enabled',
    },
    config: {
      description: 'Provider item config',
      $ref: '#/components/schemas/ProviderItemConfig',
    },
    order: {
      type: 'number',
      description: 'Provider item order',
    },
    group: {
      type: 'string',
      description: 'Provider item group',
    },
  },
} as const;

export const UpsertProviderItemResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/ProviderItem',
        },
      },
    },
  ],
} as const;

export const BatchUpsertProviderItemsRequestSchema = {
  type: 'object',
  required: ['items'],
  properties: {
    items: {
      type: 'array',
      description: 'Provider items to upsert',
      items: {
        $ref: '#/components/schemas/UpsertProviderItemRequest',
      },
    },
  },
} as const;

export const BatchUpsertProviderItemsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Upserted provider items',
          items: {
            $ref: '#/components/schemas/ProviderItem',
          },
        },
      },
    },
  ],
} as const;

export const DeleteProviderItemRequestSchema = {
  type: 'object',
  required: ['itemId'],
  properties: {
    itemId: {
      type: 'string',
      description: 'Provider item ID',
    },
  },
} as const;

export const ToolsetAuthTypeSchema = {
  type: 'string',
  description: '工具集认证类型',
  enum: ['credentials', 'oauth', 'config_based'],
} as const;

export const ToolDefinitionSchema = {
  type: 'object',
  required: ['name', 'descriptionDict'],
  properties: {
    name: {
      type: 'string',
      description: '工具名称',
    },
    descriptionDict: {
      type: 'object',
      additionalProperties: true,
      description: '面向用户的工具多语言描述字典',
    },
    modelOnly: {
      type: 'boolean',
      description: '工具是否仅对模型可见',
    },
  },
} as const;

export const AuthPatternSchema = {
  type: 'object',
  required: ['type', 'pattern'],
  properties: {
    type: {
      type: 'string',
      description: '认证模式类型',
      $ref: '#/components/schemas/ToolsetAuthType',
    },
    credentialItems: {
      type: 'array',
      description: '凭据项，仅 `credentials` 类型使用',
      items: {
        $ref: '#/components/schemas/DynamicConfigItem',
      },
    },
    provider: {
      type: 'string',
      description: '认证提供方，仅 `oauth` 类型使用',
    },
    scope: {
      type: 'array',
      description: '认证权限范围，仅 `oauth` 类型使用',
      items: {
        type: 'string',
      },
    },
  },
} as const;

export const ToolsetDefinitionSchema = {
  type: 'object',
  required: ['key', 'labelDict', 'descriptionDict'],
  properties: {
    key: {
      type: 'string',
      description: '工具集键',
    },
    type: {
      $ref: '#/components/schemas/GenericToolsetType',
      description: '工具集类型（regular、mcp、external_oauth）',
    },
    builtin: {
      type: 'boolean',
      description: '是否为内置工具集',
    },
    internal: {
      type: 'boolean',
      description: '是否为内部工具集（不可手动选择）',
    },
    domain: {
      type: 'string',
      description: '工具集域名（用于显示图标）',
    },
    labelDict: {
      type: 'object',
      additionalProperties: true,
      description: '工具集多语言名称字典',
    },
    descriptionDict: {
      type: 'object',
      additionalProperties: true,
      description: '面向用户的工具集多语言描述字典',
    },
    tools: {
      type: 'array',
      description: '工具集工具列表',
      items: {
        $ref: '#/components/schemas/ToolDefinition',
      },
    },
    requiresAuth: {
      type: 'boolean',
      description: '工具集是否需要认证',
      default: false,
    },
    authPatterns: {
      type: 'array',
      description: '工具集认证模式',
      items: {
        $ref: '#/components/schemas/AuthPattern',
      },
    },
    configItems: {
      type: 'array',
      description: '工具集配置项',
      items: {
        $ref: '#/components/schemas/DynamicConfigItem',
      },
    },
  },
} as const;

export const ToolsetInstanceSchema = {
  type: 'object',
  required: ['toolsetId', 'name', 'key'],
  properties: {
    toolsetId: {
      type: 'string',
      description: '工具集 ID',
    },
    name: {
      type: 'string',
      description: '工具集名称',
    },
    key: {
      type: 'string',
      description: '工具集键',
    },
    isGlobal: {
      type: 'boolean',
      description: '工具集是否为全局工具集',
    },
    enabled: {
      type: 'boolean',
      description: '工具集是否启用',
    },
    authType: {
      $ref: '#/components/schemas/ToolsetAuthType',
      description: '工具集认证类型',
    },
    authData: {
      type: 'object',
      additionalProperties: true,
      description: '工具集认证数据',
    },
    config: {
      type: 'object',
      additionalProperties: true,
      description: '工具集配置',
    },
    definition: {
      description: '工具集定义',
      $ref: '#/components/schemas/ToolsetDefinition',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '工具集创建时间戳',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: '工具集更新时间戳',
    },
  },
} as const;

export const ListToolsetInventoryResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/ToolsetDefinition',
          },
        },
      },
    },
  ],
} as const;

export const ListToolsetsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/ToolsetInstance',
          },
        },
      },
    },
  ],
} as const;

export const UpsertToolsetRequestSchema = {
  type: 'object',
  properties: {
    toolsetId: {
      type: 'string',
      description: 'Toolset ID (only for update)',
    },
    name: {
      type: 'string',
      description: 'Toolset name',
    },
    key: {
      type: 'string',
      description: 'Toolset key',
    },
    enabled: {
      type: 'boolean',
      description: 'Whether the toolset is enabled',
    },
    authType: {
      $ref: '#/components/schemas/ToolsetAuthType',
      description: 'Toolset auth type',
    },
    authData: {
      type: 'object',
      additionalProperties: true,
      description: 'Toolset auth data',
    },
    config: {
      type: 'object',
      additionalProperties: true,
      description: 'Toolset config',
    },
    provider: {
      type: 'string',
      description: 'OAuth toolset provider',
    },
    scope: {
      type: 'array',
      description: 'OAuth toolset scope',
      items: {
        type: 'string',
      },
    },
  },
} as const;

export const UpsertToolsetResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/ToolsetInstance',
        },
      },
    },
  ],
} as const;

export const InitiateComposioConnectionResponseSchema = {
  type: 'object',
  required: ['redirectUrl', 'connectionRequestId', 'app'],
  properties: {
    redirectUrl: {
      type: 'string',
      format: 'uri',
      description: 'OAuth redirect URL provided by Composio.',
    },
    connectionRequestId: {
      type: 'string',
      description: 'Connection request identifier from Composio.',
    },
    app: {
      type: 'string',
      description: 'Composio app slug (e.g., gmail, slack).',
    },
  },
} as const;

export const ComposioConnectionStatusSchema = {
  type: 'string',
  description: 'Current status of the Composio connection.',
  enum: ['active', 'revoked'],
} as const;

export const ComposioConnectionStatusResponseSchema = {
  type: 'object',
  required: ['status', 'integrationId'],
  properties: {
    status: {
      $ref: '#/components/schemas/ComposioConnectionStatus',
    },
    connectedAccountId: {
      type: 'string',
      nullable: true,
      description: 'Connected account identifier returned by Composio, if available.',
    },
    integrationId: {
      type: 'string',
      description: 'Composio integration identifier (app slug).',
    },
  },
} as const;

export const ComposioRevokeResponseSchema = {
  type: 'object',
  required: ['success', 'message'],
  properties: {
    success: {
      type: 'boolean',
      description: 'Whether the connection was revoked successfully.',
    },
    message: {
      type: 'string',
      description: 'Human-readable message describing the outcome.',
    },
  },
} as const;

export const PostHandlerContextSchema = {
  type: 'object',
  description: 'Context for post-processing tool execution results',
  required: ['user', 'toolName', 'toolsetName', 'toolsetKey', 'creditCost'],
  properties: {
    user: {
      $ref: '#/components/schemas/User',
      description: 'User who executed the tool',
    },
    toolName: {
      type: 'string',
      description: 'Tool name that was executed',
    },
    toolsetName: {
      type: 'string',
      description: 'Toolset display name',
    },
    toolsetKey: {
      type: 'string',
      description: 'Toolset unique identifier/key',
    },
    creditCost: {
      type: 'number',
      description: 'Credit cost for this tool execution',
    },
    fileNameTitle: {
      type: 'string',
      description: 'File name title from input params',
    },
    resultId: {
      type: 'string',
      description: 'Result ID from execution context',
    },
    version: {
      type: 'number',
      description: 'Result version from execution context',
    },
  },
} as const;

export const PostHandlerResultSchema = {
  type: 'object',
  description: 'Result from post-processing tool execution',
  required: ['data'],
  properties: {
    data: {
      description: 'Processed data (may be uploaded to OSS)',
    },
    files: {
      type: 'array',
      description: 'Files uploaded during post-processing',
      items: {
        $ref: '#/components/schemas/DriveFile',
      },
    },
    creditCost: {
      type: 'number',
      description: 'Credit cost recorded',
    },
    metadata: {
      type: 'object',
      additionalProperties: true,
      description: 'Metadata about processing',
    },
  },
} as const;

export const ComposioConnectedAccountSchema = {
  type: 'object',
  description: 'Composio connected account structure from API response',
  required: ['id'],
  properties: {
    id: {
      type: 'string',
      description: 'Connected account ID',
    },
    status: {
      type: 'string',
      description: 'Connection status',
    },
    toolkit: {
      type: 'object',
      properties: {
        slug: {
          type: 'string',
          description: 'Toolkit slug identifier',
        },
      },
    },
  },
} as const;

export const ComposioToolSchemaSchema = {
  type: 'object',
  description: 'Composio tool JSON schema structure',
  properties: {
    type: {
      type: 'string',
      description: 'Schema type',
    },
    properties: {
      type: 'object',
      additionalProperties: {
        $ref: '#/components/schemas/ComposioSchemaProperty',
      },
      description: 'Schema properties',
    },
    required: {
      type: 'array',
      items: {
        type: 'string',
      },
      description: 'Required property names',
    },
  },
  additionalProperties: true,
} as const;

export const ComposioSchemaPropertySchema = {
  type: 'object',
  description: 'Composio schema property definition',
  properties: {
    type: {
      type: 'string',
      description: 'Property type',
    },
    description: {
      type: 'string',
      description: 'Property description',
    },
    deprecated: {
      type: 'boolean',
      description: 'Whether property is deprecated',
    },
  },
  additionalProperties: true,
} as const;

export const ToolCreationContextSchema = {
  type: 'object',
  description:
    'Context for creating a DynamicStructuredTool. User/userId comes from getCurrentUser() at runtime.',
  required: [
    'connectedAccountId',
    'authType',
    'creditCost',
    'toolsetType',
    'toolsetKey',
    'toolsetName',
  ],
  properties: {
    connectedAccountId: {
      type: 'string',
      description: 'Connected account ID from Composio',
    },
    authType: {
      type: 'string',
      enum: ['oauth', 'apikey'],
      description: 'Authentication type',
    },
    creditCost: {
      type: 'number',
      description: 'Credit cost for tool execution',
    },
    toolsetType: {
      $ref: '#/components/schemas/GenericToolsetType',
      description: 'Toolset type identifier',
    },
    toolsetKey: {
      type: 'string',
      description: 'Toolset key',
    },
    toolsetName: {
      type: 'string',
      description: 'Toolset display name',
    },
  },
} as const;

export const GenericToolsetTypeSchema = {
  type: 'string',
  enum: ['regular', 'mcp', 'external_oauth'],
} as const;

export const GenericToolsetSchema = {
  type: 'object',
  required: ['type', 'id', 'name'],
  properties: {
    type: {
      $ref: '#/components/schemas/GenericToolsetType',
      description: '工具集类型',
    },
    id: {
      type: 'string',
      description: '工具集 ID（普通工具集使用 toolsetId，MCP 工具集使用 name）',
    },
    name: {
      type: 'string',
      description: '工具集名称',
    },
    builtin: {
      type: 'boolean',
      description: '工具集是否为内置',
    },
    uninstalled: {
      type: 'boolean',
      description: '工具集是否已卸载',
    },
    toolset: {
      $ref: '#/components/schemas/ToolsetInstance',
      description: '工具集详情',
    },
    mcpServer: {
      $ref: '#/components/schemas/McpServerDTO',
      description: 'MCP 服务器',
    },
    selectedTools: {
      type: 'array',
      description: '已选工具（用于技能调用）',
      items: {
        type: 'string',
      },
    },
  },
} as const;

export const ListToolsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/GenericToolset',
          },
        },
      },
    },
  ],
} as const;

export const ListUserToolsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/UserTool',
          },
        },
      },
    },
  ],
} as const;

export const UserToolSchema = {
  type: 'object',
  properties: {
    toolsetId: {
      type: 'string',
      description: 'Toolset ID (toolsetId for installed, key for uninstalled)',
    },
    key: {
      type: 'string',
      description: 'Toolset key',
    },
    name: {
      type: 'string',
      description: 'Tool name',
    },
    description: {
      type: 'string',
      description: 'Tool description',
    },
    authorized: {
      type: 'boolean',
      description: 'Whether the tool is authorized/installed',
    },
    domain: {
      type: 'string',
      description: 'Tool domain for favicon',
    },
    toolset: {
      $ref: '#/components/schemas/GenericToolset',
      description: 'Full toolset data (only for authorized tools)',
    },
    definition: {
      $ref: '#/components/schemas/ToolsetDefinition',
      description: 'Toolset definition (for unauthorized tools)',
    },
  },
} as const;

export const DeleteToolsetRequestSchema = {
  type: 'object',
  required: ['toolsetId'],
  properties: {
    toolsetId: {
      type: 'string',
      description: 'Toolset ID',
    },
  },
} as const;

export const ExecuteToolRequestSchema = {
  type: 'object',
  required: ['toolsetKey', 'toolName', 'arguments'],
  properties: {
    toolsetKey: {
      type: 'string',
      description: 'Toolset key',
    },
    toolName: {
      type: 'string',
      description: 'Tool method name to execute',
    },
    arguments: {
      type: 'object',
      additionalProperties: true,
      description: 'Tool arguments',
    },
  },
} as const;

export const ExecuteToolResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          additionalProperties: true,
          description: 'Tool execution result data',
        },
      },
    },
  ],
} as const;

export const ExportToolsetDefinitionsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/ToolsetExportDefinition',
          },
        },
      },
    },
  ],
} as const;

export const ToolsetExportDefinitionSchema = {
  type: 'object',
  properties: {
    key: {
      type: 'string',
      description: 'Toolset unique key',
    },
    name: {
      type: 'string',
      description: 'Toolset display name',
    },
    description: {
      type: 'string',
      description: 'Toolset description',
    },
    tools: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/ToolExportDefinition',
      },
    },
  },
} as const;

export const ToolExportDefinitionSchema = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      description: 'Tool method name',
    },
    description: {
      type: 'string',
      description: 'Tool description',
    },
    inputSchema: {
      type: 'object',
      additionalProperties: true,
      description: 'JSON Schema format input parameter definition',
    },
  },
} as const;

export const GetToolCallResultResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            result: {
              $ref: '#/components/schemas/ToolCallResult',
            },
          },
        },
      },
    },
  ],
} as const;

export const DocumentInterfaceSchema = {
  type: 'object',
  properties: {
    id: {
      type: 'string',
      description:
        'An optional identifier for the document. Ideally this should be unique across the document collection and formatted as a UUID.',
    },
    pageContent: {
      type: 'string',
      description: 'The main content of the document.',
    },
    metadata: {
      type: 'object',
      additionalProperties: true,
      description: 'Metadata associated with the document.',
    },
  },
} as const;

export const InMemorySearchResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/DocumentInterface',
          },
        },
      },
    },
  ],
} as const;

export const CanvasNodeTypeSchema = {
  type: 'string',
  enum: [
    'document',
    'codeArtifact',
    'website',
    'resource',
    'skill',
    'file',
    'tool',
    'skillResponse',
    'toolResponse',
    'memo',
    'group',
    'image',
    'video',
    'audio',
    'mediaSkill',
    'mediaSkillResponse',
    'start',
    'productionStudio',
    'socialPublish',
  ],
} as const;

export const CanvasNodeDataSchema = {
  type: 'object',
  description: 'Node data',
  required: ['title', 'entityId'],
  properties: {
    title: {
      type: 'string',
      description: 'Node title',
    },
    editedTitle: {
      type: 'string',
      description: 'Node edited title',
      deprecated: true,
    },
    entityId: {
      type: 'string',
      description: 'Node entity ID',
    },
    contentPreview: {
      type: 'string',
      description: 'Node content preview',
    },
    metadata: {
      type: 'object',
      additionalProperties: true,
      description: 'Node metadata',
    },
  },
} as const;

export const XYPositionSchema = {
  type: 'object',
  required: ['x', 'y'],
  properties: {
    x: {
      type: 'number',
      description: 'Node position x',
    },
    y: {
      type: 'number',
      description: 'Node position y',
    },
  },
} as const;

export const CanvasNodeSchema = {
  type: 'object',
  required: ['id', 'type', 'position', 'data'],
  properties: {
    id: {
      type: 'string',
      description: 'Node ID',
    },
    type: {
      description: 'Node type',
      $ref: '#/components/schemas/CanvasNodeType',
    },
    position: {
      $ref: '#/components/schemas/XYPosition',
      description: 'Node position',
    },
    offsetPosition: {
      $ref: '#/components/schemas/XYPosition',
      description: 'Node offset position',
    },
    data: {
      type: 'object',
      description: 'Node data',
      $ref: '#/components/schemas/CanvasNodeData',
    },
    style: {
      type: 'object',
      description: 'Node style',
    },
    selected: {
      type: 'boolean',
      description: 'Whether the node is selected',
    },
    dragging: {
      type: 'boolean',
      description: 'Whether the node is being dragged',
    },
    parentId: {
      type: 'string',
      description: 'Parent node ID',
    },
  },
} as const;

export const CanvasEdgeSchema = {
  type: 'object',
  required: ['id', 'source', 'target', 'type'],
  properties: {
    id: {
      type: 'string',
      description: 'Edge ID',
    },
    source: {
      type: 'string',
      description: 'Edge source node ID',
    },
    target: {
      type: 'string',
      description: 'Edge target node ID',
    },
    type: {
      type: 'string',
      description: 'Edge type',
    },
  },
} as const;

export const InitializeWorkflowRequestSchema = {
  type: 'object',
  required: ['canvasId'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID to initialize workflow for',
      example: 'canvas-123',
    },
    sourceCanvasId: {
      type: 'string',
      description: 'Source canvas ID',
      example: 'canvas-456',
    },
    sourceCanvasData: {
      description: 'Source canvas data',
      $ref: '#/components/schemas/RawCanvasData',
    },
    createNewCanvas: {
      description: 'Whether to create a new canvas',
      type: 'boolean',
      default: false,
    },
    nodeBehavior: {
      description: 'Node behavior when executing workflow',
      type: 'string',
      enum: ['create', 'update'],
      default: 'update',
    },
    variables: {
      type: 'array',
      description: 'Workflow variables',
      items: {
        $ref: '#/components/schemas/WorkflowVariable',
      },
    },
    startNodes: {
      type: 'array',
      description: 'Start node IDs',
      items: {
        type: 'string',
        example: 'node-123',
      },
    },
  },
} as const;

export const InitializeWorkflowResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          required: ['workflowExecutionId'],
          properties: {
            workflowExecutionId: {
              type: 'string',
              description: 'Workflow execution ID',
              example: 'we-abc123',
            },
          },
        },
      },
    },
  ],
} as const;

export const AbortWorkflowRequestSchema = {
  type: 'object',
  required: ['executionId'],
  properties: {
    executionId: {
      type: 'string',
      description: 'Workflow execution ID to abort',
      example: 'we-abc123',
    },
  },
} as const;

export const WorkflowNodeExecutionSchema = {
  type: 'object',
  required: ['nodeId'],
  properties: {
    nodeExecutionId: {
      type: 'string',
      description: 'Node execution ID',
    },
    nodeId: {
      type: 'string',
      description: 'Node ID',
    },
    nodeType: {
      type: 'string',
      description: 'Node type',
    },
    nodeData: {
      type: 'string',
      description: 'Node data',
    },
    entityId: {
      type: 'string',
      description: 'Node entity ID',
    },
    newEntityId: {
      type: 'string',
      description: 'New node entity ID',
    },
    title: {
      type: 'string',
      description: 'Node title',
    },
    status: {
      description: 'Node status',
      $ref: '#/components/schemas/ActionStatus',
    },
    progress: {
      type: 'number',
      description: 'Node progress',
    },
    errorMessage: {
      type: 'string',
      description: 'Node error message',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Node creation timestamp',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Node update timestamp',
    },
    artifacts: {
      type: 'array',
      description: '节点成功工具调用产生的结构化产物',
      items: {
        $ref: '#/components/schemas/WorkflowArtifact',
      },
    },
  },
} as const;

export const WorkflowExecutionStatusSchema = {
  type: 'string',
  enum: ['init', 'executing', 'waiting_user', 'finish', 'failed'],
} as const;

export const WorkflowExecutionSchema = {
  type: 'object',
  required: ['executionId'],
  properties: {
    executionId: {
      type: 'string',
      description: 'Workflow execution ID',
    },
    canvasId: {
      type: 'string',
      description: 'Canvas ID',
    },
    title: {
      type: 'string',
      description: 'Workflow title',
    },
    status: {
      $ref: '#/components/schemas/WorkflowExecutionStatus',
      description: 'Workflow status',
    },
    abortedByUser: {
      type: 'boolean',
      description: 'Whether the workflow was aborted by user',
    },
    nodeExecutions: {
      type: 'array',
      description: 'Node executions',
      items: {
        $ref: '#/components/schemas/WorkflowNodeExecution',
      },
    },
    appId: {
      type: 'string',
      description: 'Workflow app ID',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Workflow creation timestamp',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Workflow update timestamp',
    },
  },
} as const;

export const ListWorkflowExecutionsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'List of workflow executions',
          items: {
            $ref: '#/components/schemas/WorkflowExecution',
          },
        },
      },
    },
  ],
} as const;

export const WorkflowTaskSchema = {
  type: 'object',
  required: ['id', 'title', 'prompt', 'toolsets'],
  properties: {
    id: {
      type: 'string',
      description: 'Unique ID for the task',
    },
    title: {
      type: 'string',
      description: 'Display title for the task',
    },
    prompt: {
      type: 'string',
      description: 'The prompt or instruction for this task',
    },
    toolsets: {
      type: 'array',
      description: 'Toolsets selected for this task',
      items: {
        type: 'string',
        description: 'Toolset ID',
      },
    },
    dependentTasks: {
      type: 'array',
      description: 'Tasks that must be executed before this task',
      items: {
        type: 'string',
        description: 'Task ID',
      },
    },
    mediaType: {
      type: 'string',
      enum: ['image', 'video'],
      description:
        'Media type for generation tasks. When set, this task becomes a media generation task and will create image/video nodes instead of skillResponse nodes.',
    },
    batchCount: {
      type: 'integer',
      minimum: 1,
      maximum: 20,
      default: 1,
      description:
        'Number of media items to generate in batch. Only applicable when mediaType is set.',
    },
    model: {
      type: 'string',
      description:
        'Model ID for media generation (e.g. gpt-image-2, sora-2). Only applicable when mediaType is set.',
    },
    providerItemId: {
      type: 'string',
      description: 'Provider item ID for media generation. Only applicable when mediaType is set.',
    },
    skillId: {
      type: 'string',
      description: 'Workflow skill ID bound to a regular task.',
    },
    promptSkillIds: {
      type: 'array',
      description:
        "Prompt skill IDs (「我的技能」) bound to this task. Each ID must come from the user's enabled prompt skills.",
      items: {
        type: 'string',
        description: 'Prompt skill ID',
      },
    },
    nodeType: {
      type: 'string',
      enum: ['productionStudio', 'socialPublish'],
      description: 'Dedicated workflow node type. Mutually exclusive with mediaType.',
    },
    visionProviderItemId: {
      type: 'string',
      description: 'Vision-capable LLM provider item ID for social publishing.',
    },
    strategy: {
      type: 'string',
      enum: [
        'highlight-montage',
        'story-narration',
        'series-recommendation',
        'overlay-only',
        'assemble',
        'speech_cleanup',
        'highlight',
      ],
      description: 'Production strategy. Only applicable to productionStudio.',
    },
    strategyMarket: {
      type: 'string',
      enum: ['zh-CN', 'en-US', 'fil-PH', 'id-ID', 'th-TH', 'vi-VN'],
      description: 'Versioned video-production market pack for productionStudio.',
    },
    mediaVariableId: {
      type: 'string',
      description: 'Stable media input variable ID.',
    },
    sourceVariableIds: {
      type: 'array',
      maxItems: 20,
      items: {
        type: 'string',
      },
      description: 'Ordered stable media input variable IDs for productionStudio.',
    },
    sourceNodeIds: {
      type: 'array',
      maxItems: 20,
      items: {
        type: 'string',
      },
      description: 'Ordered upstream task IDs for productionStudio.',
    },
    requestVariableId: {
      type: 'string',
      description: 'Stable text requirement variable ID.',
    },
    targetPlatform: {
      type: 'string',
      maxLength: 64,
    },
    targetDurationSeconds: {
      type: 'integer',
      minimum: 1,
      maximum: 600,
    },
    aspectRatio: {
      type: 'string',
      enum: ['9:16', '16:9', '1:1', '4:5'],
    },
    resolution: {
      type: 'object',
      additionalProperties: false,
      required: ['width', 'height'],
      properties: {
        width: {
          type: 'integer',
          minimum: 16,
          maximum: 7680,
        },
        height: {
          type: 'integer',
          minimum: 16,
          maximum: 7680,
        },
      },
    },
    includeSubtitles: {
      type: 'boolean',
    },
    musicAssetId: {
      type: 'string',
      maxLength: 128,
    },
    accountVariableId: {
      type: 'string',
      description: 'Stable social-account input variable ID.',
    },
    platformType: {
      type: 'integer',
      minimum: 1,
      maximum: 20,
      description: 'Target social platform code. Only applicable to socialPublish.',
    },
    postCount: {
      type: 'integer',
      minimum: 1,
      maximum: 10,
      description: 'Posts to publish per account. Only applicable to socialPublish.',
    },
    intervalMinutes: {
      type: 'number',
      minimum: 0,
      maximum: 1440,
      description: 'Minutes between posts for the same account. Only applicable to socialPublish.',
    },
  },
} as const;

export const WorkflowPlanSchema = {
  type: 'object',
  required: ['title', 'tasks'],
  properties: {
    title: {
      type: 'string',
      description: 'Title of the workflow plan',
    },
    tasks: {
      type: 'array',
      description: 'Array of workflow tasks to be executed',
      items: {
        $ref: '#/components/schemas/WorkflowTask',
      },
    },
    variables: {
      type: 'array',
      description: 'Array of variables (aka User inputs) defined for the workflow plan',
      items: {
        $ref: '#/components/schemas/WorkflowVariable',
      },
    },
  },
} as const;

export const WorkflowPlanRecordSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/WorkflowPlan',
    },
    {
      type: 'object',
      properties: {
        planId: {
          type: 'string',
          description: 'Workflow plan ID',
        },
        version: {
          type: 'number',
          description: 'Workflow plan version',
        },
        createdAt: {
          type: 'string',
          format: 'date-time',
          description: 'Workflow plan creation timestamp',
        },
        updatedAt: {
          type: 'string',
          format: 'date-time',
          description: 'Workflow plan update timestamp',
        },
      },
    },
  ],
} as const;

export const GetWorkflowPlanDetailResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/WorkflowPlanRecord',
        },
      },
    },
  ],
} as const;

export const GetWorkflowDetailResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          $ref: '#/components/schemas/WorkflowExecution',
        },
      },
    },
  ],
} as const;

export const CreateWorkflowAppRequestSchema = {
  type: 'object',
  required: ['canvasId', 'title', 'description', 'query', 'variables', 'coverStorageKey'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID',
    },
    title: {
      type: 'string',
      description: 'Workflow app title',
    },
    description: {
      type: 'string',
      description: 'Workflow app description',
    },
    query: {
      type: 'string',
      description: 'Workflow app query',
    },
    variables: {
      type: 'array',
      description: 'Workflow app variables',
      items: {
        $ref: '#/components/schemas/WorkflowVariable',
      },
    },
    resultNodeIds: {
      type: 'array',
      description: 'Result node IDs',
      items: {
        type: 'string',
      },
    },
    coverStorageKey: {
      type: 'string',
      description: 'Cover image storage key',
    },
    remixEnabled: {
      type: 'boolean',
      description: 'Whether remix is enabled for this app',
    },
    publishToCommunity: {
      type: 'boolean',
      description: 'Whether to publish this app to the community',
    },
  },
} as const;

export const DeleteWorkflowAppRequestSchema = {
  type: 'object',
  required: ['appId'],
  properties: {
    appId: {
      type: 'string',
      description: 'Workflow app ID',
    },
  },
} as const;

export const WorkflowAppSchema = {
  type: 'object',
  required: ['appId', 'canvasId', 'variables'],
  properties: {
    appId: {
      type: 'string',
      description: '工作流应用 ID',
    },
    shareId: {
      type: 'string',
      description: '用于公开访问和生成 URL 的分享 ID',
    },
    title: {
      type: 'string',
      description: '工作流应用标题',
    },
    description: {
      type: 'string',
      description: '工作流应用描述',
    },
    owner: {
      description: '工作流应用所有者',
      $ref: '#/components/schemas/ShareUser',
    },
    canvasId: {
      type: 'string',
      description: '画布 ID',
    },
    query: {
      type: 'string',
      description: '工作流应用查询语句',
    },
    variables: {
      type: 'array',
      description: '工作流应用变量',
      items: {
        $ref: '#/components/schemas/WorkflowVariable',
      },
    },
    resultNodeIds: {
      type: 'array',
      description: '结果节点 ID 列表',
      items: {
        type: 'string',
      },
    },
    remixEnabled: {
      type: 'boolean',
      description: '是否允许对该应用进行二次创作',
    },
    coverUrl: {
      type: 'string',
      description: '封面图片 URL',
    },
    publishToCommunity: {
      type: 'boolean',
      description: '是否将该应用发布到社区',
    },
    publishReviewStatus: {
      type: 'string',
      description: '社区发布审核状态',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '工作流应用创建时间戳',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: '工作流应用更新时间戳',
    },
    voucherTriggerResult: {
      $ref: '#/components/schemas/VoucherTriggerResult',
      description: '发布到社区时的优惠券触发结果',
    },
  },
} as const;

export const CreateWorkflowAppResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/WorkflowApp',
        },
      },
    },
  ],
} as const;

export const GetWorkflowAppDetailResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/WorkflowApp',
        },
      },
    },
  ],
} as const;

export const ListWorkflowAppsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'List of workflow apps',
          items: {
            $ref: '#/components/schemas/WorkflowApp',
          },
        },
      },
    },
  ],
} as const;

export const TemplateGenerationStatusSchema = {
  type: 'string',
  description: 'Template generation status',
  enum: ['idle', 'pending', 'generating', 'completed', 'failed'],
  example: 'completed',
} as const;

export const GetTemplateGenerationStatusResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      required: ['data'],
      properties: {
        data: {
          type: 'object',
          required: ['status', 'updatedAt', 'createdAt'],
          properties: {
            status: {
              $ref: '#/components/schemas/TemplateGenerationStatus',
            },
            templateContent: {
              type: 'string',
              nullable: true,
              description: 'Generated template content',
            },
            error: {
              type: 'string',
              nullable: true,
              description: 'Error message if generation failed',
            },
            updatedAt: {
              type: 'string',
              format: 'date-time',
              description: 'Last update time',
            },
            createdAt: {
              type: 'string',
              format: 'date-time',
              description: 'Creation time',
            },
          },
        },
      },
    },
  ],
} as const;

export const ExecuteWorkflowAppRequestSchema = {
  type: 'object',
  required: ['shareId', 'variables'],
  properties: {
    shareId: {
      type: 'string',
      description: 'Workflow app share ID for execution',
    },
    variables: {
      type: 'array',
      description: 'Workflow app variables',
      items: {
        $ref: '#/components/schemas/WorkflowVariable',
      },
    },
  },
} as const;

export const ExecuteWorkflowAppResultSchema = {
  type: 'object',
  required: ['executionId'],
  properties: {
    executionId: {
      type: 'string',
      description: 'Workflow execution ID',
    },
  },
} as const;

export const ExecuteWorkflowAppResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/ExecuteWorkflowAppResult',
        },
      },
    },
  ],
} as const;

export const ValueTypeSchema = {
  type: 'string',
  enum: ['text', 'resource'],
} as const;

export const ResourceValueSchema = {
  type: 'object',
  required: ['name', 'fileType'],
  properties: {
    name: {
      type: 'string',
      description: '资源名称',
    },
    fileType: {
      description: '资源文件类型',
      $ref: '#/components/schemas/VariableResourceType',
    },
    fileId: {
      type: 'string',
      description: 'DriveFile ID（资源主标识）',
    },
    assetId: {
      type: 'string',
      description: '媒体生产链路中的素材库文件条目 ID（视频编辑外层字段名保留 assetId）',
    },
    source: {
      type: 'string',
      enum: ['upload', 'media_library', 'canvas_file', 'upstream_output'],
      description: '本次资源值的明确来源',
    },
    storageKey: {
      type: 'string',
      description: '资源存储键（旧字段，仅为兼容保留）',
    },
    entityId: {
      type: 'string',
      description: '资源 ID（已弃用，请使用 fileId）',
    },
    categoryName: {
      type: 'string',
      description: '该值所属分类（文件夹）名称，用于展示',
    },
  },
} as const;

export const AccountValueSchema = {
  type: 'object',
  required: ['accountId'],
  properties: {
    accountId: {
      type: 'string',
      description: '社交账号 ID',
    },
    userName: {
      type: 'string',
      description: '社交账号用户名',
    },
    platform: {
      type: 'string',
      description: '社交账号平台名称',
    },
    platformType: {
      type: 'integer',
      description: '社交账号平台类型',
    },
    filePath: {
      type: 'string',
      description: '社交账号 Cookie 文件路径',
    },
    categoryName: {
      type: 'string',
      description: '账号所属分类名称，用于展示',
    },
  },
} as const;

export const VariableValueSchema = {
  type: 'object',
  required: ['type'],
  properties: {
    type: {
      description: '变量类型',
      $ref: '#/components/schemas/ValueType',
    },
    text: {
      type: 'string',
      description: '文本变量值（用于 text 类型）',
    },
    resource: {
      description: '资源变量值（用于 resource 类型）',
      $ref: '#/components/schemas/ResourceValue',
    },
    account: {
      description: '账号变量值（用于 account/option 类型）',
      $ref: '#/components/schemas/AccountValue',
    },
  },
} as const;

export const VariableResourceTypeSchema = {
  type: 'string',
  enum: ['document', 'image', 'video', 'audio'],
} as const;

export const WorkflowVariableSchema = {
  type: 'object',
  description: '工作流变量定义',
  required: ['name', 'value', 'variableId'],
  properties: {
    variableId: {
      type: 'string',
      description: '变量 ID，唯一且只读',
    },
    name: {
      type: 'string',
      description: '工作流中使用的变量名称',
    },
    value: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/VariableValue',
      },
      description: '变量值',
    },
    description: {
      type: 'string',
      description: '变量描述',
      example: '用户姓名',
    },
    createdAt: {
      type: 'string',
      description: '变量创建时间',
      example: '2021-01-01T00:00:00.000Z',
    },
    updatedAt: {
      type: 'string',
      description: '变量更新时间',
      example: '2021-01-01T00:00:00.000Z',
    },
    snapshottedAt: {
      type: 'string',
      format: 'date-time',
      description: '本次执行固化该输入值的时间；画布定义中可为空',
    },
    variableType: {
      type: 'string',
      description: '变量类型',
      enum: ['string', 'option', 'resource'],
    },
    inputKind: {
      type: 'string',
      enum: ['text', 'media', 'social_account'],
      description: '专用工作流节点使用的稳定输入语义；旧变量可为空',
    },
    allowedPlatforms: {
      type: 'array',
      items: {
        type: 'string',
      },
      description: 'social_account 输入允许的平台范围',
    },
    required: {
      type: 'boolean',
      description: '变量是否必填，默认为 false',
    },
    isSingle: {
      type: 'boolean',
      description: '变量值是否为单值（非多值）',
    },
    options: {
      type: 'array',
      items: {
        type: 'string',
      },
      description: '选项数组（仅变量类型为 `option` 时有效）',
    },
    resourceTypes: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/VariableResourceType',
      },
      description: '支持的资源类型（仅变量类型为 resource 时有效）',
    },
  },
} as const;

export const GetWorkflowVariablesResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'List of workflow variables',
          items: {
            $ref: '#/components/schemas/WorkflowVariable',
          },
        },
      },
    },
  ],
} as const;

export const UpdateWorkflowVariablesRequestSchema = {
  type: 'object',
  required: ['canvasId', 'variables'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID',
    },
    variables: {
      type: 'array',
      description: 'List of workflow variables',
      items: {
        $ref: '#/components/schemas/WorkflowVariable',
      },
    },
    archiveOldFiles: {
      type: 'boolean',
      description:
        'Whether to archive existing drive files associated with old resource variables before updating',
    },
  },
} as const;

export const UpdateWorkflowVariablesResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'Updated list of workflow variables',
          items: {
            $ref: '#/components/schemas/WorkflowVariable',
          },
        },
      },
    },
  ],
} as const;

export const DriveFileCategorySchema = {
  type: 'string',
  enum: ['document', 'image', 'video', 'audio', 'others'],
} as const;

export const DriveFileSourceSchema = {
  type: 'string',
  enum: ['manual', 'variable', 'agent'],
} as const;

export const DriveFileScopeSchema = {
  type: 'string',
  enum: ['present', 'archive'],
} as const;

export const DriveFileSchema = {
  type: 'object',
  required: ['fileId', 'canvasId', 'name', 'type'],
  properties: {
    fileId: {
      type: 'string',
      description: 'Drive file ID',
    },
    canvasId: {
      type: 'string',
      description: 'Canvas ID',
    },
    folderId: {
      type: 'string',
      nullable: true,
      description: 'Folder ID',
    },
    assetId: {
      type: 'string',
      description: '当前文件已关联的媒体库正式条目 ID',
    },
    categoryName: {
      type: 'string',
      description: '当前文件关联的媒体库父目录名称',
    },
    name: {
      type: 'string',
      description: 'Drive file name',
    },
    type: {
      type: 'string',
      description: 'Drive file type',
    },
    category: {
      $ref: '#/components/schemas/DriveFileCategory',
      description: 'Drive file category',
    },
    source: {
      $ref: '#/components/schemas/DriveFileSource',
      description: 'Drive file source',
    },
    scope: {
      $ref: '#/components/schemas/DriveFileScope',
      description: 'Drive file scope',
    },
    size: {
      type: 'number',
      description: 'Drive file size',
    },
    summary: {
      type: 'string',
      description: 'Drive file summary',
    },
    storageKey: {
      type: 'string',
      description: 'Object storage key for the file',
    },
    variableId: {
      type: 'string',
      description: 'Related variable ID',
    },
    resultId: {
      type: 'string',
      description: 'Action result ID',
    },
    resultVersion: {
      type: 'number',
      description: 'Action result version',
    },
    content: {
      type: 'string',
      description: 'Drive file content (only used for model input)',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Drive file creation timestamp',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Drive file update timestamp',
    },
    url: {
      type: 'string',
      description: 'Private access URL for the file (requires authentication)',
    },
  },
} as const;

export const ExportJobStatusSchema = {
  type: 'string',
  enum: ['pending', 'processing', 'completed', 'failed'],
  description: 'Status of an export job',
} as const;

export const ExportJobSchema = {
  type: 'object',
  required: ['jobId', 'status'],
  properties: {
    jobId: {
      type: 'string',
      description: 'Export job ID',
    },
    fileId: {
      type: 'string',
      description: 'Source file ID being exported',
    },
    status: {
      $ref: '#/components/schemas/ExportJobStatus',
    },
    format: {
      type: 'string',
      enum: ['pdf', 'docx'],
      description: 'Export format',
    },
    name: {
      type: 'string',
      description: 'Output file name',
    },
    error: {
      type: 'string',
      description: 'Error message if job failed',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Job creation timestamp',
    },
  },
} as const;

export const StartExportJobRequestSchema = {
  type: 'object',
  required: ['fileId', 'format'],
  properties: {
    fileId: {
      type: 'string',
      description: 'File ID to export',
    },
    format: {
      type: 'string',
      enum: ['pdf', 'docx'],
      description: 'Target export format',
    },
  },
} as const;

export const StartExportJobResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/ExportJob',
        },
      },
    },
  ],
} as const;

export const GetExportJobStatusResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/ExportJob',
        },
      },
    },
  ],
} as const;

export const ListDriveFilesResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'List of drive files',
          items: {
            $ref: '#/components/schemas/DriveFile',
          },
        },
      },
    },
  ],
} as const;

export const UpsertDriveFileRequestSchema = {
  type: 'object',
  required: ['canvasId', 'name'],
  properties: {
    fileId: {
      type: 'string',
      description: 'File ID (required for update)',
    },
    canvasId: {
      type: 'string',
      description: 'Canvas ID',
    },
    folderId: {
      type: 'string',
      nullable: true,
      description: 'Folder ID',
    },
    name: {
      type: 'string',
      description: 'Drive file name',
    },
    type: {
      type: 'string',
      description: 'Drive file type (MIME type)',
    },
    summary: {
      type: 'string',
      description: 'Brief summary for this file',
    },
    content: {
      type: 'string',
      description: 'File content (for plain text files)',
    },
    storageKey: {
      type: 'string',
      description: 'File storage key (for uploaded files)',
    },
    externalUrl: {
      type: 'string',
      description: 'External URL to download from',
    },
    source: {
      $ref: '#/components/schemas/DriveFileSource',
      description: 'File source',
    },
    variableId: {
      type: 'string',
      description: 'Related variable ID',
    },
    resultId: {
      type: 'string',
      description: 'Related agent result ID',
    },
    resultVersion: {
      type: 'number',
      description: 'Related agent result version',
    },
    archiveFiles: {
      type: 'boolean',
      description:
        'Whether to archive existing files with the same variableId or resultId before creating new file',
    },
  },
} as const;

export const BatchCreateDriveFilesRequestSchema = {
  type: 'object',
  required: ['canvasId', 'files'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID',
    },
    files: {
      type: 'array',
      description: 'List of drive files',
      items: {
        $ref: '#/components/schemas/UpsertDriveFileRequest',
      },
    },
  },
} as const;

export const BatchCreateDriveFilesResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: 'List of drive files',
          items: {
            $ref: '#/components/schemas/DriveFile',
          },
        },
      },
    },
  ],
} as const;

export const UpsertDriveFileResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/DriveFile',
        },
      },
    },
  ],
} as const;

export const DeleteDriveFileRequestSchema = {
  type: 'object',
  required: ['fileId'],
  properties: {
    fileId: {
      type: 'string',
      description: 'Drive file ID',
    },
  },
} as const;

export const SendEmailRequestSchema = {
  type: 'object',
  required: ['subject', 'html'],
  properties: {
    subject: {
      type: 'string',
      description: 'Email subject',
    },
    html: {
      type: 'string',
      description: 'Email HTML content',
    },
    to: {
      type: 'string',
      description: 'Email recipient. If not specified, the email will be sent to current user.',
    },
    from: {
      type: 'string',
      description: 'Email sender. If not specified, server will use the default sender.',
    },
    attachments: {
      type: 'array',
      description: 'Email attachments, should be array of URLs.',
      items: {
        type: 'string',
      },
    },
  },
} as const;

export const GenerateAppTemplateRequestSchema = {
  type: 'object',
  required: ['canvasId'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID to generate template for',
      pattern: '^[a-zA-Z0-9_-]+$',
      example: 'canvas-123',
    },
  },
} as const;

export const AppTemplateResultSchema = {
  type: 'object',
  required: ['templateContent', 'variables', 'metadata'],
  properties: {
    templateContent: {
      type: 'string',
      description: `Template with placeholders for user interaction.
Variables are represented using handlebars syntax (e.g., {{variableName}}).
`,
      example:
        'Create a {{workflowType}} workflow with {{variableCount}} variables including {{primarySkill}} functionality',
    },
    variables: {
      type: 'array',
      description: 'List of related workflow variables used in the template',
      items: {
        $ref: '#/components/schemas/WorkflowVariable',
      },
    },
    metadata: {
      type: 'object',
      required: ['extractedAt', 'variableCount'],
      properties: {
        extractedAt: {
          type: 'number',
          description: 'Template generation timestamp (for version control)',
          example: 1703123456789,
        },
        variableCount: {
          type: 'number',
          description: 'Total variable count (for frontend statistics display)',
          example: 5,
        },
        promptCount: {
          type: 'number',
          description: 'Original prompt count (for quality assessment)',
          example: 3,
        },
        canvasComplexity: {
          type: 'string',
          description: 'Canvas complexity level',
          enum: ['simple', 'medium', 'complex'],
          example: 'medium',
        },
        workflowType: {
          type: 'string',
          description: 'Workflow type (for template classification and display)',
          example: 'data-processing',
        },
        templateVersion: {
          type: 'number',
          description: 'Template version number (supports template iteration)',
          example: 1,
        },
        workflowTitle: {
          type: 'string',
          description: 'Workflow title for display',
          example: 'Data Processing Pipeline',
        },
        workflowDescription: {
          type: 'string',
          description: 'Workflow description',
          example: 'Automated data processing workflow',
        },
        estimatedExecutionTime: {
          type: 'string',
          description: 'Estimated execution time',
          example: '5-10 minutes',
        },
        skillTags: {
          type: 'array',
          description: 'Skill tags for categorization',
          items: {
            type: 'string',
          },
          example: ['data-processing', 'automation'],
        },
      },
    },
  },
} as const;

export const FormDefinitionSchema = {
  type: 'object',
  required: ['formId', 'title', 'schema', 'createdAt', 'updatedAt'],
  properties: {
    formId: {
      type: 'string',
      description: 'Form ID',
      example: 'form-123',
    },
    title: {
      type: 'string',
      description: 'Form title',
      example: 'User feedback survey',
    },
    description: {
      type: 'string',
      description: 'Form description',
      example: 'Please fill out the following survey',
    },
    schema: {
      type: 'string',
      description: 'JSON Schema definition (RJSF compatible)',
      example:
        '{"type":"object","properties":{"feedback":{"type":"string","title":"Feedback content"}},"required":["feedback"]}',
    },
    uiSchema: {
      type: 'string',
      description:
        'UI Schema definition (RJSF compatible for controlling UI elements like emoji, layout, helper text)',
      example:
        '{"feedback":{"ui:widget":"textarea","ui:placeholder":"Please enter your feedback"}}',
    },
    status: {
      type: 'string',
      description: 'Form status',
      enum: ['draft', 'published', 'archived'],
      default: 'draft',
      example: 'published',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Creation timestamp',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Last update timestamp',
    },
    deletedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Soft delete timestamp',
    },
  },
} as const;

export const FormSubmissionSchema = {
  type: 'object',
  required: ['formId', 'uid', 'answers', 'createdAt', 'updatedAt'],
  properties: {
    submissionId: {
      type: 'string',
      description: 'Submission ID',
      example: 'sub-456',
    },
    formId: {
      type: 'string',
      description: 'Associated form ID',
      example: 'form-123',
    },
    uid: {
      type: 'string',
      description: 'User ID who submitted the form',
      example: 'user-789',
    },
    answers: {
      type: 'string',
      description: 'Submission answers (JSON object with field values)',
      example: '{"feedback":"The service is very good","rating":5}',
    },
    status: {
      type: 'string',
      description: 'Submission status',
      enum: ['draft', 'submitted', 'reviewed'],
      default: 'draft',
      example: 'submitted',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Creation timestamp',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Last update timestamp',
    },
  },
} as const;

export const FormFieldSchemaSchema = {
  type: 'object',
  description: 'RJSF compatible field schema definition',
  properties: {
    type: {
      type: 'string',
      description: 'Field type',
      enum: ['string', 'number', 'integer', 'boolean', 'object', 'array'],
      example: 'string',
    },
    title: {
      type: 'string',
      description: 'Field display title',
      example: 'Your name',
    },
    description: {
      type: 'string',
      description: 'Field description/helper text',
      example: 'Please enter your real name',
    },
    enum: {
      type: 'array',
      description: 'Enumeration values for radio/select fields',
      items: {
        type: 'string',
      },
      example: ['A', 'B', 'C'],
    },
    enumNames: {
      type: 'array',
      description: 'Display names for enum values',
      items: {
        type: 'string',
      },
      example: ['Excellent', 'Good', 'Average'],
    },
    default: {
      description: 'Default value for the field',
    },
    minLength: {
      type: 'integer',
      description: 'Minimum string length',
      example: 2,
    },
    maxLength: {
      type: 'integer',
      description: 'Maximum string length',
      example: 100,
    },
    pattern: {
      type: 'string',
      description: 'Regex pattern for validation',
      example: '^[a-zA-Z0-9]+$',
    },
    minimum: {
      type: 'number',
      description: 'Minimum numeric value',
    },
    maximum: {
      type: 'number',
      description: 'Maximum numeric value',
    },
    required: {
      type: 'boolean',
      description: 'Whether this field is required',
      default: false,
    },
  },
} as const;

export const FormUiSchemaSchema = {
  type: 'object',
  description: 'RJSF UI schema for controlling form appearance and behavior',
  properties: {
    'ui:widget': {
      type: 'string',
      description: 'Widget type override',
      enum: [
        'text',
        'textarea',
        'select',
        'radio',
        'checkbox',
        'checkboxes',
        'date',
        'email',
        'password',
      ],
      example: 'textarea',
    },
    'ui:placeholder': {
      type: 'string',
      description: 'Input placeholder text',
      example: 'Please enter content...',
    },
    'ui:help': {
      type: 'string',
      description: 'Help text displayed below the field',
      example: 'This field is required',
    },
    'ui:options': {
      type: 'object',
      description: 'Widget-specific options (can include emoji, layout settings, etc.)',
      properties: {
        emoji: {
          type: 'string',
          description: 'Emoji to display with the field',
          example: '📝',
        },
        layout: {
          type: 'string',
          description: 'Layout style',
          enum: ['horizontal', 'vertical'],
          example: 'vertical',
        },
        rows: {
          type: 'integer',
          description: 'Number of rows for textarea',
          example: 4,
        },
        inline: {
          type: 'boolean',
          description: 'Whether to display radio/checkbox options inline',
          example: true,
        },
      },
    },
  },
} as const;

export const BillingTypeSchema = {
  type: 'string',
  enum: ['per_call', 'per_quantity'],
  description: 'Type of billing for a tool method',
} as const;

export const BillingConfigSchema = {
  type: 'object',
  required: ['enabled', 'type'],
  properties: {
    enabled: {
      type: 'boolean',
      description: 'Whether billing is enabled',
    },
    type: {
      $ref: '#/components/schemas/BillingType',
    },
    creditsPerCall: {
      type: 'number',
      description: 'Credits per call (for PER_CALL type)',
    },
    creditsPerUnit: {
      type: 'number',
      description: 'Credits per unit (for PER_QUANTITY type)',
    },
    quantityField: {
      type: 'string',
      description: 'Field name to extract quantity from (for PER_QUANTITY type)',
    },
    maxCredits: {
      type: 'number',
      description: 'Maximum credits per single call',
    },
    quantityTransform: {
      type: 'string',
      description: 'Transform function name for custom quantity calculation',
    },
  },
} as const;

export const SchemaPropertyTypeSchema = {
  type: 'string',
  enum: ['string', 'number', 'boolean', 'object', 'array'],
  description: 'JSON Schema property type',
} as const;

export const ToolResourceTypeSchema = {
  type: 'string',
  enum: ['audio', 'video', 'image', 'document'],
  description: 'Resource type for tool operations (subset of ResourceType)',
} as const;

export const SchemaPropertySchema = {
  type: 'object',
  required: ['type'],
  properties: {
    type: {
      $ref: '#/components/schemas/SchemaPropertyType',
    },
    description: {
      type: 'string',
      description: 'Property description',
    },
    isResource: {
      type: 'boolean',
      description: 'Whether this property is a resource reference',
    },
    format: {
      type: 'string',
      description:
        'Format for the property value. For resources: base64, url, binary, text. For strings: date-time, uri, email, etc.',
      example: 'binary',
    },
    file_uploadable: {
      type: 'boolean',
      description: 'Whether this field accepts file uploads (Composio-specific marker)',
    },
    const: {
      description: 'Constant value for discriminator matching in oneOf/anyOf',
    },
    oneOf: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/SchemaProperty',
      },
      description: 'One of the listed schemas must match',
    },
    anyOf: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/SchemaProperty',
      },
      description: 'Any of the listed schemas can match',
    },
    minLength: {
      type: 'number',
      description: 'Minimum length (for string)',
    },
    maxLength: {
      type: 'number',
      description: 'Maximum length (for string)',
    },
    min: {
      type: 'number',
      description: 'Minimum value (for number)',
    },
    max: {
      type: 'number',
      description: 'Maximum value (for number)',
    },
    enum: {
      type: 'array',
      items: {
        type: 'string',
      },
      description: 'Enum values',
    },
    default: {
      description: 'Default value',
    },
    optional: {
      type: 'boolean',
      description: 'Whether this property is optional',
    },
    properties: {
      type: 'object',
      additionalProperties: {
        $ref: '#/components/schemas/SchemaProperty',
      },
      description: 'Nested properties (for object type)',
    },
    items: {
      $ref: '#/components/schemas/SchemaProperty',
    },
    required: {
      type: 'array',
      items: {
        type: 'string',
      },
      description: 'Required properties (for object type)',
    },
  },
} as const;

export const JsonSchemaSchema = {
  type: 'object',
  description: 'JSON schema definition for request/response with resource field markers',
  required: ['type', 'properties'],
  properties: {
    type: {
      type: 'string',
      enum: ['object'],
      description: 'Schema type',
    },
    properties: {
      type: 'object',
      additionalProperties: {
        $ref: '#/components/schemas/SchemaProperty',
      },
      description: 'Schema properties',
    },
    required: {
      type: 'array',
      items: {
        type: 'string',
      },
      description: 'Required property names',
    },
    omitFields: {
      type: 'array',
      items: {
        type: 'string',
      },
      description: "Field names to omit from the response (e.g., ['thoughtSignature'])",
    },
  },
} as const;

export const ResponseSchemaSchema = {
  $ref: '#/components/schemas/JsonSchema',
} as const;

export const ResourceFieldSchema = {
  type: 'object',
  required: ['fieldPath', 'isArray'],
  properties: {
    fieldPath: {
      type: 'string',
      description: 'JSONPath to the resource field',
    },
    type: {
      $ref: '#/components/schemas/ToolResourceType',
      description: 'Resource type hint (optional, can be inferred from file MIME type)',
    },
    isArray: {
      type: 'boolean',
      description: 'Whether this field is an array of resources',
    },
    format: {
      type: 'string',
      description:
        'Format to convert resource content to (default is binary). Options: base64, url, binary, text',
      example: 'binary',
    },
  },
} as const;

export const HttpMethodSchema = {
  type: 'string',
  enum: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'],
  description: 'HTTP method',
} as const;

export const ToolMethodConfigSchema = {
  type: 'object',
  required: ['name', 'description', 'endpoint', 'schema', 'responseSchema'],
  properties: {
    name: {
      type: 'string',
      description: "Method name (e.g., 'text_to_speech')",
    },
    version: {
      type: 'number',
      description: 'Version number (incremental integer, derived from pk or timestamp)',
    },
    displayName: {
      type: 'string',
      description: 'Human-readable display name',
    },
    description: {
      type: 'string',
      description: 'Method description for LLM',
    },
    endpoint: {
      type: 'string',
      description: 'API endpoint URL',
    },
    method: {
      $ref: '#/components/schemas/HttpMethod',
    },
    schema: {
      type: 'string',
      description: 'Input parameter schema (JSON string)',
    },
    responseSchema: {
      type: 'string',
      description: 'Response schema for resource identification (JSON string)',
    },
    billing: {
      $ref: '#/components/schemas/BillingConfig',
    },
    customHandler: {
      type: 'string',
      description: 'Custom handler class name (optional)',
    },
    useSdk: {
      type: 'boolean',
      description: 'Whether to use SDK instead of HTTP (optional)',
    },
    sdkPackage: {
      type: 'string',
      description: 'SDK package name (if useSdk is true)',
    },
    sdkMethod: {
      type: 'string',
      description: "SDK method path (e.g., 'client.textToSpeech.convert')",
    },
    timeout: {
      type: 'number',
      description: 'Request timeout in milliseconds',
    },
    maxRetries: {
      type: 'number',
      description: 'Maximum retries on failure',
    },
  },
} as const;

export const CredentialConfigSchema = {
  type: 'object',
  additionalProperties: {
    type: 'string',
  },
  properties: {
    apiKey: {
      type: 'string',
      description: 'API key',
    },
    apiSecret: {
      type: 'string',
      description: 'API secret',
    },
    accessToken: {
      type: 'string',
      description: 'OAuth access token',
    },
    refreshToken: {
      type: 'string',
      description: 'OAuth refresh token',
    },
  },
  description: 'Credential configuration',
} as const;

export const ToolsetConfigSchema = {
  type: 'object',
  required: ['inventoryKey', 'domain', 'methods'],
  properties: {
    inventoryKey: {
      type: 'string',
      description: "Toolset inventory key (e.g., 'fish_audio', 'heygen')",
    },
    domain: {
      type: 'string',
      description: "Tool domain (e.g., 'fish.audio')",
    },
    name: {
      type: 'string',
      description: 'Display name',
    },
    credentials: {
      $ref: '#/components/schemas/CredentialConfig',
    },
    methods: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/ToolMethodConfig',
      },
      description: 'Tool methods',
    },
    version: {
      type: 'number',
      description: 'Configuration version',
    },
    metadata: {
      type: 'object',
      additionalProperties: true,
      description: 'Configuration metadata',
    },
  },
} as const;

export const ParsedMethodConfigSchema = {
  type: 'object',
  required: ['name', 'description', 'endpoint', 'schema', 'responseSchema'],
  properties: {
    name: {
      type: 'string',
      description: 'Method name',
    },
    version: {
      type: 'number',
      description: 'Version number',
    },
    displayName: {
      type: 'string',
      description: 'Human-readable display name',
    },
    description: {
      type: 'string',
      description: 'Method description for LLM',
    },
    endpoint: {
      type: 'string',
      description: 'API endpoint URL',
    },
    method: {
      $ref: '#/components/schemas/HttpMethod',
    },
    schema: {
      $ref: '#/components/schemas/JsonSchema',
    },
    responseSchema: {
      $ref: '#/components/schemas/JsonSchema',
    },
    billing: {
      $ref: '#/components/schemas/BillingConfig',
    },
    customHandler: {
      type: 'string',
      description: 'Custom handler class name',
    },
    useSdk: {
      type: 'boolean',
      description: 'Whether to use SDK instead of HTTP',
    },
    sdkPackage: {
      type: 'string',
      description: 'SDK package name',
    },
    sdkMethod: {
      type: 'string',
      description: 'SDK method path',
    },
    timeout: {
      type: 'number',
      description: 'Request timeout in milliseconds',
    },
    maxRetries: {
      type: 'number',
      description: 'Maximum retries on failure',
    },
    useFormData: {
      type: 'boolean',
      description: 'Whether to use multipart/form-data encoding',
    },
  },
} as const;

export const ParsedToolsetConfigSchema = {
  type: 'object',
  required: ['inventoryKey', 'domain', 'methods'],
  properties: {
    inventoryKey: {
      type: 'string',
      description: "Toolset inventory key (e.g., 'fish_audio', 'heygen')",
    },
    domain: {
      type: 'string',
      description: 'Tool domain',
    },
    name: {
      type: 'string',
      description: 'Display name',
    },
    credentials: {
      $ref: '#/components/schemas/CredentialConfig',
    },
    methods: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/ParsedMethodConfig',
      },
      description: 'Parsed methods with JSON schemas',
    },
    version: {
      type: 'number',
      description: 'Configuration version',
    },
    metadata: {
      type: 'object',
      additionalProperties: true,
      description: 'Configuration metadata',
    },
  },
} as const;

export const ConfigChangeEventSchema = {
  type: 'object',
  required: ['toolsetKey', 'md5', 'config', 'timestamp'],
  properties: {
    toolsetKey: {
      type: 'string',
      description: 'Toolset key',
    },
    md5: {
      type: 'string',
      description: 'Configuration MD5 hash',
    },
    config: {
      $ref: '#/components/schemas/ToolsetConfig',
    },
    timestamp: {
      type: 'string',
      format: 'date-time',
      description: 'Event timestamp',
    },
  },
} as const;

export const AdapterRequestSchema = {
  type: 'object',
  required: ['endpoint', 'params'],
  properties: {
    endpoint: {
      type: 'string',
      description: 'API endpoint or SDK method path',
    },
    method: {
      $ref: '#/components/schemas/HttpMethod',
    },
    params: {
      type: 'object',
      additionalProperties: true,
      description: 'Request parameters',
    },
    headers: {
      type: 'object',
      additionalProperties: {
        type: 'string',
      },
      description: 'Request headers (for HTTP adapter)',
    },
    credentials: {
      type: 'object',
      additionalProperties: true,
      description: 'Credentials for authentication',
    },
    timeout: {
      type: 'number',
      description: 'Request timeout in milliseconds',
    },
    useFormData: {
      type: 'boolean',
      description: 'Whether to use multipart/form-data (for HTTP adapter)',
    },
  },
} as const;

export const AdapterResponseSchema = {
  type: 'object',
  required: ['data'],
  properties: {
    data: {
      description: 'Response data',
    },
    status: {
      type: 'number',
      description: 'HTTP status code (for HTTP adapter)',
    },
    headers: {
      type: 'object',
      additionalProperties: {
        type: 'string',
      },
      description: 'Response headers (for HTTP adapter)',
    },
    raw: {
      description: 'Raw response body (for debugging)',
    },
  },
} as const;

export const HttpAdapterConfigSchema = {
  type: 'object',
  properties: {
    baseUrl: {
      type: 'string',
      description: 'Base URL for the API',
    },
    defaultHeaders: {
      type: 'object',
      additionalProperties: {
        type: 'string',
      },
      description: 'Default headers',
    },
    timeout: {
      type: 'number',
      description: 'Request timeout in milliseconds',
    },
    maxRetries: {
      type: 'number',
      description: 'Maximum retries',
    },
    retryDelay: {
      type: 'number',
      description: 'Retry delay in milliseconds',
    },
    proxy: {
      type: 'string',
      description: 'HTTP proxy URL',
    },
    polling: {
      $ref: '#/components/schemas/PollingConfig',
      description: 'Polling configuration for async task tracking',
    },
  },
} as const;

export const PollingConfigSchema = {
  type: 'object',
  required: ['statusUrl'],
  properties: {
    statusUrl: {
      type: 'string',
      description:
        'Status check endpoint template (e.g., "/v1/tasks/{id}" or "/v1/video_status.get?video_id={id}")',
    },
    statusMethod: {
      type: 'string',
      enum: ['GET', 'POST'],
      default: 'GET',
      description: 'HTTP method for status check (GET or POST)',
    },
    statusBody: {
      type: 'object',
      additionalProperties: true,
      description:
        'Request body template for POST status check. Supports placeholders like {task_id}, {req_key}',
    },
    taskIdPath: {
      type: 'string',
      description: 'JSON path to extract task ID from initial response (e.g., "data.task_id")',
    },
    statusPath: {
      type: 'string',
      description: 'JSON path to extract status from polling response (e.g., "data.status")',
    },
    resultPath: {
      type: 'string',
      description:
        'JSON path to extract result data from completed response (e.g., "data.resp_data")',
    },
    completedStatuses: {
      type: 'array',
      items: {
        type: 'string',
      },
      description: 'List of status values indicating completion (e.g., ["done", "completed"])',
    },
    failedStatuses: {
      type: 'array',
      items: {
        type: 'string',
      },
      description: 'List of status values indicating failure (e.g., ["failed", "error"])',
    },
    maxWaitSeconds: {
      type: 'number',
      description: 'Maximum wait time in seconds',
      default: 300,
    },
    intervalSeconds: {
      type: 'number',
      description: 'Poll interval in seconds',
      default: 5,
    },
  },
} as const;

export const SdkAdapterConfigSchema = {
  type: 'object',
  required: ['packageName', 'methodPath'],
  properties: {
    packageName: {
      type: 'string',
      description: 'NPM package name',
    },
    methodPath: {
      type: 'string',
      description: "Method path in the SDK (e.g., 'client.textToSpeech.convert')",
    },
    clientFactory: {
      description: 'SDK client initialization function (runtime only, not serializable)',
    },
    paramTransformer: {
      description: 'Parameter transformer function (runtime only, not serializable)',
    },
  },
} as const;

export const RetryConfigSchema = {
  type: 'object',
  required: ['maxRetries', 'initialDelay', 'maxDelay', 'backoffMultiplier'],
  properties: {
    maxRetries: {
      type: 'number',
      description: 'Maximum number of retries',
    },
    initialDelay: {
      type: 'number',
      description: 'Initial retry delay in milliseconds',
    },
    maxDelay: {
      type: 'number',
      description: 'Maximum retry delay in milliseconds',
    },
    backoffMultiplier: {
      type: 'number',
      description: 'Backoff multiplier',
    },
    retryableStatusCodes: {
      type: 'array',
      items: {
        type: 'number',
      },
      description: 'Status codes that should trigger a retry',
    },
    retryableErrorCodes: {
      type: 'array',
      items: {
        type: 'string',
      },
      description: 'Error codes that should trigger a retry',
    },
  },
} as const;

export const CircuitBreakerStateSchema = {
  type: 'string',
  enum: ['closed', 'open', 'half_open'],
  description: 'Circuit breaker state',
} as const;

export const CircuitBreakerConfigSchema = {
  type: 'object',
  required: ['failureThreshold', 'successThreshold', 'timeout', 'rollingWindowSize'],
  properties: {
    failureThreshold: {
      type: 'number',
      description: 'Failure threshold to open the circuit',
    },
    successThreshold: {
      type: 'number',
      description: 'Success threshold to close the circuit from half-open',
    },
    timeout: {
      type: 'number',
      description: 'Timeout in milliseconds before attempting to close',
    },
    rollingWindowSize: {
      type: 'number',
      description: 'Rolling window size for failure counting',
    },
  },
} as const;

export const HandlerRequestSchema = {
  type: 'object',
  required: ['provider', 'method', 'params'],
  properties: {
    provider: {
      type: 'string',
      description: 'Provider/toolset identifier',
    },
    method: {
      type: 'string',
      description: 'Method name',
    },
    params: {
      type: 'object',
      additionalProperties: true,
      description: 'Input parameters',
    },
    user: {
      $ref: '#/components/schemas/User',
    },
    metadata: {
      type: 'object',
      additionalProperties: true,
      description: 'Request metadata',
    },
  },
} as const;

export const UploadResultMetadataSchema = {
  type: 'object',
  properties: {
    size: {
      type: 'number',
      description: 'File size in bytes',
    },
    mimeType: {
      type: 'string',
      description: 'MIME type',
    },
    duration: {
      type: 'number',
      description: 'Duration (for audio/video)',
    },
    width: {
      type: 'number',
      description: 'Width (for images/video)',
    },
    height: {
      type: 'number',
      description: 'Height (for images/video)',
    },
    format: {
      type: 'string',
      description: 'File format',
    },
  },
} as const;

export const UploadResultSchema = {
  type: 'object',
  required: ['fileId', 'resourceType'],
  properties: {
    fileId: {
      type: 'string',
      description: 'File ID in database (DriveService)',
    },
    resourceType: {
      $ref: '#/components/schemas/ToolResourceType',
    },
    metadata: {
      $ref: '#/components/schemas/UploadResultMetadata',
    },
  },
} as const;

export const HandlerResponseSchema = {
  type: 'object',
  required: ['success'],
  properties: {
    success: {
      type: 'boolean',
      description: 'Success status',
    },
    data: {
      oneOf: [
        {
          type: 'object',
          additionalProperties: true,
        },
        {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: true,
          },
        },
      ],
      description: 'Response data (object or array of objects)',
    },
    localPath: {
      type: 'string',
      description: 'Local file path (if file was saved)',
    },
    storageKey: {
      type: 'string',
      description: 'Storage key (if file was uploaded)',
    },
    entityId: {
      type: 'string',
      description: 'Entity ID (if resource was created)',
    },
    fileId: {
      type: 'string',
      description: 'File ID in database',
    },
    files: {
      type: 'array',
      description: 'List of drive files generated by the tool',
      items: {
        $ref: '#/components/schemas/DriveFile',
      },
    },
    url: {
      type: 'string',
      description: 'CDN/public URL',
    },
    error: {
      type: 'string',
      description: 'Error information',
    },
    errorCode: {
      type: 'string',
      description: 'Error code',
    },
    metadata: {
      type: 'object',
      additionalProperties: true,
      description: 'Response metadata',
    },
  },
} as const;

export const HandlerContextSchema = {
  type: 'object',
  required: ['startTime'],
  properties: {
    user: {
      $ref: '#/components/schemas/User',
      description: 'User information for billing and tracking',
    },
    credentials: {
      type: 'object',
      additionalProperties: true,
      description: 'Credentials for authentication',
    },
    responseSchema: {
      $ref: '#/components/schemas/JsonSchema',
      description: 'Response schema for identifying resource fields via traversal',
    },
    startTime: {
      type: 'number',
      description: 'Request start timestamp',
    },
  },
} as const;

export const HandlerConfigSchema = {
  type: 'object',
  required: ['endpoint'],
  properties: {
    endpoint: {
      type: 'string',
      description: 'API endpoint URL',
    },
    method: {
      $ref: '#/components/schemas/HttpMethod',
    },
    credentials: {
      type: 'object',
      additionalProperties: true,
      description: 'Credentials',
    },
    responseSchema: {
      $ref: '#/components/schemas/JsonSchema',
      description: 'Response schema for identifying resource fields via traversal',
    },
    timeout: {
      type: 'number',
      description: 'Request timeout (ms)',
    },
    maxRetries: {
      type: 'number',
      description: 'Maximum retries',
    },
    useFormData: {
      type: 'boolean',
      description: 'Whether to use multipart/form-data',
    },
  },
} as const;

export const FileMetadataSchema = {
  type: 'object',
  required: ['entityId', 'storageKey', 'type', 'visibility'],
  properties: {
    entityId: {
      type: 'string',
      description: 'Entity ID',
    },
    storageKey: {
      type: 'string',
      description: 'Storage key',
    },
    type: {
      type: 'string',
      description: 'File type',
    },
    mimeType: {
      type: 'string',
      description: 'MIME type',
    },
    visibility: {
      type: 'string',
      enum: ['public', 'private'],
      description: 'Visibility',
    },
    size: {
      type: 'number',
      description: 'File size in bytes',
    },
    metadata: {
      type: 'object',
      additionalProperties: true,
      description: 'Additional metadata',
    },
  },
} as const;

export const UploadMetadataSchema = {
  type: 'object',
  required: ['provider', 'method', 'type'],
  properties: {
    provider: {
      type: 'string',
      description: 'Provider name',
    },
    method: {
      type: 'string',
      description: 'Method name',
    },
    type: {
      $ref: '#/components/schemas/ToolResourceType',
    },
    mimeType: {
      type: 'string',
      description: 'MIME type',
    },
    userId: {
      type: 'string',
      description: 'User ID',
    },
  },
} as const;

export const HandlerExecutionResultSchema = {
  type: 'object',
  required: ['success'],
  properties: {
    success: {
      type: 'boolean',
      description: 'Success status',
    },
    data: {
      description: 'Result data',
    },
    error: {
      type: 'object',
      properties: {
        code: {
          type: 'string',
          description: 'Error code',
        },
        message: {
          type: 'string',
          description: 'Error message',
        },
        details: {
          type: 'object',
          additionalProperties: true,
          description: 'Error details',
        },
      },
    },
    metrics: {
      type: 'object',
      properties: {
        duration: {
          type: 'number',
          description: 'Execution duration in milliseconds',
        },
        retries: {
          type: 'number',
          description: 'Number of retries',
        },
      },
    },
  },
} as const;

export const ToolMetadataBillingSchema = {
  type: 'object',
  required: ['enabled', 'type'],
  properties: {
    enabled: {
      type: 'boolean',
      description: 'Whether billing is enabled',
    },
    type: {
      type: 'string',
      enum: ['per_call', 'per_quantity'],
      description: 'Billing type',
    },
    creditsPerCall: {
      type: 'number',
      description: 'Credits per call',
    },
    creditsPerUnit: {
      type: 'number',
      description: 'Credits per unit',
    },
  },
} as const;

export const ToolMetadataSchema = {
  type: 'object',
  required: ['version', 'toolsetKey', 'methodName'],
  properties: {
    version: {
      type: 'number',
      description: 'Version number (incremental integer for version tracking)',
    },
    toolsetKey: {
      type: 'string',
      description: 'Toolset key',
    },
    methodName: {
      type: 'string',
      description: 'Method name',
    },
    billing: {
      $ref: '#/components/schemas/ToolMetadataBilling',
    },
    resourceTypes: {
      type: 'array',
      items: {
        type: 'string',
      },
      description: 'Resource types handled by this tool',
    },
    resourceExtensions: {
      type: 'object',
      additionalProperties: {
        type: 'array',
        items: {
          type: 'string',
        },
      },
      description: 'Supported file extensions for each resource type',
    },
  },
} as const;

export const ToolInstantiationContextUserSchema = {
  type: 'object',
  required: ['uid'],
  properties: {
    uid: {
      type: 'string',
      description: 'User ID',
    },
    name: {
      type: 'string',
      description: 'User name',
    },
    email: {
      type: 'string',
      description: 'User email',
    },
  },
} as const;

export const ToolInstantiationContextSchema = {
  type: 'object',
  required: ['user', 'toolsetId'],
  properties: {
    user: {
      $ref: '#/components/schemas/ToolInstantiationContextUser',
    },
    toolsetId: {
      type: 'string',
      description: 'Toolset ID',
    },
    configId: {
      type: 'string',
      description: 'Configuration ID (for versioned configs)',
    },
    metadata: {
      type: 'object',
      additionalProperties: true,
      description: 'Additional context',
    },
  },
} as const;

export const ToolExecutionContextUserSchema = {
  type: 'object',
  required: ['uid'],
  properties: {
    uid: {
      type: 'string',
      description: 'User ID',
    },
    name: {
      type: 'string',
      description: 'User name',
    },
    email: {
      type: 'string',
      description: 'User email',
    },
  },
} as const;

export const ToolExecutionContextSchema = {
  type: 'object',
  required: ['user'],
  properties: {
    user: {
      $ref: '#/components/schemas/ToolExecutionContextUser',
    },
    parentResultId: {
      type: 'string',
      description: 'Parent result ID (for workflow chaining)',
    },
    actionStepPk: {
      type: 'number',
      description: 'Action step PK (for workflow tracking)',
    },
    canvasNodeId: {
      type: 'string',
      description: 'Canvas node ID',
    },
    metadata: {
      type: 'object',
      additionalProperties: true,
      description: 'Additional metadata',
    },
  },
} as const;

export const ToolExecutionResultSchema = {
  type: 'object',
  required: ['success'],
  properties: {
    success: {
      type: 'boolean',
      description: 'Success status',
    },
    data: {
      description: 'Result data',
    },
    error: {
      type: 'object',
      properties: {
        code: {
          type: 'string',
          description: 'Error code',
        },
        message: {
          type: 'string',
          description: 'Error message',
        },
        details: {
          type: 'object',
          additionalProperties: true,
          description: 'Error details',
        },
      },
    },
    metrics: {
      type: 'object',
      properties: {
        duration: {
          type: 'number',
          description: 'Execution duration in milliseconds',
        },
        creditsCost: {
          type: 'number',
          description: 'Credits cost',
        },
      },
    },
  },
} as const;

export const DynamicToolDefinitionSchema = {
  type: 'object',
  required: ['name', 'description', 'metadata'],
  properties: {
    name: {
      type: 'string',
      description: 'Tool name (unique identifier)',
    },
    description: {
      type: 'string',
      description: 'Tool description for LLM',
    },
    schema: {
      description: 'Zod schema for parameters (runtime only, not serializable)',
    },
    metadata: {
      $ref: '#/components/schemas/ToolMetadata',
    },
  },
} as const;

export const InstantiatedToolSchema = {
  type: 'object',
  required: ['metadata'],
  properties: {
    tool: {
      description: 'LangChain DynamicStructuredTool instance (runtime only, not serializable)',
    },
    metadata: {
      $ref: '#/components/schemas/ToolMetadata',
    },
    configVersion: {
      type: 'number',
      description: 'Configuration version',
    },
  },
} as const;

export const ToolCacheEntrySchema = {
  type: 'object',
  required: ['configHash', 'timestamp', 'expiresAt'],
  properties: {
    configHash: {
      type: 'string',
      description: 'Configuration MD5 hash',
    },
    timestamp: {
      type: 'string',
      format: 'date-time',
      description: 'Cache timestamp',
    },
    expiresAt: {
      type: 'string',
      format: 'date-time',
      description: 'Cache expiration time',
    },
  },
} as const;

export const ToolRegistryEntrySchema = {
  type: 'object',
  required: ['toolsetKey', 'definitions', 'configVersion', 'updatedAt'],
  properties: {
    toolsetKey: {
      type: 'string',
      description: 'Toolset key',
    },
    definitions: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/DynamicToolDefinition',
      },
      description: 'Tool definitions',
    },
    configVersion: {
      type: 'number',
      description: 'Configuration version',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Last update timestamp',
    },
  },
} as const;

export const VoucherStatusSchema = {
  type: 'string',
  enum: ['unused', 'used', 'expired', 'invalid'],
  description: '优惠券状态',
} as const;

export const VoucherSourceSchema = {
  type: 'string',
  enum: ['template_publish', 'invitation_claim', 'run_workflow'],
  description: '优惠券来源',
} as const;

export const InvitationStatusSchema = {
  type: 'string',
  enum: ['unclaimed', 'claimed', 'expired'],
  description: 'Invitation status',
} as const;

export const VoucherSchema = {
  type: 'object',
  required: [
    'voucherId',
    'uid',
    'discountPercent',
    'status',
    'source',
    'expiresAt',
    'createdAt',
    'updatedAt',
  ],
  properties: {
    voucherId: {
      type: 'string',
      description: '优惠券唯一 ID',
    },
    uid: {
      type: 'string',
      description: '优惠券所属用户 ID',
    },
    discountPercent: {
      type: 'number',
      description: '折扣百分比（10-90）',
    },
    status: {
      $ref: '#/components/schemas/VoucherStatus',
    },
    source: {
      $ref: '#/components/schemas/VoucherSource',
    },
    sourceId: {
      type: 'string',
      description: '来源实体 ID（模板 ID 或邀请 ID）',
    },
    llmScore: {
      type: 'number',
      description: 'LLM 评分结果（0-100）',
    },
    expiresAt: {
      type: 'string',
      format: 'date-time',
      description: '优惠券过期时间',
    },
    usedAt: {
      type: 'string',
      format: 'date-time',
      description: '优惠券使用时间',
    },
    subscriptionId: {
      type: 'string',
      description: '优惠券使用时对应的订阅 ID',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '创建时间戳',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: '更新时间戳',
    },
  },
} as const;

export const VoucherInvitationSchema = {
  type: 'object',
  required: [
    'invitationId',
    'inviterUid',
    'inviteCode',
    'voucherId',
    'discountPercent',
    'status',
    'rewardGranted',
    'createdAt',
    'updatedAt',
  ],
  properties: {
    invitationId: {
      type: 'string',
      description: 'Unique invitation ID',
    },
    inviterUid: {
      type: 'string',
      description: 'Inviter user ID',
    },
    inviteeUid: {
      type: 'string',
      description: 'Invitee user ID',
    },
    inviteCode: {
      type: 'string',
      description: 'Short invitation code for sharing',
    },
    voucherId: {
      type: 'string',
      description: 'Source voucher ID',
    },
    discountPercent: {
      type: 'number',
      description: 'Discount percentage',
    },
    status: {
      $ref: '#/components/schemas/InvitationStatus',
    },
    claimedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Claim timestamp',
    },
    rewardGranted: {
      type: 'boolean',
      description: 'Whether inviter reward has been granted',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'Creation timestamp',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'Update timestamp',
    },
  },
} as const;

export const VoucherTriggerResultSchema = {
  type: 'object',
  required: ['voucher', 'score'],
  properties: {
    voucher: {
      $ref: '#/components/schemas/Voucher',
    },
    score: {
      type: 'number',
      description: 'LLM 评分（0-100）',
    },
    feedback: {
      type: 'string',
      description: 'LLM 改进建议',
    },
    triggerLimitReached: {
      type: 'boolean',
      description: '是否达到每日触发上限',
    },
  },
} as const;

export const VoucherAvailableResultSchema = {
  type: 'object',
  required: ['hasAvailableVoucher', 'vouchers'],
  properties: {
    hasAvailableVoucher: {
      type: 'boolean',
      description: 'Whether user has available vouchers',
    },
    vouchers: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/Voucher',
      },
      description: 'List of available vouchers',
    },
    bestVoucher: {
      $ref: '#/components/schemas/Voucher',
      description: 'Best available voucher (highest discount)',
    },
  },
} as const;

export const VoucherValidateResultSchema = {
  type: 'object',
  required: ['valid'],
  properties: {
    valid: {
      type: 'boolean',
      description: 'Whether voucher is valid',
    },
    voucher: {
      $ref: '#/components/schemas/Voucher',
      description: 'Voucher details',
    },
    reason: {
      type: 'string',
      description: 'Reason if not valid',
    },
  },
} as const;

export const CreateInvitationResultSchema = {
  type: 'object',
  required: ['invitation'],
  properties: {
    invitation: {
      $ref: '#/components/schemas/VoucherInvitation',
      description: 'Created invitation',
    },
  },
} as const;

export const ClaimInvitationResultSchema = {
  type: 'object',
  required: ['success'],
  properties: {
    success: {
      type: 'boolean',
      description: 'Whether claim was successful',
    },
    voucher: {
      $ref: '#/components/schemas/Voucher',
      description: 'Created voucher for invitee',
    },
    inviterName: {
      type: 'string',
      description: 'Name of the user who sent the invitation',
    },
    message: {
      type: 'string',
      description: 'Error message if failed',
    },
  },
} as const;

export const ValidateVoucherRequestSchema = {
  type: 'object',
  required: ['voucherId'],
  properties: {
    voucherId: {
      type: 'string',
      description: 'Voucher ID to validate',
    },
  },
} as const;

export const CreateVoucherInvitationRequestSchema = {
  type: 'object',
  required: ['voucherId'],
  properties: {
    voucherId: {
      type: 'string',
      description: 'Voucher ID to create invitation for',
    },
  },
} as const;

export const ClaimVoucherInvitationRequestSchema = {
  type: 'object',
  required: ['inviteCode'],
  properties: {
    inviteCode: {
      type: 'string',
      description: 'Invitation code to claim',
    },
  },
} as const;

export const GetAvailableVouchersResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/VoucherAvailableResult',
        },
      },
    },
  ],
} as const;

export const ListUserVouchersResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/Voucher',
          },
        },
      },
    },
  ],
} as const;

export const ValidateVoucherResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/VoucherValidateResult',
        },
      },
    },
  ],
} as const;

export const CreateVoucherInvitationResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/CreateInvitationResult',
        },
      },
    },
  ],
} as const;

export const VerifyInvitationResultSchema = {
  type: 'object',
  required: ['valid'],
  properties: {
    valid: {
      type: 'boolean',
      description: 'Whether the invitation is valid and can be claimed',
    },
    invitation: {
      $ref: '#/components/schemas/VoucherInvitation',
    },
    voucher: {
      $ref: '#/components/schemas/Voucher',
      description: 'The original voucher being shared (for unclaimed invitations)',
    },
    claimedByUid: {
      type: 'string',
      description: 'If already claimed, the UID of the user who claimed it',
    },
    inviterName: {
      type: 'string',
      description: "Inviter's name (for display)",
    },
    message: {
      type: 'string',
      description: 'Error or status message',
    },
  },
} as const;

export const VerifyVoucherInvitationResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/VerifyInvitationResult',
        },
      },
    },
  ],
} as const;

export const ClaimVoucherInvitationResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/ClaimInvitationResult',
        },
      },
    },
  ],
} as const;

export const TriggerVoucherRequestSchema = {
  type: 'object',
  required: ['canvasId', 'triggerType'],
  properties: {
    canvasId: {
      type: 'string',
      description: 'Canvas ID of the template being published',
    },
    templateId: {
      type: 'string',
      description: 'Optional template ID (defaults to canvasId if not provided)',
    },
    triggerType: {
      type: 'string',
      enum: ['template_publish'],
      description: 'Type of trigger event',
    },
  },
} as const;

export const TriggerVoucherResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/VoucherTriggerResult',
        },
      },
    },
  ],
} as const;

export const CreateCliApiKeyRequestSchema = {
  type: 'object',
  required: ['name'],
  properties: {
    name: {
      type: 'string',
      minLength: 1,
      maxLength: 100,
      description: 'API key display name',
    },
    expiresInDays: {
      type: 'integer',
      minimum: 1,
      description: 'Optional expiration period in days',
    },
  },
} as const;

export const UpdateCliApiKeyRequestSchema = {
  type: 'object',
  required: ['name'],
  properties: {
    name: {
      type: 'string',
      minLength: 1,
      maxLength: 100,
      description: 'New API key display name',
    },
  },
} as const;

export const CliApiKeyInfoSchema = {
  type: 'object',
  required: ['keyId', 'name', 'keyPrefix', 'createdAt'],
  properties: {
    keyId: {
      type: 'string',
    },
    name: {
      type: 'string',
    },
    keyPrefix: {
      type: 'string',
      example: 'af_abc123456',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
    },
    lastUsedAt: {
      type: 'string',
      format: 'date-time',
      nullable: true,
    },
    expiresAt: {
      type: 'string',
      format: 'date-time',
      nullable: true,
    },
  },
} as const;

export const CreateCliApiKeyDataSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/CliApiKeyInfo',
    },
    {
      type: 'object',
      required: ['apiKey'],
      properties: {
        apiKey: {
          type: 'string',
          description: 'Full API key, returned only once',
          example: 'af_abc123456789',
        },
      },
    },
  ],
} as const;

export const CreateCliApiKeyResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/CreateCliApiKeyData',
        },
      },
    },
  ],
} as const;

export const ListCliApiKeysResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/CliApiKeyInfo',
          },
        },
      },
    },
  ],
} as const;

export const UpdateOpenapiConfigRequestSchema = {
  type: 'object',
  required: ['canvasId'],
  properties: {
    canvasId: {
      type: 'string',
    },
    resultNodeIds: {
      type: 'array',
      nullable: true,
      items: {
        type: 'string',
      },
      description: 'Null for all output nodes, empty array for none',
    },
  },
} as const;

export const OpenapiConfigResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            canvasId: {
              type: 'string',
            },
            resultNodeIds: {
              type: 'array',
              nullable: true,
              items: {
                type: 'string',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const OpenapiUploadedFileSchema = {
  type: 'object',
  required: ['fileKey', 'fileName'],
  properties: {
    fileKey: {
      type: 'string',
      example: 'of_abc123',
    },
    fileName: {
      type: 'string',
    },
  },
} as const;

export const OpenapiFileUploadResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            files: {
              type: 'array',
              items: {
                $ref: '#/components/schemas/OpenapiUploadedFile',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const OpenapiWorkflowRunRequestSchema = {
  type: 'object',
  additionalProperties: false,
  properties: {
    variables: {
      type: 'object',
      additionalProperties: true,
      description: 'Workflow variables keyed by variable name',
    },
  },
} as const;

export const RunWorkflowApiResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            executionId: {
              type: 'string',
            },
            status: {
              $ref: '#/components/schemas/WorkflowExecutionStatus',
            },
          },
        },
      },
    },
  ],
} as const;

export const OpenapiWorkflowSummarySchema = {
  type: 'object',
  required: ['canvasId', 'title'],
  properties: {
    canvasId: {
      type: 'string',
    },
    title: {
      type: 'string',
    },
  },
} as const;

export const OpenapiWorkflowSearchResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          items: {
            $ref: '#/components/schemas/OpenapiWorkflowSummary',
          },
        },
      },
    },
  ],
} as const;

export const OpenapiWorkflowDetailResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/WorkflowPlan',
        },
      },
    },
  ],
} as const;

export const OpenapiCopilotGenerateRequestSchema = {
  type: 'object',
  required: ['query'],
  properties: {
    query: {
      type: 'string',
    },
    canvasId: {
      type: 'string',
    },
    locale: {
      type: 'string',
    },
  },
} as const;

export const OpenapiCopilotGenerateResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            canvasId: {
              type: 'string',
            },
            workflowPlan: {
              $ref: '#/components/schemas/WorkflowPlan',
            },
          },
        },
      },
    },
  ],
} as const;

export const CanvasMaterialSchema = {
  description: '当前画布中的有效素材',
  type: 'object',
  required: [
    'fileId',
    'canvasId',
    'name',
    'extension',
    'mimeType',
    'mediaKind',
    'size',
    'source',
    'category',
    'scope',
    'previewSupported',
    'downloadSupported',
    'thumbnailUrl',
    'createdAt',
    'updatedAt',
  ],
  properties: {
    fileId: {
      type: 'string',
      description: 'Drive 文件 ID',
    },
    canvasId: {
      type: 'string',
      description: '所属画布 ID',
    },
    name: {
      type: 'string',
      description: '文件名',
    },
    extension: {
      type: 'string',
      description: '小写文件扩展名',
    },
    mimeType: {
      type: 'string',
      description: '规范 MIME 类型',
    },
    mediaKind: {
      type: 'string',
      enum: ['image', 'video', 'audio', 'pdf', 'text', 'document', 'other'],
    },
    size: {
      type: 'integer',
      format: 'int64',
      minimum: 0,
      description: '文件大小，单位为字节',
    },
    source: {
      type: 'string',
      enum: ['manual', 'variable', 'agent'],
    },
    category: {
      type: 'string',
      enum: ['uploads', 'creations'],
    },
    scope: {
      type: 'string',
      enum: ['present'],
    },
    previewSupported: {
      type: 'boolean',
    },
    downloadSupported: {
      type: 'boolean',
    },
    thumbnailUrl: {
      type: 'string',
      format: 'uri',
      nullable: true,
      description: '第一阶段允许为空',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
    },
  },
} as const;

export const CanvasMaterialAccessUrlSchema = {
  type: 'object',
  required: ['url', 'expiresAt', 'disposition'],
  properties: {
    url: {
      type: 'string',
      format: 'uri',
    },
    expiresAt: {
      type: 'string',
      format: 'date-time',
    },
    disposition: {
      type: 'string',
      enum: ['inline', 'attachment'],
    },
  },
} as const;

export const CreateCanvasMaterialImportRequestSchema = {
  type: 'object',
  required: ['parentId', 'category', 'fileIds', 'clientRequestId'],
  additionalProperties: false,
  properties: {
    parentId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
      description: '素材库目标文件夹条目 ID',
    },
    category: {
      type: 'string',
      enum: ['creations'],
      description: 'UI 只允许保存“我的创作”',
    },
    fileIds: {
      type: 'array',
      minItems: 1,
      uniqueItems: true,
      items: {
        type: 'string',
        minLength: 1,
        maxLength: 128,
      },
    },
    clientRequestId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
    },
  },
} as const;

export const CreateCanvasMaterialFromLibraryRequestSchema = {
  type: 'object',
  required: ['entryIds', 'clientRequestId'],
  additionalProperties: false,
  properties: {
    entryIds: {
      type: 'array',
      minItems: 1,
      uniqueItems: true,
      items: {
        type: 'string',
        minLength: 1,
        maxLength: 128,
      },
    },
    clientRequestId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
    },
  },
} as const;

export const CanvasMaterialFromLibraryItemSchema = {
  type: 'object',
  required: ['entryId', 'status'],
  properties: {
    entryId: {
      type: 'string',
    },
    status: {
      type: 'string',
      enum: ['succeeded', 'failed'],
    },
    fileId: {
      type: 'string',
      nullable: true,
      description: '导入成功后的画布文件 ID',
    },
    errorCode: {
      type: 'string',
      nullable: true,
    },
    errorMessage: {
      type: 'string',
      nullable: true,
    },
  },
} as const;

export const CanvasMaterialFromLibraryResultSchema = {
  type: 'object',
  required: ['requestId', 'status', 'total', 'succeeded', 'failed', 'items'],
  properties: {
    requestId: {
      type: 'string',
    },
    status: {
      type: 'string',
      enum: ['completed', 'completed_with_errors', 'failed'],
    },
    total: {
      type: 'integer',
      minimum: 1,
    },
    succeeded: {
      type: 'integer',
      minimum: 0,
    },
    failed: {
      type: 'integer',
      minimum: 0,
    },
    items: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/CanvasMaterialFromLibraryItem',
      },
    },
  },
} as const;

export const CanvasMaterialImportItemSchema = {
  type: 'object',
  required: ['fileId', 'status'],
  properties: {
    fileId: {
      type: 'string',
    },
    status: {
      type: 'string',
      enum: ['succeeded', 'failed'],
    },
    entryId: {
      type: 'string',
      nullable: true,
      description: '导入成功后的素材库文件条目 ID',
    },
    errorCode: {
      type: 'string',
      nullable: true,
    },
    errorMessage: {
      type: 'string',
      nullable: true,
    },
  },
} as const;

export const CanvasMaterialImportResultSchema = {
  type: 'object',
  required: ['requestId', 'status', 'total', 'succeeded', 'failed', 'items'],
  properties: {
    requestId: {
      type: 'string',
    },
    status: {
      type: 'string',
      enum: ['completed', 'completed_with_errors', 'failed'],
    },
    total: {
      type: 'integer',
      minimum: 1,
    },
    succeeded: {
      type: 'integer',
      minimum: 0,
    },
    failed: {
      type: 'integer',
      minimum: 0,
    },
    items: {
      type: 'array',
      items: {
        $ref: '#/components/schemas/CanvasMaterialImportItem',
      },
    },
  },
} as const;

export const CreateCanvasMaterialDownloadTaskRequestSchema = {
  type: 'object',
  required: ['category', 'fileIds', 'clientRequestId'],
  additionalProperties: false,
  properties: {
    category: {
      type: 'string',
      enum: ['uploads', 'creations'],
    },
    fileIds: {
      type: 'array',
      minItems: 1,
      uniqueItems: true,
      items: {
        type: 'string',
        minLength: 1,
        maxLength: 128,
      },
    },
    clientRequestId: {
      type: 'string',
      minLength: 1,
      maxLength: 128,
    },
  },
} as const;

export const CanvasMaterialDownloadTaskSchema = {
  type: 'object',
  required: [
    'taskId',
    'status',
    'fileCount',
    'totalSourceSize',
    'zipSize',
    'downloadUrl',
    'downloadUrlExpiresAt',
    'expiresAt',
    'errorCode',
    'errorMessage',
    'createdAt',
    'updatedAt',
  ],
  properties: {
    taskId: {
      type: 'string',
    },
    status: {
      type: 'string',
      enum: ['pending', 'processing', 'ready', 'failed', 'expired'],
    },
    fileCount: {
      type: 'integer',
      minimum: 1,
    },
    totalSourceSize: {
      type: 'integer',
      format: 'int64',
      minimum: 0,
    },
    zipSize: {
      type: 'integer',
      format: 'int64',
      minimum: 0,
      nullable: true,
    },
    downloadUrl: {
      type: 'string',
      format: 'uri',
      nullable: true,
    },
    downloadUrlExpiresAt: {
      type: 'string',
      format: 'date-time',
      nullable: true,
    },
    expiresAt: {
      type: 'string',
      format: 'date-time',
      nullable: true,
    },
    errorCode: {
      type: 'string',
      nullable: true,
    },
    errorMessage: {
      type: 'string',
      nullable: true,
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
    },
  },
} as const;

export const ListCanvasMaterialsResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          required: ['items', 'page', 'pageSize', 'total', 'hasMore'],
          properties: {
            items: {
              type: 'array',
              items: {
                $ref: '#/components/schemas/CanvasMaterial',
              },
            },
            page: {
              type: 'integer',
              minimum: 1,
            },
            pageSize: {
              type: 'integer',
              minimum: 1,
              maximum: 100,
            },
            total: {
              type: 'integer',
              minimum: 0,
            },
            hasMore: {
              type: 'boolean',
            },
          },
        },
      },
    },
  ],
} as const;

export const CanvasMaterialAccessUrlResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/CanvasMaterialAccessUrl',
        },
      },
    },
  ],
} as const;

export const CanvasMaterialImportResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/CanvasMaterialImportResult',
        },
      },
    },
  ],
} as const;

export const CanvasMaterialFromLibraryResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/CanvasMaterialFromLibraryResult',
        },
      },
    },
  ],
} as const;

export const CanvasMaterialDownloadTaskResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          $ref: '#/components/schemas/CanvasMaterialDownloadTask',
        },
      },
    },
  ],
} as const;

export const MediaLibraryEntrySchema = {
  description: '素材库统一条目；文件夹不返回文件专属字段',
  type: 'object',
  additionalProperties: false,
  required: ['entryId', 'parentId', 'type', 'name', 'path', 'createdAt', 'updatedAt'],
  properties: {
    entryId: {
      type: 'string',
      description: '文件或文件夹共用的条目 ID',
      example: 'me_01JZ8J9QXT',
    },
    parentId: {
      type: 'string',
      nullable: true,
      description: '父文件夹条目 ID；根目录条目为 null',
      example: 'me_folder_01',
    },
    type: {
      type: 'string',
      enum: ['folder', 'file'],
      description: '条目类型',
      example: 'file',
    },
    name: {
      type: 'string',
      description: '条目名称',
      example: 'product-banner.png',
    },
    protectedKey: {
      type: 'string',
      enum: ['uploads', 'creations'],
      nullable: true,
      description: '仅两个受保护默认文件夹返回',
    },
    path: {
      type: 'string',
      description: '条目所在文件夹的实时完整路径；根目录为空字符串',
      example: '万里云贸/kjg',
    },
    originalName: {
      type: 'string',
      description: '上传时的原始文件名',
      example: 'product-banner.png',
    },
    extension: {
      type: 'string',
      description: '文件扩展名',
      example: 'png',
    },
    mimeType: {
      type: 'string',
      description: 'MIME 类型',
      example: 'image/png',
    },
    mediaKind: {
      type: 'string',
      description:
        '媒体类型：image 图片、video 视频、audio 音频、pdf PDF、text 文本、document 文档',
      enum: ['image', 'video', 'audio', 'pdf', 'text', 'document'],
      example: 'image',
    },
    size: {
      type: 'integer',
      format: 'int64',
      minimum: 0,
      description: '文件大小，单位为字节',
      example: 245760,
    },
    thumbnailStatus: {
      type: 'string',
      description:
        '缩略图状态：pending 待处理、processing 处理中、ready 已完成、failed 失败、not_required 不需要',
      enum: ['pending', 'processing', 'ready', 'failed', 'not_required'],
      example: 'ready',
    },
    thumbnailUrl: {
      type: 'string',
      format: 'uri',
      nullable: true,
      description: '缩略图临时访问地址',
    },
    thumbnailUrlExpiresAt: {
      type: 'string',
      format: 'date-time',
      nullable: true,
      description: '缩略图地址过期时间',
    },
    width: {
      type: 'integer',
      nullable: true,
      description: '图片或视频宽度，单位为像素',
      example: 1920,
    },
    height: {
      type: 'integer',
      nullable: true,
      description: '图片或视频高度，单位为像素',
      example: 1080,
    },
    durationMs: {
      type: 'integer',
      format: 'int64',
      nullable: true,
      description: '视频时长，单位为毫秒',
      example: 12000,
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '创建时间',
      example: '2026-08-11T08:00:00Z',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: '最后更新时间',
      example: '2026-08-11T08:00:00Z',
    },
  },
} as const;

export const MediaLibraryEntryAncestorSchema = {
  description: '面包屑祖先文件夹',
  type: 'object',
  additionalProperties: false,
  required: ['entryId', 'parentId', 'name'],
  properties: {
    entryId: {
      type: 'string',
      description: '祖先文件夹条目 ID',
    },
    parentId: {
      type: 'string',
      nullable: true,
      description: '祖先的父文件夹 ID',
    },
    name: {
      type: 'string',
      description: '祖先文件夹名称',
    },
  },
} as const;

export const MediaLibraryEntryDetailSchema = {
  description: '包含祖先面包屑的素材库条目详情',
  allOf: [
    {
      $ref: '#/components/schemas/MediaLibraryEntry',
    },
    {
      type: 'object',
      required: ['ancestors'],
      properties: {
        ancestors: {
          type: 'array',
          description: '从根目录到直接父目录的祖先列表',
          items: {
            $ref: '#/components/schemas/MediaLibraryEntryAncestor',
          },
        },
      },
    },
  ],
} as const;

export const MediaLibraryBatchDeleteResultSchema = {
  description: '单个条目的删除结果',
  type: 'object',
  additionalProperties: false,
  required: ['entryId', 'status'],
  properties: {
    entryId: {
      type: 'string',
      description: '请求中的条目 ID',
    },
    status: {
      type: 'string',
      enum: ['deleted', 'deleted_by_ancestor', 'failed'],
      description: '删除结果',
    },
    code: {
      type: 'string',
      description: 'failed 时的错误码',
      example: 'MEDIA_ENTRY_NOT_FOUND',
    },
    message: {
      type: 'string',
      description: 'failed 时的错误说明',
    },
  },
} as const;

export const MediaLibraryUploadSessionSchema = {
  description: '素材分片上传会话',
  type: 'object',
  additionalProperties: false,
  required: [
    'sessionId',
    'entryId',
    'parentId',
    'fileName',
    'expectedSize',
    'partSize',
    'partCount',
    'status',
    'expiresAt',
  ],
  properties: {
    sessionId: {
      type: 'string',
      description: '上传会话 ID',
      example: 'mus_01JZ8JD5HZ',
    },
    entryId: {
      type: 'string',
      description: '预分配的文件条目 ID',
      example: 'me_01JZ8J9QXT',
    },
    parentId: {
      type: 'string',
      description: '实际上传目标文件夹 ID；根目录请求会返回“我的上传”ID',
      example: 'me_uploads_01',
    },
    fileName: {
      type: 'string',
      description: '文件名',
      example: 'product-banner.png',
    },
    expectedSize: {
      type: 'integer',
      format: 'int64',
      description: '预期文件大小，单位为字节',
      example: 245760,
    },
    partSize: {
      type: 'integer',
      description: '每个分片大小，单位为字节',
      example: 16777216,
    },
    partCount: {
      type: 'integer',
      description: '分片总数',
      example: 1,
    },
    status: {
      type: 'string',
      description:
        '会话状态：initiated 已初始化、completed 已完成、aborted 已取消、expired 已过期、failed 失败',
      enum: ['initiated', 'completed', 'aborted', 'expired', 'failed'],
      example: 'initiated',
    },
    expiresAt: {
      type: 'string',
      format: 'date-time',
      description: '会话过期时间',
      example: '2026-08-12T08:00:00Z',
    },
  },
} as const;

export const MediaLibraryUploadedPartSchema = {
  description: '已上传的分片',
  type: 'object',
  required: ['partNumber'],
  properties: {
    partNumber: {
      type: 'integer',
      minimum: 1,
      description: '分片编号，从 1 开始',
      example: 1,
    },
    etag: {
      type: 'string',
      nullable: true,
      description: '对象存储返回的分片 ETag',
    },
    size: {
      type: 'integer',
      format: 'int64',
      nullable: true,
      description: '分片大小，单位为字节',
      example: 16777216,
    },
  },
} as const;

export const MediaLibraryAccessUrlSchema = {
  description: '素材临时访问地址',
  type: 'object',
  required: ['url', 'expiresAt', 'disposition'],
  properties: {
    url: {
      type: 'string',
      format: 'uri',
      description: '临时访问地址',
    },
    expiresAt: {
      type: 'string',
      format: 'date-time',
      description: '地址过期时间',
      example: '2026-08-11T08:15:00Z',
    },
    disposition: {
      type: 'string',
      description: 'inline 预览或 attachment 下载',
      enum: ['inline', 'attachment'],
      example: 'inline',
    },
  },
} as const;

export const MediaLibraryDownloadTaskSchema = {
  description: '素材 ZIP 打包下载任务',
  type: 'object',
  required: ['taskId', 'status', 'fileCount', 'totalSourceSize', 'createdAt', 'updatedAt'],
  properties: {
    taskId: {
      type: 'string',
      description: '打包任务 ID',
      example: 'mdt_01JZ8JH4YS',
    },
    status: {
      type: 'string',
      description:
        '任务状态：pending 排队中、processing 处理中、ready 已完成、failed 失败、expired 已过期、invalidated 已失效',
      enum: ['pending', 'processing', 'ready', 'failed', 'expired', 'invalidated'],
      example: 'processing',
    },
    fileCount: {
      type: 'integer',
      minimum: 0,
      description: '待打包素材数量',
      example: 3,
    },
    totalSourceSize: {
      type: 'integer',
      format: 'int64',
      minimum: 0,
      description: '源文件总大小，单位为字节',
      example: 10485760,
    },
    zipSize: {
      type: 'integer',
      format: 'int64',
      minimum: 0,
      nullable: true,
      description: 'ZIP 文件大小，单位为字节',
    },
    downloadUrl: {
      type: 'string',
      format: 'uri',
      nullable: true,
      description: 'ZIP 临时下载地址',
    },
    downloadUrlExpiresAt: {
      type: 'string',
      format: 'date-time',
      nullable: true,
      description: '下载地址过期时间',
    },
    expiresAt: {
      type: 'string',
      format: 'date-time',
      nullable: true,
      description: '任务过期时间',
    },
    errorCode: {
      type: 'string',
      nullable: true,
      description: '失败时的错误码',
    },
    errorMessage: {
      type: 'string',
      nullable: true,
      description: '失败时的错误说明',
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: '创建时间',
      example: '2026-08-11T08:00:00Z',
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: '最后更新时间',
      example: '2026-08-11T08:01:00Z',
    },
  },
} as const;

export const CreateMediaLibraryEntryRequestSchema = {
  description: '创建文件夹条目请求',
  type: 'object',
  required: ['name'],
  additionalProperties: false,
  properties: {
    name: {
      type: 'string',
      minLength: 1,
      maxLength: 255,
      description: '文件夹名称',
      example: '产品图片',
    },
    parentId: {
      type: 'string',
      nullable: true,
      description: '父文件夹条目 ID；null 或不传表示根目录',
      example: 'me_folder_01',
    },
  },
} as const;

export const UpdateMediaLibraryEntryRequestSchema = {
  description: '更新条目请求，至少提供 name 或 parentId',
  type: 'object',
  additionalProperties: false,
  minProperties: 1,
  properties: {
    name: {
      type: 'string',
      minLength: 1,
      maxLength: 255,
      description: '新条目名称；文件不得改变扩展名',
      example: '2026展会产品主图.png',
    },
    parentId: {
      type: 'string',
      nullable: true,
      description: '新父文件夹条目 ID；null 表示根目录',
      example: 'me_folder_01',
    },
  },
} as const;

export const BatchDeleteMediaLibraryEntriesRequestSchema = {
  description: '混合批量删除文件和文件夹请求',
  type: 'object',
  required: ['entryIds'],
  additionalProperties: false,
  properties: {
    entryIds: {
      type: 'array',
      minItems: 1,
      uniqueItems: true,
      description: '要删除的统一条目 ID；不设业务条数上限',
      example: ['me_01JZ8J9QXT', 'me_folder_02'],
      items: {
        type: 'string',
        minLength: 1,
        maxLength: 128,
      },
    },
  },
} as const;

export const InitializeMediaLibraryUploadRequestSchema = {
  description: '初始化素材分片上传请求',
  type: 'object',
  required: ['fileName', 'size'],
  additionalProperties: false,
  properties: {
    clientRequestId: {
      type: 'string',
      maxLength: 128,
      description: '客户端幂等请求 ID',
      example: 'upload-product-20260811-001',
    },
    parentId: {
      type: 'string',
      nullable: true,
      description: '目标文件夹条目 ID；不传、null 或目标失效时落入“我的上传”',
      example: 'me_folder_01',
    },
    fileName: {
      type: 'string',
      minLength: 1,
      maxLength: 255,
      description: '文件名',
      example: 'product-banner.png',
    },
    size: {
      type: 'integer',
      minimum: 1,
      maximum: 524288000,
      description: '文件大小，单位为字节，最大 500 MiB',
      example: 245760,
    },
    mimeType: {
      type: 'string',
      description: 'MIME 类型',
      example: 'image/png',
    },
  },
} as const;

export const SignMediaLibraryUploadPartsRequestSchema = {
  description: '获取分片签名地址请求',
  type: 'object',
  required: ['partNumbers'],
  additionalProperties: false,
  properties: {
    partNumbers: {
      type: 'array',
      minItems: 1,
      maxItems: 10,
      uniqueItems: true,
      description: '要签名的分片编号，最多 10 个',
      example: [1, 2],
      items: {
        type: 'integer',
        minimum: 1,
      },
    },
  },
} as const;

export const MediaLibraryUploadStatusSchema = {
  description: '素材分片上传进度',
  allOf: [
    {
      $ref: '#/components/schemas/MediaLibraryUploadSession',
    },
    {
      type: 'object',
      required: ['uploadedParts', 'missingPartNumbers'],
      properties: {
        uploadedParts: {
          type: 'array',
          description: '已上传的分片列表',
          items: {
            $ref: '#/components/schemas/MediaLibraryUploadedPart',
          },
        },
        missingPartNumbers: {
          type: 'array',
          description: '尚未上传的分片编号',
          example: [2, 3],
          items: {
            type: 'integer',
          },
        },
      },
    },
  ],
} as const;

export const SignMediaLibraryUploadPartSchema = {
  description: '分片上传签名地址',
  type: 'object',
  required: ['partNumber', 'url', 'expiresAt'],
  properties: {
    partNumber: {
      type: 'integer',
      description: '分片编号',
      example: 1,
    },
    url: {
      type: 'string',
      format: 'uri',
      description: '分片上传地址',
    },
    expiresAt: {
      type: 'string',
      format: 'date-time',
      description: '地址过期时间',
    },
  },
} as const;

export const CreateMediaLibraryDownloadTaskRequestSchema = {
  description: '递归创建素材打包下载任务请求',
  type: 'object',
  required: ['entryIds'],
  additionalProperties: false,
  properties: {
    entryIds: {
      type: 'array',
      minItems: 1,
      uniqueItems: true,
      description: '要打包的文件或文件夹条目 ID；不设业务条数上限',
      example: ['me_01JZ8J9QXT', 'me_folder_02'],
      items: {
        type: 'string',
        minLength: 1,
        maxLength: 128,
      },
    },
  },
} as const;

export const ListMediaLibraryEntriesResponseSchema = {
  description: '素材库条目列表响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: '不分页的条目列表',
          items: {
            $ref: '#/components/schemas/MediaLibraryEntry',
          },
        },
      },
    },
  ],
} as const;

export const MediaLibraryEntryResponseSchema = {
  description: '素材库条目响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: '素材库条目详情',
          allOf: [
            {
              $ref: '#/components/schemas/MediaLibraryEntry',
            },
          ],
        },
      },
    },
  ],
} as const;

export const MediaLibraryEntryDetailResponseSchema = {
  description: '素材库条目详情响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: '包含祖先路径的素材库条目详情',
          allOf: [
            {
              $ref: '#/components/schemas/MediaLibraryEntryDetail',
            },
          ],
        },
      },
    },
  ],
} as const;

export const BatchDeleteMediaLibraryEntriesResponseSchema = {
  description: '素材库逐项删除响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          description: '逐项删除结果集合',
          additionalProperties: false,
          required: ['results'],
          properties: {
            results: {
              type: 'array',
              description: '与请求 entryIds 顺序一致的逐项结果',
              items: {
                $ref: '#/components/schemas/MediaLibraryBatchDeleteResult',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const MediaLibraryAccessUrlResponseSchema = {
  description: '素材访问地址响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: '素材临时访问地址',
          allOf: [
            {
              $ref: '#/components/schemas/MediaLibraryAccessUrl',
            },
          ],
        },
      },
    },
  ],
} as const;

export const MediaLibraryUploadSessionResponseSchema = {
  description: '素材分片上传会话响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: '素材分片上传会话',
          allOf: [
            {
              $ref: '#/components/schemas/MediaLibraryUploadSession',
            },
          ],
        },
      },
    },
  ],
} as const;

export const MediaLibraryUploadStatusResponseSchema = {
  description: '素材分片上传进度响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: '素材分片上传进度',
          allOf: [
            {
              $ref: '#/components/schemas/MediaLibraryUploadStatus',
            },
          ],
        },
      },
    },
  ],
} as const;

export const SignMediaLibraryUploadPartsResponseSchema = {
  description: '分片上传签名地址响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'array',
          description: '分片上传签名地址列表',
          items: {
            $ref: '#/components/schemas/SignMediaLibraryUploadPart',
          },
        },
      },
    },
  ],
} as const;

export const MediaLibraryDownloadTaskResponseSchema = {
  description: '素材打包下载任务响应',
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          description: '素材打包下载任务详情',
          allOf: [
            {
              $ref: '#/components/schemas/MediaLibraryDownloadTask',
            },
          ],
        },
      },
    },
  ],
} as const;

export const GetWorkflowStatusViaApiResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            executionId: {
              type: 'string',
            },
            status: {
              $ref: '#/components/schemas/WorkflowExecutionStatus',
            },
            createdAt: {
              type: 'string',
              format: 'date-time',
            },
            nodeExecutions: {
              type: 'array',
              items: {
                $ref: '#/components/schemas/WorkflowNodeExecution',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const GetWorkflowOutputResponseSchema = {
  allOf: [
    {
      $ref: '#/components/schemas/BaseResponse',
    },
    {
      type: 'object',
      properties: {
        data: {
          type: 'object',
          properties: {
            output: {
              type: 'array',
              items: {
                allOf: [
                  {
                    $ref: '#/components/schemas/WorkflowNodeExecution',
                  },
                  {
                    type: 'object',
                    properties: {
                      messages: {
                        type: 'array',
                        items: {
                          $ref: '#/components/schemas/ActionMessage',
                        },
                      },
                    },
                  },
                ],
              },
            },
            files: {
              type: 'array',
              items: {
                $ref: '#/components/schemas/DriveFile',
              },
            },
          },
        },
      },
    },
  ],
} as const;

export const WorkflowArtifactSchema = {
  type: 'object',
  required: ['artifactId', 'kind', 'source'],
  properties: {
    artifactId: {
      type: 'string',
    },
    kind: {
      type: 'string',
      enum: ['text', 'file', 'image', 'video', 'audio'],
    },
    name: {
      type: 'string',
    },
    summary: {
      type: 'string',
    },
    assetId: {
      type: 'string',
    },
    entryId: {
      type: 'string',
    },
    fileId: {
      type: 'string',
    },
    resourceId: {
      type: 'string',
    },
    mimeType: {
      type: 'string',
    },
    source: {
      type: 'object',
      properties: {
        resultId: {
          type: 'string',
        },
        toolCallId: {
          type: 'string',
        },
        executionId: {
          type: 'string',
        },
        nodeExecutionId: {
          type: 'string',
        },
        nodeId: {
          type: 'string',
        },
      },
    },
    metadata: {
      type: 'object',
      additionalProperties: true,
    },
  },
} as const;
